import React, { useState, useEffect, useContext, useCallback, useRef, memo } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from "../../components/AuthContext";
import MasterSelection from '../../components/MasterSelection';
import TileSelectionModal from '../../components/TileSelectionModal';
import StockItemSelectionModal from '../../components/StockItemSelectionModal';
import Select from 'react-select';
import { 
  FaPlus, 
  FaMinus, 
  FaSearch, 
  FaTimes, 
  FaEye, 
  FaPrint, 
  FaDownload, 
  FaEdit, 
  FaTrash,
  FaBox,
  FaBoxes,
  FaMapMarkerAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaCube,
  FaWarehouse,
  FaCalculator,
  FaTruck,
  FaUser,
  FaClipboardList,
  FaCopy,
  FaPaste,
  FaRocket,
  FaArrowRight,
  FaBan,
  FaUserTie,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCubes,
  FaMoneyBillWave,
  FaPercentage,
  FaFileAlt,
  FaLock,
  FaCheck,
  FaCog,
  FaShoppingCart,
  FaFilter,
  FaChartBar,
  FaSave
} from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Tiles Search Bar Component (Similar to DeliveryOrderNew's ItemSearchBar)
// Format number with commas
const formatNumberWithCommas = (number) => {
  const value = typeof number === 'string' ? parseFloat(number) : number;
  if (!value || isNaN(value)) {
    return '0.00';
  }
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(value);
};

// Initialize form state function
const initializeFormState = (userId = '') => ({
  recipientType: 'Customer',
  recipient: null,
  location: null,
  items: [{
    tileId: '',
    Name: '',
    location: null, // Per-item location
    freshBoxes: 0,
    freshPieces: 0,
    damagedBoxes: 0,
    damagedPieces: 0,
    rate: 0,
    discount: 0,
    amountBeforeDiscount: 0,
    amountAfterDiscount: 0,
    availableStock: {
      freshStockBox: 0,
      freshStockPieces: 0,
      cornerDamagedBox: 0,
      cornerDamagedPieces: 0
    },
    tileDetails: null,
    tt: {
      type: 'percentage',
      value: 0,
      calculatedAmount: 0
    }
  }],
  stockItems: [{
    stockItemId: '',
    name: '',
    location: null, // Per-item location
    freshQuantity: 0,
    damagedQuantity: 0,
    rate: 0,
    discount: 0,
    amountBeforeDiscount: 0,
    amountAfterDiscount: 0,
    availableStock: {
      freshQuantity: 0,
      damagedQuantity: 0
    },
    stockItemDetails: null
  }],
  vehicleNumber: '',
  transporterName: '',
  transportMedium: '',
  siteAddress: '',
  modeOfPayment: [],
  modeOfSales: 'Paid Instantly',
  creditReference: '',
  creditPeriod: 0,
  claimPaymentFrom: {
    name: '',
    number: ''
  },
  salesman: [],
  reference: '',
  roundoff: 0,
  Narration: '',
  packingAndForwarding: 0,
  packingAndForwardingGST: 0,
  Manual_dO_Number: '',
  poNumber: '',
  itemDescription: '',
  referenceName: '',
  cartage: 0,
  cartageGST: 0,
  labour: 0,
  labourGST: 0,
  totalAmountWithoutGST: 0,
  totalAmountWithGST: 0,
  totalTTAmount: 0,
  isReference: false,
  isNarration: false,
  createdBy: userId,
  salesOrder: null, // Link to TilesSalesOrder when imported from SO
  // Hidden fields for tracking
  deliveryOrderId: null, // Store the created delivery order ID
  tilesOutwardId: null   // Store the created tiles outward ID
});

const TilesDeliveryOrder = () => {
  const { user } = useContext(AuthContext);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Generate unique ID for items (used when item doesn't have an existing _id)
  const generateId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };
  
  // Initialize refs
  const formRef = useRef(null);
  const calculationInProgress = useRef(false);
  
  // Data source states
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [references, setReferences] = useState([]);

  // Order selection states
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [quickSearch, setQuickSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '', searchType: 'all', status: '', recipientType: '', modeOfSales: '',
    dateRange: '', startDate: null, endDate: null,
    minAmount: '', maxAmount: '',
    paymentModes: [], salesmen: [], transporters: [], references: [], createdBy: [],
    creditPeriod: '', minCreditPeriod: '', maxCreditPeriod: '', minItemCount: '', maxItemCount: ''
  });
  const [filterPresets, setFilterPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tilesDO_filterPresets') || '[]'); } catch { return []; }
  });
  const [filterHistory, setFilterHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tilesDO_filterHistory') || '[]'); } catch { return []; }
  });
  const [searchSuggestionsList, setSearchSuggestionsList] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const searchInputRef = useRef(null);

  // Basic states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [originalOrderData, setOriginalOrderData] = useState(null);

  // Print-related states
  const [selectedPrintOrderId, setSelectedPrintOrderId] = useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);

  // Form state
  const [form, setForm] = useState(() => initializeFormState(user?.user?._id));

  // Ensure form.items and form.stockItems are always arrays
  useEffect(() => {
    if (!form.items || !Array.isArray(form.items)) {
      const defaultItem = initializeFormState().items[0];
      setForm(prev => ({ 
        ...prev, 
        items: Array.isArray(prev.items) ? prev.items : [defaultItem] 
      }));
    }
    if (!form.stockItems || !Array.isArray(form.stockItems)) {
      const defaultStockItem = initializeFormState().stockItems[0];
      setForm(prev => ({ 
        ...prev, 
        stockItems: Array.isArray(prev.stockItems) ? prev.stockItems : [defaultStockItem] 
      }));
    }
  }, []);

  // Tiles selection states
  const [showTilesModal, setShowTilesModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);

  // Stock items selection states
  const [showStockItemsModal, setShowStockItemsModal] = useState(false);
  const [currentStockItemIndex, setCurrentStockItemIndex] = useState(0);

  // Confirmation modal states (declared here to be used in focus useEffect below)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [stockImpact, setStockImpact] = useState(null);

  // Import SO states
  const [showImportSOModal, setShowImportSOModal] = useState(false);
  const [pendingSalesOrders, setPendingSalesOrders] = useState([]);
  const [loadingPendingSO, setLoadingPendingSO] = useState(false);

  // Refs for modals
  const confirmButtonRef = useRef(null);
  
  // Ref for first tile selection button (to focus after recipient selection)
  const firstTileSelectButtonRef = useRef(null);
  
  // Refs for quantity inputs - stores refs for each item index
  const quantityInputRefs = useRef({});
  // Refs for stock item quantity inputs
  const stockItemQuantityInputRefs = useRef({});
  
  // Refs for item location selects - stores refs for each item index
  const itemLocationSelectRefs = useRef({});
  // Refs for stock item location selects
  const stockItemLocationSelectRefs = useRef({});

  // Focus confirmation button when modal opens
  useEffect(() => {
    if (showConfirmationModal && confirmButtonRef.current) {
      setTimeout(() => confirmButtonRef.current?.focus(), 100);
    }
  }, [showConfirmationModal]);

  // Additional safety check for all arrays
  useEffect(() => {
    if (!Array.isArray(customers)) setCustomers([]);
    if (!Array.isArray(dealers)) setDealers([]);
    if (!Array.isArray(locations)) setLocations([]);
    if (!Array.isArray(orders)) setOrders([]);
    if (!Array.isArray(transporters)) setTransporters([]);
    if (!Array.isArray(employees)) setEmployees([]);
    if (!Array.isArray(references)) setReferences([]);
  }, [customers, dealers, locations, orders, transporters, employees, references]);

  // Update filtered orders when orders or filters change
  useEffect(() => {
    applyOrderFilters();
  }, [orders, quickSearch, filters]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [quickSearch, filters, itemsPerPage]);

  // Generate search suggestions from loaded orders
  const generateSearchSuggestions = useCallback((searchText) => {
    if (!searchText || searchText.length < 2 || !orders?.length) {
      setSearchSuggestionsList([]);
      return;
    }
    const searchLower = searchText.toLowerCase();
    const suggestions = [];
    const seen = new Set();
    orders.forEach(order => {
      const fields = [
        { text: order.doNumber || order.orderNumber, type: 'DO Number', fullText: `${order.doNumber || order.orderNumber} - ${order.recipient?.name || order.recipient?.businessName || ''}` },
        { text: order.Manual_dO_Number, type: 'Manual DO', fullText: `Manual: ${order.Manual_dO_Number}` },
        { text: order.poNumber, type: 'PO Number', fullText: `PO: ${order.poNumber}` },
        { text: order.recipient?.name || order.recipient?.businessName || order.recipient?.companyName, type: 'Recipient', fullText: `${order.recipient?.name || order.recipient?.businessName || ''} (${order.recipientType || ''})` },
        { text: order.vehicleNumber, type: 'Vehicle', fullText: `Vehicle: ${order.vehicleNumber}` },
        { text: order.location?.name, type: 'Location', fullText: `Location: ${order.location?.name}` }
      ];
      fields.forEach(f => {
        if (f.text && f.text.toLowerCase().includes(searchLower) && !seen.has(f.text)) {
          seen.add(f.text);
          suggestions.push(f);
        }
      });
    });
    setSearchSuggestionsList(suggestions.slice(0, 8));
  }, [orders]);

  // Handle quick search input change
  const handleQuickSearchChange = (value) => {
    setQuickSearch(value);
    generateSearchSuggestions(value);
    if (value.length > 1) {
      setShowSearchSuggestions(true);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSearchSuggestions(false);
    }
  };

  // Handle search keyboard nav
  const handleSearchKeyDown = (e) => {
    if (!showSearchSuggestions || searchSuggestionsList.length === 0) {
      if (e.key === 'Enter') { e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev < searchSuggestionsList.length - 1 ? prev + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : searchSuggestionsList.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && searchSuggestionsList[selectedSuggestionIndex]) {
        setQuickSearch(searchSuggestionsList[selectedSuggestionIndex].text);
        setShowSearchSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowSearchSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Filter change handler
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Date range preset handler
  const handleDateRangeChange = (rangeName) => {
    const now = new Date();
    let startDate = null, endDate = null;
    switch (rangeName) {
      case 'today': startDate = endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
      case 'yesterday': { const y = new Date(now); y.setDate(y.getDate()-1); startDate = endDate = new Date(y.getFullYear(), y.getMonth(), y.getDate()); break; }
      case 'thisWeek': { startDate = new Date(now); startDate.setDate(now.getDate()-now.getDay()); startDate.setHours(0,0,0,0); endDate = now; break; }
      case 'lastWeek': { const s = new Date(now); s.setDate(now.getDate()-now.getDay()-7); s.setHours(0,0,0,0); const e = new Date(s); e.setDate(s.getDate()+6); e.setHours(23,59,59,999); startDate = s; endDate = e; break; }
      case 'thisMonth': startDate = new Date(now.getFullYear(), now.getMonth(), 1); endDate = now; break;
      case 'lastMonth': startDate = new Date(now.getFullYear(), now.getMonth()-1, 1); endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23,59,59); break;
      case 'last7Days': { startDate = new Date(now.getTime()-7*864e5); endDate = now; break; }
      case 'last30Days': { startDate = new Date(now.getTime()-30*864e5); endDate = now; break; }
      case 'thisYear': startDate = new Date(now.getFullYear(), 0, 1); endDate = now; break;
      default: break;
    }
    setFilters(prev => ({ ...prev, dateRange: rangeName, startDate, endDate }));
  };

  // Multi-select filter handler
  const handleMultiSelectFilter = (key, selectedOptions) => {
    setFilters(prev => ({ ...prev, [key]: selectedOptions ? selectedOptions.map(o => o.value) : [] }));
  };

  // Save filter preset
  const saveFilterPreset = async () => {
    const { value: name } = await Swal.fire({ title: 'Save Filter Preset', input: 'text', inputLabel: 'Preset Name', inputPlaceholder: 'Enter a name for this preset', showCancelButton: true, inputValidator: (v) => !v && 'Please enter a name' });
    if (name) {
      const newPreset = { name, filters: { ...filters }, quickSearch, createdAt: new Date().toISOString() };
      const updated = [...filterPresets, newPreset];
      setFilterPresets(updated);
      localStorage.setItem('tilesDO_filterPresets', JSON.stringify(updated));
      Swal.fire({ icon: 'success', title: 'Preset Saved!', text: `"${name}" has been saved`, timer: 1500, showConfirmButton: false });
    }
  };

  // Load filter preset
  const loadFilterPreset = (preset) => {
    setFilters(preset.filters);
    setQuickSearch(preset.quickSearch || '');
    Swal.fire({ icon: 'success', title: 'Preset Loaded!', text: `"${preset.name}" applied`, timer: 1200, showConfirmButton: false });
  };

  // Delete filter preset
  const deleteFilterPreset = (index) => {
    const updated = filterPresets.filter((_, i) => i !== index);
    setFilterPresets(updated);
    localStorage.setItem('tilesDO_filterPresets', JSON.stringify(updated));
  };

  // Add to filter history
  const addToFilterHistory = () => {
    const hasFilters = quickSearch || Object.entries(filters).some(([k, v]) => Array.isArray(v) ? v.length > 0 : (v !== '' && v !== null));
    if (!hasFilters) return;
    const entry = { filters: { ...filters }, quickSearch, timestamp: new Date().toISOString() };
    const updated = [entry, ...filterHistory.filter((_, i) => i < 4)];
    setFilterHistory(updated);
    localStorage.setItem('tilesDO_filterHistory', JSON.stringify(updated));
  };

  // Reset all filters
  const resetAllFilters = () => {
    setQuickSearch('');
    setFilters({
      search: '', searchType: 'all', status: '', recipientType: '', modeOfSales: '',
      dateRange: '', startDate: null, endDate: null,
      minAmount: '', maxAmount: '',
      paymentModes: [], salesmen: [], transporters: [], references: [], createdBy: [],
      creditPeriod: '', minCreditPeriod: '', maxCreditPeriod: '', minItemCount: '', maxItemCount: ''
    });
    setShowSearchSuggestions(false);
  };

  // Compute last 7 days stats from client-side orders
  const lastSevenDaysStats = React.useMemo(() => {
    if (!orders?.length) return null;
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 864e5);
    const recent = orders.filter(o => new Date(o.createdAt) >= sevenDaysAgo);
    if (!recent.length) return null;
    const pending = recent.filter(o => o.status === 'Pending');
    const completed = recent.filter(o => o.status === 'Completed');
    const cancelled = recent.filter(o => o.status === 'Cancelled');
    const sumAmount = (arr) => arr.reduce((s, o) => s + (parseFloat(o.totalAmountWithGST || o.totalAmount) || 0), 0);
    return {
      totalOrders: recent.length,
      pendingOrders: pending.length,
      completedOrders: completed.length,
      cancelledOrders: cancelled.length,
      totalAmount: formatNumberWithCommas(sumAmount(recent)),
      pendingAmount: formatNumberWithCommas(sumAmount(pending)),
      completedAmount: formatNumberWithCommas(sumAmount(completed)),
      cancelledAmount: formatNumberWithCommas(sumAmount(cancelled))
    };
  }, [orders]);

  // Combined filter and search function
  const applyOrderFilters = () => {
    let filtered = orders || [];

    // Apply quick search
    const searchText = quickSearch.trim().toLowerCase();
    if (searchText) {
      filtered = filtered.filter(order => {
        if (filters.searchType !== 'all') {
          switch (filters.searchType) {
            case 'doNumber': return (order.doNumber || order.orderNumber || '').toLowerCase().includes(searchText);
            case 'manualDO': return (order.Manual_dO_Number || '').toLowerCase().includes(searchText);
            case 'poNumber': return (order.poNumber || '').toLowerCase().includes(searchText);
            case 'recipient': return (order.recipient?.name || order.recipient?.companyName || order.recipient?.businessName || '').toLowerCase().includes(searchText);
            case 'vehicle': return (order.vehicleNumber || '').toLowerCase().includes(searchText);
            default: break;
          }
        }
        const orderNumber = (order.doNumber || order.orderNumber || '').toLowerCase();
        const customer = (order.recipient?.name || order.recipient?.companyName || order.recipient?.businessName || '').toLowerCase();
        const location = (order.location?.name || '').toLowerCase();
        const status = (order.status || '').toLowerCase();
        const totalAmount = (order.totalAmountWithGST || order.totalAmount || '').toString();
        const vehicleNumber = (order.vehicleNumber || '').toLowerCase();
        const transporter = (order.transporterName?.name || '').toLowerCase();
        const manualDO = (order.Manual_dO_Number || '').toLowerCase();
        const poNumber = (order.poNumber || '').toLowerCase();
        return orderNumber.includes(searchText) || customer.includes(searchText) || location.includes(searchText) ||
               status.includes(searchText) || totalAmount.includes(searchText) || vehicleNumber.includes(searchText) ||
               transporter.includes(searchText) || manualDO.includes(searchText) || poNumber.includes(searchText);
      });
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(o => o.status === filters.status);
    }

    // Recipient type filter
    if (filters.recipientType) {
      filtered = filtered.filter(o => o.recipientType === filters.recipientType);
    }

    // Mode of sales filter
    if (filters.modeOfSales) {
      filtered = filtered.filter(o => o.modeOfSales === filters.modeOfSales);
    }

    // Date range filter
    if (filters.startDate) {
      const start = new Date(filters.startDate); start.setHours(0,0,0,0);
      filtered = filtered.filter(o => new Date(o.createdAt) >= start);
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate); end.setHours(23,59,59,999);
      filtered = filtered.filter(o => new Date(o.createdAt) <= end);
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(o => (parseFloat(o.totalAmountWithGST || o.totalAmount) || 0) >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(o => (parseFloat(o.totalAmountWithGST || o.totalAmount) || 0) <= parseFloat(filters.maxAmount));
    }

    // Payment modes filter
    if (filters.paymentModes.length > 0) {
      filtered = filtered.filter(o => {
        const orderModes = Array.isArray(o.modeOfPayment) ? o.modeOfPayment.map(m => m.toLowerCase()) : [];
        return filters.paymentModes.some(pm => orderModes.includes(pm.toLowerCase()));
      });
    }

    // Salesperson filter
    if (filters.salesmen.length > 0) {
      filtered = filtered.filter(o => {
        const orderSalesmen = Array.isArray(o.salesman) ? o.salesman.map(s => s._id || s) : [];
        return filters.salesmen.some(sid => orderSalesmen.includes(sid));
      });
    }

    // Transporters filter
    if (filters.transporters.length > 0) {
      filtered = filtered.filter(o => {
        const tid = o.transporterName?._id || o.transporterName;
        return filters.transporters.includes(tid);
      });
    }

    // References filter
    if (filters.references.length > 0) {
      filtered = filtered.filter(o => {
        const rid = o.interior?._id || o.interior;
        return filters.references.includes(rid);
      });
    }

    // Created by filter
    if (filters.createdBy.length > 0) {
      filtered = filtered.filter(o => {
        const cid = o.createdBy?._id || o.createdBy;
        return filters.createdBy.includes(cid);
      });
    }

    // Credit period filter
    if (filters.creditPeriod) {
      filtered = filtered.filter(o => String(o.creditPeriod) === filters.creditPeriod);
    }
    if (filters.minCreditPeriod) {
      filtered = filtered.filter(o => (parseFloat(o.creditPeriod) || 0) >= parseFloat(filters.minCreditPeriod));
    }
    if (filters.maxCreditPeriod) {
      filtered = filtered.filter(o => (parseFloat(o.creditPeriod) || 0) <= parseFloat(filters.maxCreditPeriod));
    }

    // Item count filter
    if (filters.minItemCount) {
      filtered = filtered.filter(o => (o.items?.length || 0) >= parseInt(filters.minItemCount));
    }
    if (filters.maxItemCount) {
      filtered = filtered.filter(o => (o.items?.length || 0) <= parseInt(filters.maxItemCount));
    }

    setFilteredOrders(filtered);
  };

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // showConfirmationModal and stockImpact are declared earlier (before the focus useEffect)
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [showTransporterModal, setShowTransporterModal] = useState(false);
  
  // Edit recipient modal states
  const [showEditRecipientModal, setShowEditRecipientModal] = useState(false);
  const [editingRecipientOrder, setEditingRecipientOrder] = useState(null);
  const [editRecipientType, setEditRecipientType] = useState('Customer');
  const [editRecipientValue, setEditRecipientValue] = useState(null);
  const [updatingRecipient, setUpdatingRecipient] = useState(false);
  const [showEditRecipientPicker, setShowEditRecipientPicker] = useState(false);
  
  // Status management states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrderForStatus, setSelectedOrderForStatus] = useState(null);
  const [statusUpdateData, setStatusUpdateData] = useState({
    status: '',
    remarks: '',
    invoiceNumber: ''
  });

  // Form input states
  const [paymentModesInput, setPaymentModesInput] = useState('');

  // Pagination states
  const [count, setCount] = useState(0);

  // Constants
  const paymentModeOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'credit', label: 'Credit' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'neft', label: 'NEFT' },
    { value: 'rtgs', label: 'RTGS' },
    { value: 'upi', label: 'UPI' },
    { value: 'net banking', label: 'Net Banking' },
    { value: 'card', label: 'Card Payment' },
    { value: 'Part Payment at Site', label: 'Part Payment at Site' },
    { value: 'Full Payment at Site', label: 'Full Payment at Site' },
  ];

  // Global keyboard shortcuts for the form
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Don't trigger if any modal is open
      if (showTilesModal || showStockItemsModal || showConfirmationModal || showViewModal || showRecipientModal || showReferenceModal || showTransporterModal || showEditRecipientModal || showEditRecipientPicker) {
        return;
      }

      // Ctrl+Enter to submit form (open confirmation modal)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        // Check if form can be submitted
        const hasTiles = form.items?.some(item => item.Name);
        const hasStockItems = form.stockItems?.some(item => item.stockItemId || item.name);
        if (form.recipient?.value && (hasTiles || hasStockItems)) {
          handleSubmit();
        }
      }

      // Insert key to add new tile item (or Alt+T when Insert is not available)
      if (e.key === 'Insert' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        const newItemIndex = form.items?.length || 0;
        addItem();
        // Focus on the new item's location select after a short delay
        setTimeout(() => {
          const locationSelect = itemLocationSelectRefs.current[newItemIndex];
          if (locationSelect?.focus) {
            locationSelect.focus();
          }
        }, 100);
      }
      
      // Alt+T (lowercase) to add new tile item (alternative)
      if (e.altKey && !e.shiftKey && !e.ctrlKey && e.key.toLowerCase() === 't' && e.code === 'KeyT') {
        // Don't prevent if this might be a sidebar shortcut - check if there's a location
        if (form.location) {
          e.preventDefault();
          const newItemIndex = form.items?.length || 0;
          addItem();
          // Focus on the new item's location select after a short delay
          setTimeout(() => {
            const locationSelect = itemLocationSelectRefs.current[newItemIndex];
            if (locationSelect?.focus) {
              locationSelect.focus();
            }
          }, 100);
        }
      }

      // Shift+Insert to add new stock item (or Alt+S when Insert is not available)
      if (e.key === 'Insert' && e.shiftKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        const newStockItemIndex = form.stockItems?.length || 0;
        addStockItem();
        // Focus on the new stock item's location select after a short delay
        setTimeout(() => {
          const locationSelect = stockItemLocationSelectRefs.current[newStockItemIndex];
          if (locationSelect?.focus) {
            locationSelect.focus();
          }
        }, 100);
      }
      
      // Alt+S (lowercase) to add new stock item (alternative) - only when location is selected
      if (e.altKey && !e.shiftKey && !e.ctrlKey && e.key.toLowerCase() === 's' && e.code === 'KeyS') {
        // Don't prevent if this might be a sidebar shortcut - check if there's a location
        if (form.location) {
          e.preventDefault();
          const newStockItemIndex = form.stockItems?.length || 0;
          addStockItem();
          // Focus on the new stock item's location select after a short delay
          setTimeout(() => {
            const locationSelect = stockItemLocationSelectRefs.current[newStockItemIndex];
            if (locationSelect?.focus) {
              locationSelect.focus();
            }
          }, 100);
        }
      }

      // / key to focus search (only when not typing in an input)
      if (e.key === '/' && !e.ctrlKey && !e.altKey && !e.metaKey && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Ctrl+K to focus search input
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Ctrl+F to toggle filter panel (override browser find)
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && !e.shiftKey) {
        e.preventDefault();
        setShowFilters(prev => !prev);
      }

      // Escape to cancel edit mode or close filters
      if (e.key === 'Escape') {
        if (showSearchSuggestions) {
          setShowSearchSuggestions(false);
          setSelectedSuggestionIndex(-1);
        } else if (showEditRecipientModal) {
          setShowEditRecipientModal(false);
          setEditingRecipientOrder(null);
          setEditRecipientValue(null);
        } else if (showFilters) {
          setShowFilters(false);
        } else if (isEditing) {
          e.preventDefault();
          handleCancelEdit();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [showTilesModal, showStockItemsModal, showConfirmationModal, showViewModal, showRecipientModal, showReferenceModal, showTransporterModal, showEditRecipientModal, showEditRecipientPicker, form, isEditing]);

  // Load initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load orders on mount
  useEffect(() => {
    fetchOrders();
  }, []);

  // Calculate totals when items change (debounced to prevent conflicts with user input)
  useEffect(() => {
    if (!calculationInProgress.current && form.items && Array.isArray(form.items)) {
      const timeoutId = setTimeout(() => {
        if (!calculationInProgress.current) {
          calculateTotals();
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [form.items, form.packingAndForwarding, form.cartage, form.labour, form.roundoff]);

  // ============================================================
  // Auto-import SO from Sales Order page shortcut
  // ============================================================
  useEffect(() => {
    const checkPendingSOImport = async () => {
      try {
        const pendingData = localStorage.getItem('pendingSOImport');
        if (!pendingData) return;
        
        const { salesOrderId, timestamp } = JSON.parse(pendingData);
        // Only process if less than 30 seconds old
        if (Date.now() - timestamp > 30000) {
          localStorage.removeItem('pendingSOImport');
          return;
        }
        localStorage.removeItem('pendingSOImport');

        // Fetch the full SO details
        const res = await api.get(`/tiles-sales-orders/details/${salesOrderId}`);
        const so = res.data?.data || res.data;
        if (!so || !so._id) return;

        // Auto-select recipient type and recipient
        if (so.recipientType && so.recipient) {
          const recipientName = so.recipient?.name || so.recipient?.companyName || so.recipient?.businessName || '';
          const recipientId = so.recipient?._id || so.recipient;

          setForm(prev => ({
            ...prev,
            recipientType: so.recipientType,
            recipient: { value: recipientId, label: recipientName }
          }));

          // Wait for form to update, then import SO items
          setTimeout(() => {
            handleImportSO(so);
          }, 500);
        }
      } catch (error) {
        console.error('Error auto-importing SO:', error);
      }
    };

    // Run after initial data is loaded
    const timer = setTimeout(checkPendingSOImport, 1000);
    return () => clearTimeout(timer);
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [
        customersRes,
        dealersRes,
        locationsRes,
        transportersRes,
        employeesRes,
        referencesRes
      ] = await Promise.all([
        api.get('/customers'),
        api.get('/dealers'),
        api.get(`/locations/companyId/${user?.user?.company?._id}`).catch(() => ({ data: [] })),
        api.get('/transporters/all', { 
          params: { userCompanyId: user?.user?.company?._id || user?.user?.company } 
        }).catch(() => ({ data: [] })),
        api.get(`/employees/company2/${user?.user?.company?._id || user?.user?.company}`).catch(() => ({ data: [] })),
        api.get('/references/all', { 
          params: { userCompanyId: user?.user?.company?._id || user?.user?.company } 
        }).catch(() => ({ data: [] }))
      ]);

      setCustomers(Array.isArray(customersRes?.data) ? customersRes.data : []);
      setDealers(Array.isArray(dealersRes?.data) ? dealersRes.data : []);
      setLocations(Array.isArray(locationsRes?.data) ? locationsRes.data : []);
      setTransporters(Array.isArray(transportersRes?.data) ? transportersRes.data : []);
      console.log('👥 Employees API Response:', {
        status: employeesRes?.status,
        dataType: typeof employeesRes?.data,
        hasEmployees: !!employeesRes?.data?.employees,
        employeesLength: employeesRes?.data?.employees?.length || 0,
        employees: employeesRes?.data?.employees
      });
      setEmployees(Array.isArray(employeesRes?.data?.employees) ? employeesRes.data.employees : []);
      setReferences(Array.isArray(referencesRes?.data) ? referencesRes.data : []);

      console.log('✅ Initial data loaded:', {
        customers: customersRes.data?.length || 0,
        dealers: dealersRes.data?.length || 0,
        locations: locationsRes.data?.length || 0,
        transporters: transportersRes.data?.length || 0,
        employees: employeesRes.data?.employees?.length || 0,
        references: referencesRes.data?.length || 0
      });
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Load Initial Data',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>The following data could not be loaded:</strong></p>
            <ul class="list-disc list-inside text-sm mb-3">
              <li>Customers & Dealers</li>
              <li>Locations & Transporters</li>
              <li>Employees & References</li>
            </ul>
            <p class="text-sm"><strong>Solution:</strong> Please refresh the page or check your network connection.</p>
            <p class="text-xs mt-2 text-gray-500">Error: ${error.message || 'Unknown error'}</p>
          </div>
        `,
        timer: 8000,
        timerProgressBar: true,
        customClass: { popup: 'swal-dark-mode' }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      if (!user?.user?._id) {
        console.warn('⚠️ No user ID found - cannot fetch employee-specific orders');
        setOrders([]);
        return;
      }

      setLoading(true);
      console.log('🔍 Fetching orders for employee:', user.user._id);
      
      const response = await api.get('/tiles/delivery-orders', {
        params: { 
          page: 1, 
          limit: 0,
          employeeId: user.user._id
        }
      });
      
      const orders = Array.isArray(response?.data?.orders) ? response.data.orders : [];
      console.log(`📊 Found ${orders.length} orders for employee`);
      
      setOrders(orders);
      setCount(response?.data?.pagination?.total || orders.length);
    } catch (error) {
      console.error('❌ Error fetching orders:', error);
      if (error.response?.status === 401) {
        Swal.fire({
          icon: 'error',
          title: 'Authentication Session Expired',
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Your session has expired or is invalid.</strong></p>
              <p class="text-sm mb-3">This usually happens when:</p>
              <ul class="list-disc list-inside text-sm mb-3">
                <li>You've been logged out from another device</li>
                <li>Your session has timed out due to inactivity</li>
                <li>Your login credentials have changed</li>
              </ul>
              <p class="text-sm"><strong>Solution:</strong> Please log out and log in again to continue.</p>
            </div>
          `,
          timer: 10000,
          timerProgressBar: true,
          confirmButtonText: 'Go to Login',
          customClass: { popup: 'swal-dark-mode' }
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Unable to Load Delivery Orders',
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Failed to retrieve your delivery orders from the server.</strong></p>
              <p class="text-sm mb-2"><strong>Possible causes:</strong></p>
              <ul class="list-disc list-inside text-sm mb-3">
                <li>Network connectivity issues</li>
                <li>Server is temporarily unavailable</li>
                <li>Database connection timeout</li>
              </ul>
              <p class="text-sm"><strong>Solution:</strong> Please wait a moment and try refreshing the page.</p>
              <p class="text-xs mt-2 text-gray-500">Error: ${error.message || 'Connection failed'}</p>
            </div>
          `,
          timer: 8000,
          timerProgressBar: true,
          customClass: { popup: 'swal-dark-mode' }
        });
      }
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== TILE & STOCK ITEM SELECTION HANDLERS =====
  // (Fetch, search, and keyboard navigation are now handled by shared TileSelectionModal / StockItemSelectionModal)

  const handleStockItemSelect = (stockItem) => {
    if (!form.stockItems || !Array.isArray(form.stockItems)) return;

    const currentIndex = currentStockItemIndex;
    
    if (currentIndex < 0 || currentIndex >= form.stockItems.length) {
      console.error('Invalid stock item index:', currentIndex);
      return;
    }

    // Get the current stock item's location (preserve it)
    const currentStockItemData = form.stockItems[currentIndex];
    const itemLocation = currentStockItemData?.location || form.location;

    // Get location-specific stock information
    const locationStock = stockItem.locationStock || stockItem.stock?.find(s => {
      const stockLocationId = s.location?._id?.toString?.() || s.location?.toString?.() || s.location;
      const targetLocation = itemLocation?.toString?.() || itemLocation;
      return stockLocationId === targetLocation;
    });
    const availableFresh = locationStock?.freshQuantity || 0;
    const availableDamaged = locationStock?.damagedQuantity || 0;

    const newStockItems = [...form.stockItems];
    newStockItems[currentIndex] = {
      ...currentStockItemData,
      stockItemId: stockItem._id,
      name: stockItem.name || '',
      location: itemLocation,
      freshQuantity: 0,
      damagedQuantity: 0,
      rate: stockItem.rate || 0,
      discount: 0,
      amountBeforeDiscount: 0,
      amountAfterDiscount: 0,
      availableStock: {
        freshQuantity: availableFresh,
        damagedQuantity: availableDamaged
      },
      stockItemDetails: stockItem
    };

    setForm(prev => ({
      ...prev,
      stockItems: newStockItems
    }));

    setShowStockItemsModal(false);
    
    // Focus on first available quantity input after stock item selection
    setTimeout(() => {
      const itemRefs = stockItemQuantityInputRefs.current[currentIndex];
      if (itemRefs) {
        if (availableFresh > 0 && itemRefs.freshQuantity) {
          itemRefs.freshQuantity.focus();
          itemRefs.freshQuantity.select();
        } else if (availableDamaged > 0 && itemRefs.damagedQuantity) {
          itemRefs.damagedQuantity.focus();
          itemRefs.damagedQuantity.select();
        }
      }
    }, 100);
  };

  const handleTileSelect = (tile) => {
    if (!form.items || !Array.isArray(form.items)) return;
    
    const updatedItems = [...form.items];
    const currentItem = updatedItems[currentItemIndex];
    const itemLocation = currentItem.location || form.location;
    const itemLocationStr = itemLocation?.toString?.() || itemLocation;

    // Resolve location stock from tile data
    let locationStock;
    if (tile.locationStock && typeof tile.locationStock === 'object') {
      locationStock = tile.locationStock;
    } else if (Array.isArray(tile.stock) && tile.stock.length > 0 && itemLocation) {
      const matchedStock = tile.stock.find(s => {
        const stockLocationId = s.location?._id?.toString?.() || s.location?._id || s.location?.toString?.() || s.location;
        return stockLocationId?.toString?.() === itemLocationStr;
      });
      locationStock = matchedStock || { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 };
    } else {
      locationStock = { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 };
    }

    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      tileId: tile._id,
      Name: tile.tilesName,
      location: itemLocation,
      rate: 0,
      availableStock: {
        freshStockBox: locationStock.freshStockBox || 0,
        freshStockPieces: locationStock.freshStockPieces || 0,
        cornerDamagedBox: locationStock.cornerDamagedBox || 0,
        cornerDamagedPieces: locationStock.cornerDamagedPieces || 0
      },
      tileDetails: tile
    };

    setForm(prev => ({ ...prev, items: updatedItems }));
    setShowTilesModal(false);
    
    // Focus on first available quantity input after tile selection
    setTimeout(() => {
      const itemRefs = quantityInputRefs.current[currentItemIndex];
      if (itemRefs) {
        if (locationStock.freshStockBox > 0 && itemRefs.freshBoxes) {
          itemRefs.freshBoxes.focus();
          itemRefs.freshBoxes.select();
        } else if (locationStock.freshStockPieces > 0 && itemRefs.freshPieces) {
          itemRefs.freshPieces.focus();
          itemRefs.freshPieces.select();
        } else if ((locationStock.cornerDamagedBox || 0) > 0 && itemRefs.damagedBoxes) {
          itemRefs.damagedBoxes.focus();
          itemRefs.damagedBoxes.select();
        } else if ((locationStock.cornerDamagedPieces || 0) > 0 && itemRefs.damagedPieces) {
          itemRefs.damagedPieces.focus();
          itemRefs.damagedPieces.select();
        }
      }
    }, 100);
  };

  // Keyboard navigation for confirmation modal
  const handleConfirmationModalKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowConfirmationModal(false);
      return;
    }
    if (e.key === 'Enter' && !submitting) {
      e.preventDefault();
      confirmSubmit();
    }
  };

  // Handle recipient selection from modal
  const handleRecipientSelect = (recipient, recipientType) => {
    // Get the appropriate name based on recipient type
    const recipientName = recipientType === 'Customer' ? recipient.name : recipient.businessName;
    
    setForm(prev => ({
      ...prev,
      recipientType: recipientType,
      recipient: {
        value: recipient._id,
        label: recipientType === 'Customer' 
          ? `${recipient.name} (Ph: ${recipient.phone}, ID: ${recipient.customerCode}, Station: ${recipient.station?.name})`
          : recipient.businessName
      }
    }));
    
    setShowRecipientModal(false);
    
    // Focus on first tile selection button after recipient selection
    setTimeout(() => {
      if (firstTileSelectButtonRef.current) {
        firstTileSelectButtonRef.current.focus();
      }
    }, 100);
  };

  // ============================================================
  // Import from Sales Order — Fetch pending SOs and populate DO
  // ============================================================
  const fetchPendingSalesOrders = async () => {
    if (!form.recipient?.value || !form.recipientType) {
      Swal.fire({ icon: 'warning', title: 'Select Recipient First', text: 'Please select a customer/dealer before importing a sales order.' });
      return;
    }
    setLoadingPendingSO(true);
    setShowImportSOModal(true);
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const res = await api.get(`/tiles-sales-orders/pending/${form.recipientType}/${form.recipient.value}?companyId=${companyId}`);
      setPendingSalesOrders(res.data?.data || res.data || []);
    } catch (err) {
      console.error('Error fetching pending SO:', err);
      setPendingSalesOrders([]);
    } finally {
      setLoadingPendingSO(false);
    }
  };

  const handleImportSO = async (so) => {
    const itemLocation = form.location || '';
    const itemLocationStr = itemLocation?.toString?.() || itemLocation;

    // Map SO items to DO items format (initial mapping with zero stock)
    const doItems = (so.items || []).map(soItem => {
      const remainFreshBoxes = Math.max(0, (soItem.freshBoxes || 0) - (soItem.deliveredFreshBoxes || 0));
      const remainFreshPieces = Math.max(0, (soItem.freshPieces || 0) - (soItem.deliveredFreshPieces || 0));
      const remainDamagedBoxes = Math.max(0, (soItem.damagedBoxes || 0) - (soItem.deliveredDamagedBoxes || 0));
      const remainDamagedPieces = Math.max(0, (soItem.damagedPieces || 0) - (soItem.deliveredDamagedPieces || 0));

      return {
        tileId: soItem.tileId?._id || soItem.tileId || '',
        Name: soItem.tileName || soItem.tileId?.tilesName || '',
        location: itemLocation,
        freshBoxes: remainFreshBoxes,
        freshPieces: remainFreshPieces,
        damagedBoxes: remainDamagedBoxes,
        damagedPieces: remainDamagedPieces,
        rate: soItem.rate || 0,
        discount: soItem.discount || 0,
        amountBeforeDiscount: 0,
        amountAfterDiscount: 0,
        availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 },
        tileDetails: soItem.tileId || null
      };
    }).filter(item => item.tileId && (item.freshBoxes > 0 || item.freshPieces > 0 || item.damagedBoxes > 0 || item.damagedPieces > 0));

    // Map SO stock items to DO stock items format (initial mapping with zero stock)
    const doStockItems = (so.stockItems || []).map(soSI => {
      const remainFresh = Math.max(0, (soSI.freshQuantity || 0) - (soSI.deliveredFreshQuantity || 0));
      const remainDamaged = Math.max(0, (soSI.damagedQuantity || 0) - (soSI.deliveredDamagedQuantity || 0));

      return {
        stockItemId: soSI.stockItemId?._id || soSI.stockItemId || '',
        name: soSI.name || soSI.stockItemId?.name || '',
        location: itemLocation,
        freshQuantity: remainFresh,
        damagedQuantity: remainDamaged,
        rate: soSI.rate || 0,
        discount: soSI.discount || 0,
        amountBeforeDiscount: 0,
        amountAfterDiscount: 0,
        availableStock: { freshQuantity: 0, damagedQuantity: 0 },
        stockItemDetails: soSI.stockItemId || null
      };
    }).filter(item => item.stockItemId && (item.freshQuantity > 0 || item.damagedQuantity > 0));

    // Fetch actual stock data for tiles in parallel
    if (doItems.length > 0) {
      try {
        const tileIds = [...new Set(doItems.map(item => item.tileId).filter(Boolean))];
        const tileResponses = await Promise.all(
          tileIds.map(id => api.get(`/tiles/get-tile/${id}`).catch(() => null))
        );
        const tileDataMap = {};
        tileResponses.forEach(res => {
          const tile = res?.data?.tile;
          if (tile && tile._id) tileDataMap[tile._id] = tile;
        });

        // Update each DO item with actual stock data
        doItems.forEach(item => {
          const tile = tileDataMap[item.tileId];
          if (!tile) return;

          // Find location-specific stock
          let locationStock = { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 };
          if (Array.isArray(tile.stock) && tile.stock.length > 0 && itemLocationStr) {
            const matched = tile.stock.find(s => {
              const stockLocId = s.location?._id?.toString?.() || s.location?._id || s.location?.toString?.() || s.location;
              return stockLocId?.toString?.() === itemLocationStr;
            });
            if (matched) locationStock = matched;
          }

          item.availableStock = {
            freshStockBox: locationStock.freshStockBox || 0,
            freshStockPieces: locationStock.freshStockPieces || 0,
            cornerDamagedBox: locationStock.cornerDamagedBox || 0,
            cornerDamagedPieces: locationStock.cornerDamagedPieces || 0
          };
          item.tileDetails = tile;
        });
      } catch (err) {
        console.error('Error fetching tile stock for SO import:', err);
      }
    }

    // Fetch actual stock data for stock items in parallel
    if (doStockItems.length > 0) {
      try {
        const stockItemIds = [...new Set(doStockItems.map(item => item.stockItemId).filter(Boolean))];
        const siResponses = await Promise.all(
          stockItemIds.map(id => api.get(`/stock-items/get-stock-item/${id}`).catch(() => null))
        );
        const siDataMap = {};
        siResponses.forEach(res => {
          const si = res?.data?.stockItem;
          if (si && si._id) siDataMap[si._id] = si;
        });

        // Update each DO stock item with actual stock data
        doStockItems.forEach(item => {
          const si = siDataMap[item.stockItemId];
          if (!si) return;

          // Find location-specific stock
          let locationStock = { freshQuantity: 0, damagedQuantity: 0 };
          if (Array.isArray(si.stock) && si.stock.length > 0 && itemLocationStr) {
            const matched = si.stock.find(s => {
              const stockLocId = s.location?._id?.toString?.() || s.location?.toString?.() || s.location;
              return stockLocId?.toString?.() === itemLocationStr;
            });
            if (matched) locationStock = matched;
          }

          item.availableStock = {
            freshQuantity: locationStock.freshQuantity || 0,
            damagedQuantity: locationStock.damagedQuantity || 0
          };
          item.stockItemDetails = si;
        });
      } catch (err) {
        console.error('Error fetching stock item data for SO import:', err);
      }
    }

    setForm(prev => ({
      ...prev,
      salesOrder: so._id,
      items: doItems.length > 0 ? doItems : prev.items,
      stockItems: doStockItems.length > 0 ? doStockItems : prev.stockItems,
      siteAddress: so.siteAddress || prev.siteAddress,
      Narration: `Imported from SO: ${so.orderNumber}${prev.Narration ? '\n' + prev.Narration : ''}`,
      salesman: so.salesman?.map(s => s._id || s) || prev.salesman,
      contactPersonName: so.contactPersonName || prev.contactPersonName,
      contactNumber: so.contactNumber || prev.contactNumber
    }));

    setShowImportSOModal(false);
    setTimeout(() => calculateTotals(), 200);

    Swal.fire({
      icon: 'success',
      title: 'Sales Order Imported',
      text: `${so.orderNumber} imported with ${doItems.length} tile items and ${doStockItems.length} stock items. Remaining quantities populated.`,
      timer: 3000,
      showConfirmButton: false
    });
  };

  // Payment modes handlers
  const handlePaymentModesChange = (selected) => {
    const selectedValues = selected ? selected.map(option => option.value) : [];
    setForm(prev => ({
      ...prev,
      modeOfPayment: selectedValues
    }));
    
    // Clear input when selecting from dropdown
    if (selected && selected.length > 0) {
      setPaymentModesInput('');
    }
  };
  
  const handlePaymentModeInput = (e) => {
    setPaymentModesInput(e.target.value);
    
    // Parse comma-separated values and update form
    const modes = e.target.value.split(',').map(mode => mode.trim()).filter(mode => mode);
    setForm(prev => ({
      ...prev,
      modeOfPayment: modes
    }));
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...(prev.items || []), {
        tileId: '',
        Name: '',
        location: prev.location, // Default to form's location
        freshBoxes: 0,
        freshPieces: 0,
        damagedBoxes: 0,
        damagedPieces: 0,
        rate: 0,
        discount: 0,
        amountBeforeDiscount: 0,
        amountAfterDiscount: 0,
        availableStock: {
          freshStockBox: 0,
          freshStockPieces: 0,
          cornerDamagedBox: 0,
          cornerDamagedPieces: 0
        },
        tileDetails: null,
        tt: {
          type: 'percentage',
          value: 0,
          calculatedAmount: 0
        }
      }]
    }));
  };

  const removeItem = (index) => {
    if (form.items && form.items.length > 1) {
      setForm(prev => ({
        ...prev,
        items: (prev.items || []).filter((_, i) => i !== index)
      }));
    }
  };

  // ===== STOCK ITEMS ADD/REMOVE/UPDATE FUNCTIONS =====

  const addStockItem = () => {
    setForm(prev => ({
      ...prev,
      stockItems: [...(prev.stockItems || []), {
        stockItemId: '',
        name: '',
        location: prev.location, // Default to form's location
        freshQuantity: 0,
        damagedQuantity: 0,
        rate: 0,
        discount: 0,
        amountBeforeDiscount: 0,
        amountAfterDiscount: 0,
        availableStock: {
          freshQuantity: 0,
          damagedQuantity: 0
        },
        stockItemDetails: null
      }]
    }));
  };

  const removeStockItem = (index) => {
    if (form.stockItems && form.stockItems.length > 1) {
      setForm(prev => ({
        ...prev,
        stockItems: (prev.stockItems || []).filter((_, i) => i !== index)
      }));
    }
  };

  // Clipboard state for copy/paste items
  const [copiedItem, setCopiedItem] = useState(() => {
    try {
      const saved = localStorage.getItem('tiles_do_clipboard_item');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [copiedStockItem, setCopiedStockItem] = useState(() => {
    try {
      const saved = localStorage.getItem('tiles_do_clipboard_stock_item');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const copyItem = (index) => {
    const itemCopy = JSON.parse(JSON.stringify(form.items[index]));
    setCopiedItem(itemCopy);
    localStorage.setItem('tiles_do_clipboard_item', JSON.stringify(itemCopy));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Item copied', showConfirmButton: false, timer: 1500 });
  };

  const pasteItem = () => {
    if (!copiedItem) {
      Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: 'No item copied', showConfirmButton: false, timer: 1500 });
      return;
    }
    setForm(prev => ({
      ...prev,
      items: [...(prev.items || []), JSON.parse(JSON.stringify(copiedItem))]
    }));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Item pasted', showConfirmButton: false, timer: 1500 });
  };

  const copyStockItem = (index) => {
    const itemCopy = JSON.parse(JSON.stringify(form.stockItems[index]));
    setCopiedStockItem(itemCopy);
    localStorage.setItem('tiles_do_clipboard_stock_item', JSON.stringify(itemCopy));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Stock item copied', showConfirmButton: false, timer: 1500 });
  };

  const pasteStockItem = () => {
    if (!copiedStockItem) {
      Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: 'No stock item copied', showConfirmButton: false, timer: 1500 });
      return;
    }
    setForm(prev => ({
      ...prev,
      stockItems: [...(prev.stockItems || []), JSON.parse(JSON.stringify(copiedStockItem))]
    }));
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Stock item pasted', showConfirmButton: false, timer: 1500 });
  };

  const updateStockItem = (index, field, value) => {
    if (!form.stockItems || !Array.isArray(form.stockItems)) return;
    
    calculationInProgress.current = true;
    
    const updatedStockItems = [...form.stockItems];
    const stockItem = updatedStockItems[index];

    // Handle location change - need to reset stock and fetch new stock info
    if (field === 'location') {
      updatedStockItems[index].location = value;
      // Reset stock-related fields when location changes
      updatedStockItems[index].freshQuantity = 0;
      updatedStockItems[index].damagedQuantity = 0;
      updatedStockItems[index].availableStock = {
        freshQuantity: 0,
        damagedQuantity: 0
      };
      updatedStockItems[index].amountBeforeDiscount = 0;
      updatedStockItems[index].amountAfterDiscount = 0;
      
      // If stock item is already selected, fetch stock for new location
      if (stockItem.stockItemId && stockItem.stockItemDetails) {
        const locationStock = stockItem.stockItemDetails.stock?.find(s => 
          s.location?._id === value || s.location === value
        );
        if (locationStock) {
          updatedStockItems[index].availableStock = {
            freshQuantity: locationStock.freshQuantity || 0,
            damagedQuantity: locationStock.damagedQuantity || 0
          };
        }
      }
      
      setForm(prev => ({ ...prev, stockItems: updatedStockItems }));
      calculationInProgress.current = false;
      calculateTotals();
      return;
    }

    // Validate stock limits
    if (['freshQuantity', 'damagedQuantity'].includes(field)) {
      const numValue = parseFloat(value) || 0;
      const stockField = field === 'freshQuantity' ? 'freshQuantity' : 'damagedQuantity';
      
      const availableStock = stockItem.availableStock[stockField] || 0;
      // Include in-transit quantity for fresh stock max calculation
      const maxAllowed = field === 'freshQuantity'
        ? availableStock + (stockItem.stockItemDetails?.inTransitQuantity || 0)
        : availableStock;
      if (numValue > maxAllowed) {
        calculationInProgress.current = false;
        const stockLabel = field === 'freshQuantity' ? 'Fresh Stock' : 'Damaged Stock';
        const stockItemName = stockItem.name || 'Selected Item';
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Stock Available',
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Stock Item:</strong> ${stockItemName}</p>
              <div class="bg-red-50 border border-red-200 rounded p-3 mb-3">
                <p class="text-red-700"><strong>Requested:</strong> ${numValue} units</p>
                <p class="text-green-700"><strong>Available ${stockLabel}:</strong> ${maxAllowed} units${field === 'freshQuantity' && (stockItem.stockItemDetails?.inTransitQuantity || 0) > 0 ? ` (incl. ${stockItem.stockItemDetails.inTransitQuantity} in-transit)` : ''}</p>
                <p class="text-orange-700"><strong>Shortage:</strong> ${numValue - maxAllowed} units</p>
              </div>
              <p class="text-sm"><strong>Note:</strong> You can only dispatch quantities up to the available stock level.</p>
            </div>
          `,
          timer: 6000,
          timerProgressBar: true,
          customClass: { popup: 'swal-dark-mode' }
        });
        return;
      }
      updatedStockItems[index][field] = numValue;
    } else {
      updatedStockItems[index][field] = value;
    }

    // Calculate amounts if rate, discount, or quantities changed
    if (['rate', 'discount', 'freshQuantity', 'damagedQuantity'].includes(field)) {
      const rate = parseFloat(updatedStockItems[index].rate) || 0;
      const discount = parseFloat(updatedStockItems[index].discount) || 0;
      const freshQty = parseFloat(updatedStockItems[index].freshQuantity) || 0;
      const damagedQty = parseFloat(updatedStockItems[index].damagedQuantity) || 0;
      const totalQuantity = freshQty + damagedQty;

      const amountBeforeDiscount = totalQuantity * rate;
      const discountAmount = (amountBeforeDiscount * discount) / 100;
      const amountAfterDiscount = amountBeforeDiscount - discountAmount;

      updatedStockItems[index].amountBeforeDiscount = amountBeforeDiscount;
      updatedStockItems[index].amountAfterDiscount = amountAfterDiscount;
    }

    setForm(prev => ({ ...prev, stockItems: updatedStockItems }));
    
    setTimeout(() => {
      if (calculationInProgress.current) {
        calculationInProgress.current = false;
        calculateTotals();
      }
    }, 100);
  };

  // ===== END STOCK ITEMS FUNCTIONS =====

  const updateItem = (index, field, value) => {
    if (!form.items || !Array.isArray(form.items)) return;
    
    // Prevent calculation loops during user input
    calculationInProgress.current = true;
    
    const updatedItems = [...form.items];
    const item = updatedItems[index];

    // Handle location change - need to reset stock and fetch new stock info
    if (field === 'location') {
      updatedItems[index].location = value;
      // Reset stock-related fields when location changes
      updatedItems[index].freshBoxes = 0;
      updatedItems[index].freshPieces = 0;
      updatedItems[index].damagedBoxes = 0;
      updatedItems[index].damagedPieces = 0;
      updatedItems[index].availableStock = {
        freshStockBox: 0,
        freshStockPieces: 0,
        cornerDamagedBox: 0,
        cornerDamagedPieces: 0
      };
      updatedItems[index].amountBeforeDiscount = 0;
      updatedItems[index].amountAfterDiscount = 0;
      
      // If tile is already selected, fetch stock for new location from the stock array
      if (item.tileId && item.tileDetails && Array.isArray(item.tileDetails.stock)) {
        const targetLocationStr = value?.toString?.() || value;
        const locationStock = item.tileDetails.stock.find(s => {
          // Handle both populated location objects and plain ObjectId strings
          const stockLocationId = s.location?._id?.toString?.() || 
                                 s.location?._id || 
                                 s.location?.toString?.() || 
                                 s.location;
          return stockLocationId?.toString?.() === targetLocationStr;
        });
        if (locationStock) {
          updatedItems[index].availableStock = {
            freshStockBox: locationStock.freshStockBox || 0,
            freshStockPieces: locationStock.freshStockPieces || 0,
            cornerDamagedBox: locationStock.cornerDamagedBox || 0,
            cornerDamagedPieces: locationStock.cornerDamagedPieces || 0
          };
        }
        console.log('📍 Location changed for tile - stock lookup:', {
          tileId: item.tileId,
          newLocation: targetLocationStr,
          stockFound: !!locationStock,
          newAvailableStock: updatedItems[index].availableStock
        });
      }
      
      setForm(prev => ({ ...prev, items: updatedItems }));
      calculationInProgress.current = false;
      calculateTotals();
      return;
    }

    // Validate stock limits
    if (['freshBoxes', 'freshPieces', 'damagedBoxes', 'damagedPieces'].includes(field)) {
      const numValue = parseInt(value) || 0;
      const stockField = field === 'freshBoxes' ? 'freshStockBox' :
                        field === 'freshPieces' ? 'freshStockPieces' :
                        field === 'damagedBoxes' ? 'cornerDamagedBox' :
                        field === 'damagedPieces' ? 'cornerDamagedPieces' : '';
      
      const availableStock = item.availableStock[stockField] || 0;
      // Include in-transit boxes for fresh boxes max calculation
      const maxAllowed = field === 'freshBoxes'
        ? availableStock + (item.tileDetails?.inTransitBoxes || 0)
        : availableStock;
      if (numValue > maxAllowed) {
        calculationInProgress.current = false; // Reset flag on early return
        const tileName = item.tileDetails?.displayName || item.tileName || 'Selected Tile';
        const fieldLabels = {
          freshBoxes: 'Fresh Boxes',
          freshPieces: 'Fresh Pieces',
          damagedBoxes: 'Corner Damaged Boxes',
          damagedPieces: 'Corner Damaged Pieces'
        };
        const fieldLabel = fieldLabels[field] || field;
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Tile Stock',
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Tile:</strong> ${tileName}</p>
              <div class="bg-red-50 border border-red-200 rounded p-3 mb-3">
                <p class="text-red-700"><strong>Requested ${fieldLabel}:</strong> ${numValue}</p>
                <p class="text-green-700"><strong>Available ${fieldLabel}:</strong> ${maxAllowed}${field === 'freshBoxes' && (item.tileDetails?.inTransitBoxes || 0) > 0 ? ` (incl. ${item.tileDetails.inTransitBoxes} in-transit)` : ''}</p>
                <p class="text-orange-700"><strong>Shortage:</strong> ${numValue - maxAllowed}</p>
              </div>
              <p class="text-sm"><strong>Note:</strong> Please reduce the quantity or select a different tile/location with sufficient stock.</p>
            </div>
          `,
          timer: 6000,
          timerProgressBar: true,
          customClass: { popup: 'swal-dark-mode' }
        });
        return;
      }
      updatedItems[index][field] = numValue;
    } else {
      updatedItems[index][field] = value;
    }

    // Recalculate item amounts
    if (['freshBoxes', 'freshPieces', 'damagedBoxes', 'damagedPieces', 'rate', 'discount'].includes(field)) {
      const totalBoxes = (updatedItems[index].freshBoxes || 0) + (updatedItems[index].damagedBoxes || 0);
      const totalPieces = (updatedItems[index].freshPieces || 0) + (updatedItems[index].damagedPieces || 0);
      const rate = parseFloat(updatedItems[index].rate) || 0;
      const discount = parseFloat(updatedItems[index].discount) || 0;

      // Calculate amount based on total quantity and rate
      const totalQuantity = totalBoxes + (totalPieces / (updatedItems[index].tileDetails?.tilesPiecesPerBox || 1));
      const amountBeforeDiscount = totalQuantity * rate;
      const discountAmount = (amountBeforeDiscount * discount) / 100;
      const amountAfterDiscount = amountBeforeDiscount - discountAmount;

      // Ensure calculated values are valid numbers (not NaN)
      const validAmountBeforeDiscount = isNaN(amountBeforeDiscount) ? 0 : amountBeforeDiscount;
      const validAmountAfterDiscount = isNaN(amountAfterDiscount) ? 0 : amountAfterDiscount;

      // Debug logging for NaN detection
      if (isNaN(amountBeforeDiscount) || isNaN(amountAfterDiscount)) {
        console.warn('⚠️ NaN detected in calculation:', {
          totalQuantity,
          rate,
          discount,
          amountBeforeDiscount,
          amountAfterDiscount,
          tilesPiecesPerBox: updatedItems[index].tileDetails?.tilesPiecesPerBox
        });
      }

      // Calculate TT amount - only if reference exists
      let ttCalculatedAmount = 0;
      if (form.reference) {
        const ttValue = parseFloat(updatedItems[index].tt?.value) || 0;
        const ttType = updatedItems[index].tt?.type || 'percentage';

        if (ttType === 'percentage') {
          ttCalculatedAmount = (validAmountAfterDiscount * ttValue) / 100;
        } else if (ttType === 'fixed') {
          ttCalculatedAmount = ttValue;
        }

        // Ensure TT calculated amount is valid
        ttCalculatedAmount = isNaN(ttCalculatedAmount) ? 0 : ttCalculatedAmount;
      }

      updatedItems[index].amountBeforeDiscount = validAmountBeforeDiscount;
      updatedItems[index].amountAfterDiscount = validAmountAfterDiscount;
      if (updatedItems[index].tt) {
        updatedItems[index].tt.calculatedAmount = ttCalculatedAmount;
      }
    }

    // Handle TT field updates specifically - only calculate if reference exists
    if (field === 'tt') {
      let ttCalculatedAmount = 0;
      if (form.reference) {
        const ttValue = parseFloat(value.value) || 0;
        const ttType = value.type || 'percentage';
        const amountAfterDiscount = parseFloat(updatedItems[index].amountAfterDiscount) || 0;

        if (ttType === 'percentage') {
          ttCalculatedAmount = (amountAfterDiscount * ttValue) / 100;
        } else if (ttType === 'fixed') {
          ttCalculatedAmount = ttValue;
        }
      }
      updatedItems[index].tt.calculatedAmount = ttCalculatedAmount;
    }

    setForm(prev => ({ ...prev, items: updatedItems }));
    
    // Reset flag and trigger calculations manually after a short delay to ensure user input is preserved
    setTimeout(() => {
      if (calculationInProgress.current) {
        calculationInProgress.current = false;
        calculateTotals();
      }
    }, 100);
  };

  const calculateTotals = () => {
    calculationInProgress.current = true;
    
    // Calculate items total (amount after discount only)
    const itemsTotal = (form.items || []).reduce((sum, item) => {
      const itemAmount = parseFloat(item.amountAfterDiscount) || 0;
      return sum + itemAmount;
    }, 0);
    
    // Calculate stock items total
    const stockItemsTotal = (form.stockItems || []).reduce((sum, stockItem) => {
      const stockItemAmount = parseFloat(stockItem.amountAfterDiscount) || 0;
      return sum + stockItemAmount;
    }, 0);
    
    // Calculate TT total - saved for reporting purposes only, NOT added to total amount
    // TT is only applicable when reference exists
    const ttTotal = form.reference 
      ? (form.items || []).reduce((sum, item) => sum + (parseFloat(item.tt?.calculatedAmount) || 0), 0)
      : 0;
    
    const packingTotal = parseFloat(form.packingAndForwarding) || 0;
    const cartageTotal = parseFloat(form.cartage) || 0;
    const labourTotal = parseFloat(form.labour) || 0;
    const roundoff = parseFloat(form.roundoff) || 0;
    
    // Calculate GST for charges only (fixed 18% for additional charges)
    const packingGST = packingTotal * 0.18;
    const cartageGST = cartageTotal * 0.18;
    const labourGST = labourTotal * 0.18;
    
    // Subtotal = items + stock items + charges (TT is NOT included - saved separately for reporting)
    const totalWithoutGST = itemsTotal + stockItemsTotal + packingTotal + cartageTotal + labourTotal;
    
    // Total GST for charges only
    const totalGST = packingGST + cartageGST + labourGST;
    
    // Final total = subtotal + charges GST + roundoff (TT is saved separately for reporting)
    const totalWithGST = totalWithoutGST + totalGST + roundoff;

    setForm(prev => ({
      ...prev,
      totalAmountWithoutGST: totalWithoutGST,
      totalAmountWithGST: totalWithGST,
      totalTTAmount: ttTotal, // Saved for reporting purposes
      packingAndForwardingGST: packingGST,
      cartageGST: cartageGST,
      labourGST: labourGST
    }));

    setTimeout(() => {
      calculationInProgress.current = false;
    }, 100);
  };

  // Helper function to distribute total quantity to individual quantity fields
  const distributeQuantityToFields = (item) => {
    const totalIndividualQty = (parseFloat(item.freshBoxes) || 0) + 
                              (parseFloat(item.freshPieces) || 0) + 
                              (parseFloat(item.damagedBoxes) || 0) + 
                              (parseFloat(item.damagedPieces) || 0);
    
    const mainQuantity = parseFloat(item.quantity) || 0;
    
    // If no individual quantities are set but main quantity exists, put it all in freshBoxes
    if (totalIndividualQty === 0 && mainQuantity > 0) {
      console.log(`🔄 TilesDeliveryOrder: Distributing quantity ${mainQuantity} from main quantity to freshBoxes for item: ${item.Name}`);
      return {
        freshBoxes: mainQuantity,
        freshPieces: 0,
        damagedBoxes: 0,
        damagedPieces: 0
      };
    }
    
    // Otherwise, use the individual quantities as-is
    const result = {
      freshBoxes: parseFloat(item.freshBoxes) || 0,
      freshPieces: parseFloat(item.freshPieces) || 0,
      damagedBoxes: parseFloat(item.damagedBoxes) || 0,
      damagedPieces: parseFloat(item.damagedPieces) || 0
    };
    
    if (mainQuantity > 0 || totalIndividualQty > 0) {
      console.log(`📊 TilesDeliveryOrder: Quantities for ${item.Name} - main: ${mainQuantity}, distributed:`, result);
    }
    
    return result;
  };

  const calculateStockImpact = () => {
    if (!form.items || !Array.isArray(form.items)) return [];
    
    // Use a Map to consolidate items with the same tileId and location
    const impactMap = new Map();
    
    form.items.forEach(item => {
      // Skip stock impact calculation for items without tileId or availableStock
      if (!item.tileId || !item.availableStock) return;
      
      // Create a unique key combining tileId and location
      const key = `${item.tileId}-${item.location || 'default'}`;
      
      const current = item.availableStock;
      const newQuantities = {
        freshBoxes: item.freshBoxes || 0,
        freshPieces: item.freshPieces || 0,
        damagedBoxes: item.damagedBoxes || 0,
        damagedPieces: item.damagedPieces || 0
      };
      
      let requested = { ...newQuantities };
      let originalQuantities = null;
      
      // In edit mode, calculate differential and store original for display
      if (isEditing && originalOrderData) {
        const originalItem = originalOrderData.items?.find(orig => 
          (typeof orig.tileId === 'object' ? orig.tileId._id : orig.tileId) === item.tileId
        );
        
        if (originalItem) {
          originalQuantities = {
            freshBoxes: originalItem.freshBoxes || 0,
            freshPieces: originalItem.freshPieces || 0,
            damagedBoxes: originalItem.damagedBoxes || 0,
            damagedPieces: originalItem.damagedPieces || 0
          };
          
          // Net change for stock calculation (rounded to avoid floating point artifacts)
          requested = {
            freshBoxes: parseFloat((newQuantities.freshBoxes - originalQuantities.freshBoxes).toFixed(4)),
            freshPieces: parseFloat((newQuantities.freshPieces - originalQuantities.freshPieces).toFixed(4)),
            damagedBoxes: parseFloat((newQuantities.damagedBoxes - originalQuantities.damagedBoxes).toFixed(4)),
            damagedPieces: parseFloat((newQuantities.damagedPieces - originalQuantities.damagedPieces).toFixed(4))
          };
        } else {
          // New item added during edit - original was 0
          originalQuantities = { freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0 };
        }
      }
      
      // If this tile+location combo already exists, aggregate the quantities
      if (impactMap.has(key)) {
        const existing = impactMap.get(key);
        existing.requested.freshBoxes += requested.freshBoxes;
        existing.requested.freshPieces += requested.freshPieces;
        existing.requested.damagedBoxes += requested.damagedBoxes;
        existing.requested.damagedPieces += requested.damagedPieces;
        if (isEditing) {
          existing.newQuantities.freshBoxes += newQuantities.freshBoxes;
          existing.newQuantities.freshPieces += newQuantities.freshPieces;
          existing.newQuantities.damagedBoxes += newQuantities.damagedBoxes;
          existing.newQuantities.damagedPieces += newQuantities.damagedPieces;
          if (originalQuantities) {
            existing.originalQuantities.freshBoxes += originalQuantities.freshBoxes;
            existing.originalQuantities.freshPieces += originalQuantities.freshPieces;
            existing.originalQuantities.damagedBoxes += originalQuantities.damagedBoxes;
            existing.originalQuantities.damagedPieces += originalQuantities.damagedPieces;
          }
        }
        existing.remaining = {
          freshStockBox: parseFloat((existing.current.freshStockBox - existing.requested.freshBoxes).toFixed(4)),
          freshStockPieces: parseFloat((existing.current.freshStockPieces - existing.requested.freshPieces).toFixed(4)),
          cornerDamagedBox: parseFloat((existing.current.cornerDamagedBox - existing.requested.damagedBoxes).toFixed(4)),
          cornerDamagedPieces: parseFloat((existing.current.cornerDamagedPieces - existing.requested.damagedPieces).toFixed(4))
        };
      } else {
        const remaining = {
          freshStockBox: parseFloat((current.freshStockBox - requested.freshBoxes).toFixed(4)),
          freshStockPieces: parseFloat((current.freshStockPieces - requested.freshPieces).toFixed(4)),
          cornerDamagedBox: parseFloat((current.cornerDamagedBox - requested.damagedBoxes).toFixed(4)),
          cornerDamagedPieces: parseFloat((current.cornerDamagedPieces - requested.damagedPieces).toFixed(4))
        };

        impactMap.set(key, {
          tileName: item.Name,
          locationId: item.location || 'default',
          current,
          requested,
          remaining,
          ...(isEditing && {
            originalQuantities: originalQuantities || { freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0 },
            newQuantities: { ...newQuantities }
          })
        });
      }
    });

    // Also process stock items
    if (form.stockItems && Array.isArray(form.stockItems)) {
      form.stockItems.forEach(stockItem => {
        // Skip stock items without stockItemId or availableStock
        if (!stockItem.stockItemId || !stockItem.availableStock) return;
        
        // Create a unique key for stock items
        const key = `stock-${stockItem.stockItemId}-${stockItem.location || 'default'}`;
        
        const current = stockItem.availableStock;
        // Stock items availableStock uses freshQuantity/damagedQuantity
        const currentFresh = current.freshQuantity ?? current.freshStock ?? current.freshStockBox ?? 0;
        const currentDamaged = current.damagedQuantity ?? current.damagedStock ?? current.cornerDamagedBox ?? 0;
        
        const newQuantities = {
          freshBoxes: parseFloat(stockItem.freshQuantity) || 0,
          freshPieces: 0,
          damagedBoxes: parseFloat(stockItem.damagedQuantity) || 0,
          damagedPieces: 0
        };
        
        let requested = { ...newQuantities };
        let originalQuantities = null;
        
        // In edit mode, calculate differential for stock items too
        if (isEditing && originalOrderData) {
          const originalSI = originalOrderData.stockItems?.find(orig => 
            (typeof orig.stockItemId === 'object' ? orig.stockItemId._id : orig.stockItemId) === stockItem.stockItemId
          );
          
          if (originalSI) {
            originalQuantities = {
              freshBoxes: parseFloat(originalSI.freshQuantity) || 0,
              freshPieces: 0,
              damagedBoxes: parseFloat(originalSI.damagedQuantity) || 0,
              damagedPieces: 0
            };
            
            requested = {
              freshBoxes: parseFloat((newQuantities.freshBoxes - originalQuantities.freshBoxes).toFixed(4)),
              freshPieces: 0,
              damagedBoxes: parseFloat((newQuantities.damagedBoxes - originalQuantities.damagedBoxes).toFixed(4)),
              damagedPieces: 0
            };
          } else {
            originalQuantities = { freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0 };
          }
        }
        
        // Calculate remaining stock
        const remaining = {
          freshStockBox: parseFloat((currentFresh - requested.freshBoxes).toFixed(4)),
          freshStockPieces: 0,
          cornerDamagedBox: parseFloat((currentDamaged - requested.damagedBoxes).toFixed(4)),
          cornerDamagedPieces: 0
        };

        // If this stock item already exists, aggregate
        if (impactMap.has(key)) {
          const existing = impactMap.get(key);
          existing.requested.freshBoxes += requested.freshBoxes;
          existing.requested.damagedBoxes += requested.damagedBoxes;
          if (isEditing) {
            existing.newQuantities.freshBoxes += newQuantities.freshBoxes;
            existing.newQuantities.damagedBoxes += newQuantities.damagedBoxes;
            if (originalQuantities) {
              existing.originalQuantities.freshBoxes += originalQuantities.freshBoxes;
              existing.originalQuantities.damagedBoxes += originalQuantities.damagedBoxes;
            }
          }
          existing.remaining = {
            freshStockBox: parseFloat((currentFresh - existing.requested.freshBoxes).toFixed(4)),
            freshStockPieces: 0,
            cornerDamagedBox: parseFloat((currentDamaged - existing.requested.damagedBoxes).toFixed(4)),
            cornerDamagedPieces: 0
          };
        } else {
          impactMap.set(key, {
            tileName: `📦 ${stockItem.name || 'Stock Item'}`,
            locationId: stockItem.location || 'default',
            current: {
              freshStockBox: currentFresh,
              freshStockPieces: 0,
              cornerDamagedBox: currentDamaged,
              cornerDamagedPieces: 0
            },
            requested,
            remaining,
            isStockItem: true,
            ...(isEditing && {
              originalQuantities: originalQuantities || { freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0 },
              newQuantities: { ...newQuantities }
            })
          });
        }
      });
    }

    return Array.from(impactMap.values());
  };

  // Get validation reasons for why form cannot be submitted
  const getValidationReasons = () => {
    const reasons = [];
    
    // Check recipient
    if (!form.recipient?.value) {
      reasons.push('Recipient is required');
    }
    
    // Note: Location is now selected per-item, not at form level
    
    // Check if at least one item exists
    const hasTiles = form.items && form.items.length > 0 && form.items.some(item => item.tileId || item.Name);
    const hasStockItems = form.stockItems && form.stockItems.length > 0 && form.stockItems.some(item => item.stockItemId || item.name);
    
    if (!hasTiles && !hasStockItems) {
      reasons.push('At least one tile or stock item is required');
    }
    
    // Validate tiles items - only validate items that have been selected (have tileId or Name)
    if (form.items && form.items.length > 0) {
      form.items.forEach((item, i) => {
        // Only validate if a tile has been selected for this row
        if (item.tileId || item.Name) {
          if (!item.location) {
            reasons.push(`Tile item ${i + 1}: Location not selected`);
          }
          const totalQuantity = (item.freshBoxes || 0) + (item.freshPieces || 0) + (item.damagedBoxes || 0) + (item.damagedPieces || 0);
          const hasQuantity = totalQuantity > 0 || (isEditing && (item.quantity || 0) > 0);
          if (!hasQuantity) {
            reasons.push(`Tile item ${i + 1}: Quantity is required`);
          }
          if (isNaN(item.amountAfterDiscount) || isNaN(item.rate)) {
            reasons.push(`Tile item ${i + 1}: Invalid rate or calculation`);
          }
        }
        // NOTE: We don't show "No tile selected" for empty rows anymore.
        // The "At least one tile or stock item is required" message covers the case
        // when no items are selected at all.
      });
    }
    
    // Validate stock items - only validate items that have been selected
    if (form.stockItems && form.stockItems.length > 0) {
      form.stockItems.forEach((stockItem, i) => {
        // Only validate if a stock item has been selected for this row
        if (stockItem.stockItemId || stockItem.name) {
          if (!stockItem.location) {
            reasons.push(`Stock item ${i + 1}: Location not selected`);
          }
          const totalQuantity = (stockItem.freshQuantity || 0) + (stockItem.damagedQuantity || 0);
          if (totalQuantity === 0) {
            reasons.push(`Stock item ${i + 1}: Quantity is required`);
          }
          if (isNaN(stockItem.amountAfterDiscount) || isNaN(stockItem.rate)) {
            reasons.push(`Stock item ${i + 1}: Invalid rate or calculation`);
          }
        }
      });
    }
    
    return reasons;
  };

  const handleSubmit = async () => {
    try {
      // Validate form - recipient is required, location is now per-item
      if (!form.recipient?.value) {
        Swal.fire({
          icon: 'error',
          title: 'Recipient Required',
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>Please select a recipient for this delivery order.</strong></p>
              <p class="text-sm mb-2">The recipient can be:</p>
              <ul class="list-disc list-inside text-sm mb-3">
                <li>An existing customer from your database</li>
                <li>A registered dealer</li>
                <li>A new recipient (can be added in Customers section)</li>
              </ul>
              <p class="text-sm"><strong>Tip:</strong> Use the search box to find recipients by name or contact number.</p>
            </div>
          `,
          timer: 8000,
          timerProgressBar: true,
          customClass: { popup: 'swal-dark-mode' }
        });
        return;
      }

      // Validate that at least one item type exists (tiles OR stock items)
      const hasTiles = form.items && form.items.length > 0 && form.items.some(item => item.tileId || item.Name);
      const hasStockItems = form.stockItems && form.stockItems.length > 0 && form.stockItems.some(item => item.stockItemId || item.name);
      
      if (!hasTiles && !hasStockItems) {
        Swal.fire({
          icon: 'error',
          title: 'No Items Added',
          html: `
            <div class="text-left">
              <p class="mb-2"><strong>A delivery order must contain at least one item.</strong></p>
              <p class="text-sm mb-2">You can add:</p>
              <ul class="list-disc list-inside text-sm mb-3">
                <li><strong>Tiles:</strong> Search and add tiles with box/piece quantities</li>
                <li><strong>Stock Items:</strong> Add other inventory items like adhesives, grout, etc.</li>
              </ul>
              <p class="text-sm"><strong>Steps:</strong></p>
              <ol class="list-decimal list-inside text-sm">
                <li>Select a location for the item</li>
                <li>Search for the tile/stock item</li>
                <li>Enter the required quantities</li>
                <li>Set the rate and discount (if applicable)</li>
              </ol>
            </div>
          `,
          timer: 10000,
          timerProgressBar: true,
          customClass: { popup: 'swal-dark-mode' }
        });
        return;
      }

      // Validate tiles items
      if (hasTiles) {
        for (let i = 0; i < form.items.length; i++) {
          const item = form.items[i];
          // Validate location is selected
          if (!item.location) {
            Swal.fire({
              icon: 'error',
              title: 'Location Required for Tile',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Tile Item #${i + 1}</strong> requires a location.</p>
                  <p class="text-sm mb-3">Each tile item must have a warehouse/showroom location selected to ensure accurate stock tracking.</p>
                  <p class="text-sm"><strong>Action:</strong> Click the location dropdown for this tile item and select the appropriate warehouse or showroom.</p>
                </div>
              `,
              timer: 6000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
          // In edit mode, if tileId is missing but we have item Name, allow it (existing item)
          if (!item.tileId && !(isEditing && item.Name)) {
            Swal.fire({
              icon: 'error',
              title: 'Tile Selection Required',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Item #${i + 1}</strong> does not have a tile selected.</p>
                  <p class="text-sm mb-3">You must select a tile from the available inventory before proceeding.</p>
                  <p class="text-sm"><strong>Steps:</strong></p>
                  <ol class="list-decimal list-inside text-sm">
                    <li>First select a location for the item</li>
                    <li>Use the search bar to find the tile</li>
                    <li>Click on the tile to add it</li>
                  </ol>
                </div>
              `,
              timer: 6000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
          const totalQuantity = (item.freshBoxes || 0) + (item.freshPieces || 0) + (item.damagedBoxes || 0) + (item.damagedPieces || 0);
          // In edit mode, also check the general quantity field if detailed quantities are 0
          const hasQuantity = totalQuantity > 0 || (isEditing && (item.quantity || 0) > 0);
          if (!hasQuantity) {
            const tileName = item.tileDetails?.displayName || item.tileName || item.Name || 'Unknown Tile';
            Swal.fire({
              icon: 'error',
              title: 'Quantity Required',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Item #${i + 1}:</strong> ${tileName}</p>
                  <p class="text-sm mb-3">Please enter at least one quantity value:</p>
                  <ul class="list-disc list-inside text-sm mb-3">
                    <li><strong>Fresh Boxes:</strong> Number of undamaged tile boxes</li>
                    <li><strong>Fresh Pieces:</strong> Loose undamaged tiles</li>
                    <li><strong>Damaged Boxes:</strong> Boxes with minor defects</li>
                    <li><strong>Damaged Pieces:</strong> Loose tiles with minor defects</li>
                  </ul>
                  <p class="text-sm"><strong>Note:</strong> At least one field must have a value greater than 0.</p>
                </div>
              `,
              timer: 8000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
          
          // Validate numeric fields are not NaN
          if (isNaN(item.amountAfterDiscount) || isNaN(item.rate)) {
            const tileName = item.tileDetails?.displayName || item.tileName || item.Name || 'Unknown Tile';
            Swal.fire({
              icon: 'error',
              title: 'Calculation Error Detected',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Item #${i + 1}:</strong> ${tileName}</p>
                  <p class="text-sm mb-3">The rate or calculated amount contains invalid values (NaN).</p>
                  <p class="text-sm mb-2"><strong>Please verify:</strong></p>
                  <ul class="list-disc list-inside text-sm mb-3">
                    <li>Rate is a valid number (not empty or negative)</li>
                    <li>Discount percentage is between 0-100</li>
                    <li>All quantity fields contain valid numbers</li>
                  </ul>
                  <p class="text-sm"><strong>Action:</strong> Clear the rate field and re-enter a valid number.</p>
                </div>
              `,
              timer: 8000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
        }
      }

      // Validate stock items
      if (hasStockItems) {
        for (let i = 0; i < form.stockItems.length; i++) {
          const stockItem = form.stockItems[i];
          // Validate location is selected
          if (!stockItem.location) {
            Swal.fire({
              icon: 'error',
              title: 'Location Required for Stock Item',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Stock Item #${i + 1}</strong> requires a location.</p>
                  <p class="text-sm mb-3">Each stock item must have a warehouse/showroom location selected for proper inventory tracking.</p>
                  <p class="text-sm"><strong>Action:</strong> Select the appropriate location from the dropdown for this stock item.</p>
                </div>
              `,
              timer: 6000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
          if (!stockItem.stockItemId && !stockItem.name) {
            Swal.fire({
              icon: 'error',
              title: 'Stock Item Selection Required',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Stock Item #${i + 1}</strong> does not have an item selected.</p>
                  <p class="text-sm mb-3">You must select a stock item (adhesive, grout, tools, etc.) from the inventory.</p>
                  <p class="text-sm"><strong>Steps:</strong></p>
                  <ol class="list-decimal list-inside text-sm">
                    <li>First select a location</li>
                    <li>Search for the stock item by name</li>
                    <li>Click to select the item</li>
                  </ol>
                </div>
              `,
              timer: 6000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
          const totalQuantity = (stockItem.freshQuantity || 0) + (stockItem.damagedQuantity || 0);
          if (totalQuantity === 0) {
            const itemName = stockItem.name || 'Unknown Item';
            Swal.fire({
              icon: 'error',
              title: 'Quantity Required for Stock Item',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Stock Item #${i + 1}:</strong> ${itemName}</p>
                  <p class="text-sm mb-3">Please enter at least one quantity:</p>
                  <ul class="list-disc list-inside text-sm mb-3">
                    <li><strong>Fresh Quantity:</strong> Items in good condition</li>
                    <li><strong>Damaged Quantity:</strong> Items with minor defects (if applicable)</li>
                  </ul>
                  <p class="text-sm"><strong>Note:</strong> Total quantity must be greater than 0 to proceed.</p>
                </div>
              `,
              timer: 6000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
          
          // Validate numeric fields are not NaN
          if (isNaN(stockItem.amountAfterDiscount) || isNaN(stockItem.rate)) {
            const itemName = stockItem.name || 'Unknown Item';
            Swal.fire({
              icon: 'error',
              title: 'Calculation Error in Stock Item',
              html: `
                <div class="text-left">
                  <p class="mb-2"><strong>Stock Item #${i + 1}:</strong> ${itemName}</p>
                  <p class="text-sm mb-3">The rate or calculated amount contains invalid values.</p>
                  <p class="text-sm mb-2"><strong>Please verify:</strong></p>
                  <ul class="list-disc list-inside text-sm mb-3">
                    <li>Rate is a valid positive number</li>
                    <li>Discount is between 0-100%</li>
                    <li>Quantities are valid numbers</li>
                  </ul>
                  <p class="text-sm"><strong>Action:</strong> Clear and re-enter the rate value.</p>
                </div>
              `,
              timer: 8000,
              timerProgressBar: true,
              customClass: { popup: 'swal-dark-mode' }
            });
            return;
          }
        }
      }

      // Calculate and show stock impact
      const impact = calculateStockImpact();
      setStockImpact(impact);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error('❌ Validation error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Form Validation Failed',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>An error occurred during form validation.</strong></p>
            <p class="text-sm mb-3">Please review the form and ensure all required fields are filled correctly.</p>
            <p class="text-sm mb-2"><strong>Common issues:</strong></p>
            <ul class="list-disc list-inside text-sm mb-3">
              <li>Missing recipient selection</li>
              <li>Items without location selected</li>
              <li>Zero quantities in items</li>
              <li>Invalid rate or discount values</li>
            </ul>
            <p class="text-xs text-gray-500">Error: ${error.message || 'Unknown validation error'}</p>
          </div>
        `,
        timer: 8000,
        timerProgressBar: true,
        customClass: { popup: 'swal-dark-mode' }
      });
    }
  };

  const confirmSubmit = async () => {
    try {
      setSubmitting(true);
      setShowConfirmationModal(false);

      // Validate and sanitize form data before submission
      const sanitizedItems = (form.items || []).map(item => {
        // Calculate total quantity from boxes and pieces
        const totalBoxes = (parseFloat(item.freshBoxes) || 0) + (parseFloat(item.damagedBoxes) || 0);
        const totalPieces = (parseFloat(item.freshPieces) || 0) + (parseFloat(item.damagedPieces) || 0);
        const piecesPerBox = item.tileDetails?.tilesPiecesPerBox || 1;
        const totalQuantity = totalBoxes + (totalPieces / piecesPerBox);

        // Calculate amounts
        const rate = parseFloat(item.rate) || 0;
        const discount = parseFloat(item.discount) || 0;
        const amountBeforeDiscount = totalQuantity * rate;
        const discountAmount = (amountBeforeDiscount * discount) / 100;
        const amountAfterDiscount = amountBeforeDiscount - discountAmount;

        // Calculate available stock as total
        const availableStockTotal = (item.availableStock?.freshStockBox || 0) + 
                                  (item.availableStock?.cornerDamagedBox || 0) + 
                                  ((item.availableStock?.freshStockPieces || 0) / piecesPerBox) + 
                                  ((item.availableStock?.cornerDamagedPieces || 0) / piecesPerBox);

        // Get item location - use item-specific location or fallback to form's default location
        const itemLocation = item.location || form.location;

        return {
          tileId: item.tileId,
          Name: item.Name || '',
          location: itemLocation, // Include per-item location for multi-location support
          quantity: isNaN(totalQuantity) ? 1 : Math.max(totalQuantity, 1), // Backend requires minimum 1
          rate: isNaN(rate) ? 0 : rate,
          units: 'box', // Default unit
          discount: isNaN(discount) ? 0 : discount,
          amountBeforeDiscount: isNaN(amountBeforeDiscount) ? 0 : amountBeforeDiscount,
          amountAfterDiscount: isNaN(amountAfterDiscount) ? 0 : amountAfterDiscount,
          availableStock: isNaN(availableStockTotal) ? 0 : availableStockTotal,
          // Preserve individual quantities for TilesOutward creation (with smart distribution)
          ...distributeQuantityToFields(item),
          // Preserve TT information
          tt: item.tt || { type: 'percentage', value: 0, calculatedAmount: 0 }
        };
      });

      // Prepare base submit data
      const baseSubmitData = {
        items: sanitizedItems,
        stockItems: (form.stockItems || [])
          .filter(item => item.stockItemId && (item.freshQuantity > 0 || item.damagedQuantity > 0))
          .map(item => ({
            stockItemId: item.stockItemId,
            name: item.name || '',
            location: item.location || form.location, // Include per-item location for multi-location support
            freshQuantity: parseFloat(item.freshQuantity) || 0,
            damagedQuantity: parseFloat(item.damagedQuantity) || 0,
            rate: parseFloat(item.rate) || 0,
            discount: parseFloat(item.discount) || 0,
            amountBeforeDiscount: parseFloat(item.amountBeforeDiscount) || 0,
            amountAfterDiscount: parseFloat(item.amountAfterDiscount) || 0,
            availableStock: {
              freshQuantity: item.availableStock?.freshQuantity || 0,
              damagedQuantity: item.availableStock?.damagedQuantity || 0
            }
          })),
        vehicleNumber: form.vehicleNumber || '',
        transporterName: form.transporterName?.value || form.transporterName,
        transportMedium: form.transportMedium || '',
        siteAddress: form.siteAddress || '',
        modeOfPayment: Array.isArray(form.modeOfPayment) ? form.modeOfPayment : [],
        modeOfSales: form.modeOfSales || 'Paid Instantly',
        creditReference: form.creditReference || '',
        creditPeriod: parseFloat(form.creditPeriod) || 0,
        salesman: Array.isArray(form.salesman) ? form.salesman.map(s => s.value || s) : [],
        interior: form.interior?.value || form.interior,
        roundoff: isNaN(parseFloat(form.roundoff)) ? 0 : parseFloat(form.roundoff),
        Narration: form.Narration || '',
        packingAndForwarding: isNaN(parseFloat(form.packingAndForwarding)) ? 0 : parseFloat(form.packingAndForwarding),
        packingAndForwardingGST: isNaN(parseFloat(form.packingAndForwardingGST)) ? 0 : parseFloat(form.packingAndForwardingGST),
        Manual_dO_Number: form.Manual_dO_Number || '',
        poNumber: form.poNumber || '',
        itemDescription: form.itemDescription || '',
        referenceName: form.referenceName || '',
        cartage: isNaN(parseFloat(form.cartage)) ? 0 : parseFloat(form.cartage),
        cartageGST: isNaN(parseFloat(form.cartageGST)) ? 0 : parseFloat(form.cartageGST),
        labour: isNaN(parseFloat(form.labour)) ? 0 : parseFloat(form.labour),
        labourGST: isNaN(parseFloat(form.labourGST)) ? 0 : parseFloat(form.labourGST),
        totalAmountWithoutGST: isNaN(parseFloat(form.totalAmountWithoutGST)) ? 0 : parseFloat(form.totalAmountWithoutGST),
        totalAmountWithGST: isNaN(parseFloat(form.totalAmountWithGST)) ? 1 : Math.max(parseFloat(form.totalAmountWithGST), 1), // Required field, minimum 1
        totalTTAmount: isNaN(parseFloat(form.totalTTAmount)) ? 0 : parseFloat(form.totalTTAmount),
        reference: form.reference || '',
        claimPaymentFrom: {
          name: form.claimPaymentFrom?.name || '',
          number: form.claimPaymentFrom?.number || ''
        },
        isReference: Boolean(form.isReference),
        isNarration: Boolean(form.isNarration),
        createdBy: user?.user?._id,
        salesOrder: form.salesOrder || null
      };

      // Determine the default location to send to backend
      // If form.location is not set, use the first item's location as default
      const getDefaultLocation = () => {
        if (form.location) {
          return form.location?.value || form.location;
        }
        // Find first item with a location
        const firstTileWithLocation = (form.items || []).find(item => item.location);
        if (firstTileWithLocation) {
          return firstTileWithLocation.location?.value || firstTileWithLocation.location;
        }
        // Check stock items
        const firstStockItemWithLocation = (form.stockItems || []).find(item => item.location);
        if (firstStockItemWithLocation) {
          return firstStockItemWithLocation.location?.value || firstStockItemWithLocation.location;
        }
        return null;
      };
      
      const defaultLocation = getDefaultLocation();

      // For edits, exclude only recipient fields (location is now editable)
      const submitData = isEditing ? {
        location: defaultLocation, // Include location for edits
        ...baseSubmitData
      } : {
        recipientType: form.recipientType,
        recipient: form.recipient?.value, // Extract the recipient ID from the object
        location: defaultLocation,
        ...baseSubmitData
      };

      console.log('📤 Submitting sanitized data:', submitData);
      console.log('🔍 Edit Mode Check - Fields being submitted:', {
        isEditing,
        hasReference: 'reference' in submitData,
        hasRecipient: 'recipient' in submitData,
        hasLocation: 'location' in submitData,
        hasTotalTTAmount: 'totalTTAmount' in submitData,
        hasClaimPaymentFrom: 'claimPaymentFrom' in submitData,
        hasStockItems: 'stockItems' in submitData,
        stockItemsCount: submitData.stockItems?.length || 0
      });
      console.log('🔍 Sanitized items with quantities and TT:', 
        sanitizedItems.map(item => ({
          Name: item.Name,
          freshBoxes: item.freshBoxes,
          freshPieces: item.freshPieces,
          damagedBoxes: item.damagedBoxes,
          damagedPieces: item.damagedPieces,
          totalQuantity: item.quantity,
          ttValue: item.tt?.value,
          ttCalculated: item.tt?.calculatedAmount
        }))
      );
      console.log('🔍 Sanitized stock items:', 
        submitData.stockItems?.map(item => ({
          name: item.name,
          freshQuantity: item.freshQuantity,
          damagedQuantity: item.damagedQuantity,
          rate: item.rate,
          discount: item.discount,
          amountAfterDiscount: item.amountAfterDiscount
        }))
      );
      console.log('🔍 Original form data for comparison:', {
        recipient: form.recipient,
        location: form.location,
        items: form.items?.map(item => ({
          tileId: item.tileId,
          freshBoxes: item.freshBoxes,
          freshPieces: item.freshPieces,
          amountAfterDiscount: item.amountAfterDiscount,
          availableStock: item.availableStock
        })),
        stockItems: form.stockItems?.map(item => ({
          stockItemId: item.stockItemId,
          name: item.name,
          freshQuantity: item.freshQuantity,
          damagedQuantity: item.damagedQuantity
        }))
      });

      let response;
      
      if (isEditing && orderId) {
        // Update existing order - show loading toast
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'info',
          title: 'Updating Delivery Order...',
          html: `<div class="text-sm">Order: <strong>${orderId}</strong><br/>Syncing stock & outward entries...</div>`,
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#f3f4f6' : '#1f2937'
        });
        
        response = await api.put(`/tiles/delivery-orders/${orderId}`, submitData);
        console.log('✅ Tiles Delivery Order updated:', response.data);
        console.log('✅ Backend handled stock reversal and reapplication automatically');
        
        // Backend automatically handles outward entry updates when delivery order is edited
        console.log('✅ Associated TilesOutward entry updated automatically by backend');
      } else {
        // Create new order - show loading toast
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'info',
          title: 'Creating Delivery Order...',
          html: '<div class="text-sm">Validating items & checking stock levels...</div>',
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#f3f4f6' : '#1f2937'
        });
        
        response = await api.post('/tiles/delivery-orders', submitData);
        console.log('✅ Tiles Delivery Order created:', response.data);
      }

      // Create corresponding outward entries (only for new orders, not updates)
      // Use the dedicated backend endpoint that handles multi-location properly
      const hasTilesItems = response.data.deliveryOrder?.items && response.data.deliveryOrder.items.length > 0;
      const hasStockItems = response.data.deliveryOrder?.stockItems && response.data.deliveryOrder.stockItems.length > 0;
      const hasAnyItems = hasTilesItems || hasStockItems;
      
      console.log('📦 Checking for outward creation:', { isEditing, hasDeliveryOrder: !!response.data.deliveryOrder, orderId: response.data.deliveryOrder?._id, hasTilesItems, hasStockItems, hasAnyItems });
      
      if (!isEditing && response.data.deliveryOrder && response.data.deliveryOrder._id && hasAnyItems) {
        try {
          // Use the dedicated endpoint that handles multi-location grouping
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'info',
            title: 'Creating Outward Entries...',
            html: `<div class="text-sm">DO: <strong>${response.data.deliveryOrder.doNumber || response.data.deliveryOrder._id}</strong><br/>Processing stock deductions...</div>`,
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
            background: darkMode ? '#1f2937' : '#ffffff',
            color: darkMode ? '#f3f4f6' : '#1f2937'
          });
          console.log('📤 Creating outward entries via dedicated endpoint for delivery order:', response.data.deliveryOrder._id);
          
          const outwardResponse = await api.post(`/tiles/delivery-orders/${response.data.deliveryOrder._id}/create-outward`);
          
          console.log('✅ Outward entries created:', outwardResponse.data);
          
          // Store the IDs in form state for reference (before reset)
          setForm(prev => ({
            ...prev,
            deliveryOrderId: response.data.deliveryOrder._id,
            tilesOutwardId: outwardResponse.data?.tilesOutward?.[0]?.outwardId || null
          }));

          // Build success message based on what was created
          const tilesOutwardInfo = outwardResponse.data?.tilesOutward?.length > 0 
            ? outwardResponse.data.tilesOutward.map(o => o.outwardId || 'Generated').join(', ')
            : null;
          const stockOutwardInfo = outwardResponse.data?.stockItemsOutward?.length > 0
            ? outwardResponse.data.stockItemsOutward.map(o => o.outwardId || 'Generated').join(', ')
            : null;
          const isMultiLocation = outwardResponse.data?.multiLocation;

          Swal.fire({
            icon: 'success',
            title: 'Delivery Order Created Successfully!',
            html: `
              <div class="text-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}">
                <div class="mb-4 p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-50'}">
                  <p class="text-lg font-bold">DO Number: ${response.data.deliveryOrder?.doNumber || 'Generated'}</p>
                  <p class="text-sm">Order ID: ${response.data.deliveryOrder?._id || 'Generated'}</p>
                </div>
                ${tilesOutwardInfo ? `
                <div class="mb-3 p-2 rounded ${darkMode ? 'bg-blue-900' : 'bg-blue-50'}">
                  <p class="font-semibold">📦 Tiles Outward${isMultiLocation ? ' (Multi-Location)' : ''}</p>
                  <p class="text-sm">${tilesOutwardInfo}</p>
                </div>
                ` : ''}
                ${stockOutwardInfo ? `
                <div class="mb-3 p-2 rounded ${darkMode ? 'bg-purple-900' : 'bg-purple-50'}">
                  <p class="font-semibold">📋 Stock Items Outward</p>
                  <p class="text-sm">${stockOutwardInfo}</p>
                </div>
                ` : ''}
                <div class="text-sm mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}">
                  ✅ Stock deducted automatically<br/>
                  ✅ All entries linked for tracking
                </div>
              </div>
            `,
            timer: 8000,
            timerProgressBar: true,
            showConfirmButton: true,
            confirmButtonText: 'Great!',
            background: darkMode ? '#1f2937' : '#ffffff',
            color: darkMode ? '#f3f4f6' : '#1f2937'
          });

        } catch (outwardError) {
          console.error('❌ Error creating Tiles Outward:', outwardError);
          
          // Still show success for delivery order, but warn about outward
          Swal.fire({
            icon: 'warning',
            title: 'Delivery Order Created - Action Required',
            html: `
              <div class="text-left ${darkMode ? 'text-gray-200' : 'text-gray-800'}">
                <div class="mb-4 p-3 rounded-lg bg-green-100">
                  <p class="text-green-700 font-bold">✅ Delivery Order ${isEditing ? 'Updated' : 'Created'} Successfully</p>
                  <p class="text-green-600 text-sm">DO: ${response.data.deliveryOrder?.doNumber || response.data.deliveryOrder?._id || 'Generated'}</p>
                </div>
                ${!isEditing ? `
                <div class="mb-4 p-3 rounded-lg bg-orange-100">
                  <p class="text-orange-700 font-bold">⚠️ Outward Entry Creation Failed</p>
                  <p class="text-orange-600 text-sm">${outwardError.response?.data?.message || 'Could not create outward entries automatically'}</p>
                </div>
                <div class="bg-yellow-50 p-3 rounded-lg">
                  <p class="text-yellow-800 font-semibold mb-2">Manual Action Required:</p>
                  <ol class="text-yellow-700 text-sm list-decimal list-inside">
                    <li>Go to Tiles Outward section</li>
                    <li>Create a new outward entry</li>
                    <li>Link it to DO: ${response.data.deliveryOrder?.doNumber || 'N/A'}</li>
                  </ol>
                </div>
                ` : ''}
              </div>
            `,
            timer: 12000,
            timerProgressBar: true,
            showConfirmButton: true,
            confirmButtonText: 'I Understand',
            background: darkMode ? '#1f2937' : '#ffffff',
            color: darkMode ? '#f3f4f6' : '#1f2937'
          });
        }
      } else if (isEditing) {
        // Update mode - success notification with stock and outward update details
        Swal.fire({
          icon: 'success',
          title: 'Delivery Order Updated Successfully!',
          html: `
            <div class="text-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}">
              <div class="mb-4 p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-50'}">
                <p class="text-lg font-bold">DO Number: ${response.data.deliveryOrder?.doNumber || 'N/A'}</p>
                <p class="text-sm">Order ID: ${response.data.deliveryOrder?._id || orderId}</p>
              </div>
              <div class="text-left p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}">
                <p class="font-semibold mb-2">Changes Applied:</p>
                <ul class="text-sm space-y-1">
                  <li>✅ Stock quantities recalculated</li>
                  <li>✅ Previous outward entries reversed</li>
                  <li>✅ New outward entries created</li>
                  <li>✅ Inventory synchronized</li>
                </ul>
              </div>
            </div>
          `,
          timer: 8000,
          timerProgressBar: true,
          showConfirmButton: true,
          confirmButtonText: 'Great!',
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#f3f4f6' : '#1f2937'
        });
      } else {
        // Fallback if delivery order creation/update response is incomplete
        Swal.fire({
          icon: 'success',
          title: `Delivery Order ${isEditing ? 'Updated' : 'Created'}!`,
          html: `
            <div class="text-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}">
              <p class="mb-2">Your delivery order has been ${isEditing ? 'updated' : 'created'} successfully.</p>
              <p class="text-lg font-bold">Order ID: ${response.data.deliveryOrder?.doNumber || response.data.deliveryOrder?._id || 'Generated'}</p>
              <p class="text-sm mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}">The order list will refresh automatically.</p>
            </div>
          `,
          timer: 6000,
          timerProgressBar: true,
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#f3f4f6' : '#1f2937'
        });
      }

      // Reset form and exit edit mode
      if (isEditing) {
        setIsEditing(false);
        setOrderId(null);
        setOriginalOrderData(null);
      }
      setForm(initializeFormState(user?.user?._id));
      fetchOrders();
    } catch (error) {
      console.error(`❌ Error ${isEditing ? 'updating' : 'creating'} order:`, error);
      Swal.fire({
        icon: 'error',
        title: `Failed to ${isEditing ? 'Update' : 'Create'} Delivery Order`,
        html: `
          <div class="text-left ${darkMode ? 'text-gray-200' : 'text-gray-800'}">
            <div class="mb-3 p-3 rounded-lg bg-red-50">
              <p class="text-red-700 font-semibold">Error Details:</p>
              <p class="text-red-600">${error.response?.data?.message || error.message || 'Unknown error occurred'}</p>
            </div>
            ${error.response?.status ? `
            <div class="mb-3">
              <p class="text-sm"><strong>Status Code:</strong> ${error.response.status}</p>
              <p class="text-xs text-gray-500">${error.response.status === 400 ? 'Bad Request - Check form data' : error.response.status === 401 ? 'Unauthorized - Please login again' : error.response.status === 500 ? 'Server Error - Contact support' : 'Check your data and try again'}</p>
            </div>
            ` : ''}
            <div class="bg-yellow-50 p-3 rounded-lg">
              <p class="text-yellow-800 font-semibold">What to do:</p>
              <ol class="text-yellow-700 text-sm list-decimal list-inside mt-1">
                <li>Check all required fields are filled</li>
                <li>Verify stock availability for items</li>
                <li>Ensure network connection is stable</li>
                <li>Try again or contact support if issue persists</li>
              </ol>
            </div>
          </div>
        `,
        timer: 12000,
        timerProgressBar: true,
        showConfirmButton: true,
        confirmButtonText: 'OK',
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f3f4f6' : '#1f2937'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Add handleEdit function for tiles delivery orders
  const handleEdit = async (order) => {
    try {
      setLoading(true);
      setIsEditing(true);
      
      const response = await api.get(`/tiles/delivery-orders/details/${order._id}`);

      if (!response.data || !response.data.success || !response.data.deliveryOrder) {
        throw new Error('Invalid response data');
      }

      const orderDetails = response.data.deliveryOrder;
      console.log('🔍 Edit Mode - OrderDetails received:', orderDetails);

      // Store original order data for differential calculations
      setOriginalOrderData(orderDetails);

      // Create form data for tiles delivery order
      // Start with clean state, then explicitly map orderDetails fields
      const formData = {
        ...initializeFormState(user?.user?._id), // Start with clean state
        // Explicitly map all orderDetails fields to avoid conflicts
        recipientType: orderDetails.recipientType,
        recipient: orderDetails.recipient ? {
          value: orderDetails.recipient._id,
          label: orderDetails.recipientType === 'Customer' 
            ? orderDetails.recipient.name
            : (orderDetails.recipient.businessName || orderDetails.recipient.businessName)
        } : null,
        location: orderDetails.location ? orderDetails.location._id : null,
        transporterName: orderDetails.transporterName ? orderDetails.transporterName._id : '',
        reference: orderDetails.reference ? orderDetails.reference._id : '',
        display: {
          transporterDisplayName: orderDetails.transporterName?.name || '',
          referenceName: orderDetails.reference?.name || '',
        },
        godown: orderDetails.godown ? orderDetails.godown._id : null,
        salesman: orderDetails.salesman ? orderDetails.salesman.map(s => s._id) : [],
        interior: orderDetails.interior ? orderDetails.interior._id : null,
        items: orderDetails.items.map(item => {
          // Try to extract tileId properly
          let tileId = '';
          if (item.tileId) {
            // If tileId is an object (populated), get the _id
            tileId = typeof item.tileId === 'object' ? item.tileId._id : item.tileId;
          } else if (item.Name) {
            // If no tileId but we have item data, try to find matching tile
            // For now, we'll use a placeholder and let the user re-select if needed
            console.warn(`Missing tileId for item: ${item.Name}. User may need to reselect tile.`);
          }
          
          // Get item location - could be object or string
          const itemLocation = item.location?._id || item.location || orderDetails.location?._id || orderDetails.location;
          
          return {
            _id: item._id || generateId(),
            tileId: tileId,
            Name: item.Name || '',
            Size: item.Size || '',
            Company: item.Company || '',
            Category: item.Category || '',
            SubCategory: item.SubCategory || '',
            Design: item.Design || '',
            quality: item.quality || '',
            thickness: item.thickness || '',
            finish: item.finish || '',
            tileDetails: item.tileDetails || {},
            description: item.description || '',
            printType: item.printType || 'item',
            location: itemLocation, // Per-item location for multi-location support
            quantity: parseFloat(item.quantity) || 0,
            // Use helper function for smart quantity distribution
            ...distributeQuantityToFields(item),
            units: item.units || 'pcs',
            rate: parseFloat(item.rate) || 0,
            discount: parseFloat(item.discount) || 0,
            amountBeforeDiscount: parseFloat(item.amountBeforeDiscount) || 0,
            amountAfterDiscount: parseFloat(item.amountAfterDiscount) || 0,
            availableStock: item.availableStock || {
              freshStockBox: 0, // Will be updated when tiles are loaded
              freshStockPieces: 0,
              cornerDamagedBox: 0,
              cornerDamagedPieces: 0
            },
            tt: item.tt || { type: 'percentage', value: 0, calculatedAmount: 0 }
          };
        }),
        // Map stock items with location
        stockItems: (orderDetails.stockItems || []).map(stockItem => {
          // Get stockItemId properly
          let stockItemId = '';
          if (stockItem.stockItemId) {
            stockItemId = typeof stockItem.stockItemId === 'object' ? stockItem.stockItemId._id : stockItem.stockItemId;
          }
          
          // Get item location
          const itemLocation = stockItem.location?._id || stockItem.location || orderDetails.location?._id || orderDetails.location;
          
          return {
            _id: stockItem._id || generateId(),
            stockItemId: stockItemId,
            name: stockItem.name || '',
            location: itemLocation, // Per-item location for multi-location support
            freshQuantity: parseFloat(stockItem.freshQuantity) || 0,
            damagedQuantity: parseFloat(stockItem.damagedQuantity) || 0,
            rate: parseFloat(stockItem.rate) || 0,
            discount: parseFloat(stockItem.discount) || 0,
            amountBeforeDiscount: parseFloat(stockItem.amountBeforeDiscount) || 0,
            amountAfterDiscount: parseFloat(stockItem.amountAfterDiscount) || 0,
            availableStock: stockItem.availableStock || {
              freshQuantity: 0, // Will be updated when stock items are loaded
              damagedQuantity: 0
            }
          };
        }),
        vehicleNumber: orderDetails.vehicleNumber || '',
        transportMedium: orderDetails.transportMedium || '',
        siteAddress: orderDetails.siteAddress || '',
        modeOfPayment: Array.isArray(orderDetails.modeOfPayment) ? orderDetails.modeOfPayment : [],
        modeOfSales: orderDetails.modeOfSales || 'Paid Instantly',
        creditReference: orderDetails.creditReference || '',
        creditPeriod: parseFloat(orderDetails.creditPeriod) || 0,
        claimPaymentFrom: {
          name: orderDetails.claimPaymentFrom?.name || '',
          number: orderDetails.claimPaymentFrom?.number || ''
        },
        roundoff: parseFloat(orderDetails.roundoff) || 0,
        Narration: orderDetails.Narration || orderDetails.narration || '',
        packingAndForwarding: parseFloat(orderDetails.packingAndForwarding) || 0,
        packingAndForwardingGST: parseFloat(orderDetails.packingAndForwardingGST) || 0,
        Manual_dO_Number: orderDetails.Manual_dO_Number || '',
        poNumber: orderDetails.poNumber || '',
        itemDescription: orderDetails.itemDescription || '',
        referenceName: orderDetails.referenceName || '',
        cartage: parseFloat(orderDetails.cartage) || 0,
        cartageGST: parseFloat(orderDetails.cartageGST) || 0,
        labour: parseFloat(orderDetails.labour) || 0,
        labourGST: parseFloat(orderDetails.labourGST) || 0,
        totalAmountWithoutGST: parseFloat(orderDetails.totalAmountWithoutGST) || 0,
        totalAmountWithGST: parseFloat(orderDetails.totalAmountWithGST) || 0,
        totalTTAmount: parseFloat(orderDetails.totalTTAmount) || 0,
        isReference: orderDetails.isReference || orderDetails.isreference || false,
        isNarration: orderDetails.isNarration || orderDetails.isnarration || false,
        dueDate: orderDetails.dueDate || '',
        invoiceNumber: orderDetails.invoiceNumber || '',
        status: orderDetails.status || 'Pending',
        doNumber: orderDetails.doNumber || '',
        orderNumber: orderDetails.orderNumber || orderDetails.doNumber || '',
        // Preserve tracking fields from initial state
        deliveryOrderId: orderDetails._id || null,
        tilesOutwardId: orderDetails.tilesOutwardId || null,
        createdBy: orderDetails.createdBy || user?.user?._id
      };
      
      console.log('📝 Edit Mode - FormData created:', formData);
      console.log('🔍 Edit Mode - Field Mapping Check:', {
        reference: {
          orderDetails: orderDetails.reference,
          mapped: formData.reference
        },
        totalTTAmount: {
          orderDetails: orderDetails.totalTTAmount,
          mapped: formData.totalTTAmount
        },
        claimPaymentFrom: {
          orderDetails: orderDetails.claimPaymentFrom,
          mapped: formData.claimPaymentFrom
        },
        transportMedium: {
          orderDetails: orderDetails.transportMedium,
          mapped: formData.transportMedium
        },
        referenceName: {
          orderDetails: orderDetails.referenceName,
          mapped: formData.referenceName
        }
      });

      setForm(formData);
      setOrderId(order._id);
      
      // Update payment modes input if it exists
      if (formData.modeOfPayment && Array.isArray(formData.modeOfPayment)) {
        setPaymentModesInput(formData.modeOfPayment.join(', '));
      }

      // Recalculate totals for the loaded data
      setTimeout(() => {
        calculateTotals();
      }, 100);

      // Refresh tile availability data for edit mode - using per-item location
      const hasItems = formData.items && formData.items.length > 0 && formData.items.some(i => i.tileId);
      console.log('🔍 Edit mode - hasItems check:', { hasItems, itemsCount: formData.items?.length, items: formData.items?.map(i => ({ tileId: i.tileId, Name: i.Name, location: i.location })) });
      
      if (hasItems) {
        console.log('🔄 Refreshing tile availability data for edit mode');
        
        // Update each item with current stock data using per-item location
        setTimeout(async () => {
          try {
            const updatedItems = await Promise.all(formData.items.map(async (item, index) => {
              // Check for tileId only (Name might not be set in all cases)
              if (item.tileId) {
                // Use per-item location for multi-location support
                const itemLocation = item.location || formData.location;
                console.log(`📊 Loading stock for Item ${index + 1}: ${item.Name || 'Unknown'} - TileID: ${item.tileId} - Location: ${itemLocation}`);
                
                try {
                  // Use the correct API endpoint
                  console.log(`📦 Fetching stock data for tile ${item.tileId} (${item.Name})`);
                  const tileResponse = await api.get(`/tiles/get-tile/${item.tileId}`);
                  
                  if (tileResponse?.data && tileResponse.data.success) {
                    const tile = tileResponse.data.tile;
                    
                    if (tile) {
                      const tileName = item.Name || tile.tilesName || 'Unknown';
                      console.log(`📦 Found tile data for ${tileName}:`, tile);
                      // Use per-item location
                      const itemLocation = item.location || formData.location;
                      console.log(`📍 Item location ID: ${itemLocation}`);
                      console.log(`📊 Tile stock array:`, tile.stock);
                      
                      // Find stock for item's location
                      let locationStock = null;
                      
                      if (tile.stock && Array.isArray(tile.stock)) {
                        console.log(`🔍 Searching for location match in ${tile.stock.length} stock entries`);
                        locationStock = tile.stock.find(s => {
                          const stockLocationId = s.location?._id || s.location;
                          console.log(`🏢 Comparing stock location "${stockLocationId}" with item location "${itemLocation}"`);
                          
                          // Try both direct comparison and string comparison
                          const directMatch = stockLocationId === itemLocation;
                          const stringMatch = String(stockLocationId) === String(itemLocation);
                          console.log(`   Direct match: ${directMatch}, String match: ${stringMatch}`);
                          
                          return directMatch || stringMatch;
                        });
                        console.log(`📍 Found location-specific stock:`, locationStock);
                      }
                      
                      // If no location-specific stock found, use any available stock data
                      if (!locationStock && tile.stock && tile.stock.length > 0) {
                        console.log(`⚠️ No location-specific stock found, using first available stock`);
                        locationStock = tile.stock[0]; // Use first available stock
                      }
                      
                      const currentStock = locationStock || {
                        freshStockBox: 0,
                        freshStockPieces: 0,
                        cornerDamagedBox: 0,
                        cornerDamagedPieces: 0
                      };
                      
                      // In edit mode, add back the order's quantities to available stock
                      // (since they were already deducted and would be returned on update)
                      const orderFreshBoxes = parseFloat(item.freshBoxes) || 0;
                      const orderFreshPieces = parseFloat(item.freshPieces) || 0;
                      const orderDamagedBoxes = parseFloat(item.damagedBoxes) || 0;
                      const orderDamagedPieces = parseFloat(item.damagedPieces) || 0;
                      
                      const stockData = {
                        freshStockBox: (currentStock.freshStockBox || 0) + orderFreshBoxes,
                        freshStockPieces: (currentStock.freshStockPieces || 0) + orderFreshPieces,
                        cornerDamagedBox: (currentStock.cornerDamagedBox || 0) + orderDamagedBoxes,
                        cornerDamagedPieces: (currentStock.cornerDamagedPieces || 0) + orderDamagedPieces
                      };
                      
                      console.log(`📊 Current stock for ${tileName}:`, currentStock);
                      console.log(`📦 Order quantities: Fresh ${orderFreshBoxes} box, ${orderFreshPieces} pcs | Damaged ${orderDamagedBoxes} box, ${orderDamagedPieces} pcs`);
                      console.log(`📊 Available stock (current + order):`, stockData);
                      console.log(`🚚 In-transit boxes: ${tile.inTransitBoxes || 0}, Boxes ordered: ${tile.boxesOrdered || 0}`);
                      
                      return {
                        ...item,
                        availableStock: stockData,
                        tileDetails: tile  // Full tile data including inTransitBoxes for edit mode max/disabled calculations
                      };
                    }
                  }
                } catch (error) {
                  console.warn(`⚠️ Could not load stock for tile ${item.tileId}:`, error.response?.data || error.message);
                }
              }
              return item;
            }));
            
            // Update form with refreshed stock data - create new array reference to ensure re-render
            console.log('📝 Updating form with refreshed tile stock data:', updatedItems.map(i => ({ Name: i.Name, availableStock: i.availableStock })));
            setForm(prev => ({
              ...prev,
              items: [...updatedItems]  // Spread to create new array reference
            }));
            
            console.log('✅ Tile availability data refreshed for edit mode');
          } catch (error) {
            console.error('❌ Error refreshing tile availability:', error);
          }
        }, 300);
      }

      // Refresh stock items availability data for edit mode
      const hasStockItems = formData.stockItems && formData.stockItems.length > 0 && formData.stockItems.some(i => i.stockItemId);
      if (hasStockItems) {
        console.log('🔄 Refreshing stock items availability data for edit mode');
        
        setTimeout(async () => {
          try {
            const updatedStockItems = await Promise.all(formData.stockItems.map(async (stockItem, index) => {
              if (stockItem.stockItemId && stockItem.name) {
                // Use per-item location for multi-location support
                const itemLocation = stockItem.location || formData.location;
                console.log(`📊 Loading stock for Stock Item ${index + 1}: ${stockItem.name} - ID: ${stockItem.stockItemId} - Location: ${itemLocation}`);
                
                try {
                  // Fetch stock item data
                  const stockResponse = await api.get(`/stock-items/get-stock-item/${stockItem.stockItemId}`);
                  
                  if (stockResponse?.data && stockResponse.data.success) {
                    const item = stockResponse.data.stockItem;
                    
                    if (item && item.stock && Array.isArray(item.stock)) {
                      console.log(`📦 Found stock item data for ${stockItem.name}:`, item);
                      
                      // Find stock for item's location
                      let locationStock = item.stock.find(s => {
                        const stockLocationId = s.location?._id || s.location;
                        return String(stockLocationId) === String(itemLocation);
                      });
                      
                      // Fallback to first available stock
                      if (!locationStock && item.stock.length > 0) {
                        console.log(`⚠️ No location-specific stock found, using first available stock`);
                        locationStock = item.stock[0];
                      }
                      
                      const currentStock = locationStock || {
                        freshQuantity: 0,
                        damagedQuantity: 0
                      };
                      
                      // In edit mode, add back the order's quantities to available stock
                      // (since they were already deducted and would be returned on update)
                      const orderFreshQty = parseFloat(stockItem.freshQuantity) || 0;
                      const orderDamagedQty = parseFloat(stockItem.damagedQuantity) || 0;
                      
                      const stockData = {
                        freshQuantity: (currentStock.freshQuantity || 0) + orderFreshQty,
                        damagedQuantity: (currentStock.damagedQuantity || 0) + orderDamagedQty
                      };
                      
                      console.log(`📊 Current stock for ${stockItem.name}:`, currentStock);
                      console.log(`📦 Order quantities: Fresh ${orderFreshQty}, Damaged ${orderDamagedQty}`);
                      console.log(`📊 Available stock (current + order):`, stockData);
                      console.log(`🚚 In-transit quantity: ${item.inTransitQuantity || 0}, Quantity ordered: ${item.quantityOrdered || 0}`);
                      
                      return {
                        ...stockItem,
                        availableStock: stockData,
                        stockItemDetails: item  // Full stock item data including inTransitQuantity for edit mode max/disabled calculations
                      };
                    }
                  }
                } catch (error) {
                  console.warn(`⚠️ Could not load stock for ${stockItem.name}:`, error.response?.data || error.message);
                }
              }
              return stockItem;
            }));
            
            // Update form with refreshed stock data
            setForm(prev => ({
              ...prev,
              stockItems: updatedStockItems
            }));
            
            console.log('✅ Stock items availability data refreshed for edit mode');
          } catch (error) {
            console.error('❌ Error refreshing stock items availability:', error);
          }
        }, 500); // Slight delay after tiles loading
      }

      // Scroll to top
      window.scrollTo(0, 0);
      
      Swal.fire({
        icon: 'info',
        title: 'Edit Mode',
        text: 'Order loaded for editing. Make your changes and update the order.',
        timer: 2000,
        customClass: { popup: 'swal-dark-mode' }
      });

    } catch (error) {
      console.error('Error fetching order details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to fetch order details',
        customClass: { popup: 'swal-dark-mode' }
      });
    } finally {
      setLoading(false);
    }
  };
  const handlePrint = (id, printType = 'basic') => {
    let url;
    if (printType === 'discount') {
      url = `/tiles-delivery-orders-discount/${id}`;
    } else {
      url = `/tiles-delivery-orders/${id}`;
    }
    window.open(url, '_blank');
  };

  const handlePrintMenuOpen = (event, orderId) => {
    setPrintMenuAnchor(event.currentTarget);
    setSelectedPrintOrderId(orderId);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
    setSelectedPrintOrderId(null);
  };

  const handlePrintOption = (printType) => {
    if (selectedPrintOrderId) {
      handlePrint(selectedPrintOrderId, printType);
    }
    handlePrintMenuClose();
  };

  // Cancel edit function
  const handleCancelEdit = () => {
    Swal.fire({
      title: 'Cancel Editing?',
      text: 'All unsaved changes will be lost.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel editing',
      customClass: { popup: 'swal-dark-mode' }
    }).then((result) => {
      if (result.isConfirmed) {
        setIsEditing(false);
        setOrderId(null);
        setOriginalOrderData(null);
        setForm(initializeFormState(user?.user?._id));
        
        Swal.fire({
          icon: 'info',
          title: 'Edit Cancelled',
          text: 'Returned to create new order mode.',
          timer: 1500,
          customClass: { popup: 'swal-dark-mode' }
        });
      }
    });
  };

  // Order filtering functions with grouped options
  // Enhanced custom styles for react-select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '44px',
      fontSize: '14px',
      borderWidth: '2px',
      borderColor: state.isFocused 
        ? '#3b82f6'
        : (darkMode ? '#4b5563' : '#d1d5db'),
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      borderRadius: '10px',
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        borderColor: darkMode ? '#60a5fa' : '#93c5fd',
        backgroundColor: darkMode ? '#374151' : '#ffffff'
      }
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '14px',
      padding: '10px 12px',
      cursor: 'pointer',
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? (darkMode ? '#4b5563' : '#dbeafe')
          : (darkMode ? '#1f2937' : '#ffffff'),
      color: state.isSelected ? '#ffffff' : (darkMode ? '#f3f4f6' : '#1f2937'),
      transition: 'all 0.15s ease',
      '&:active': {
        backgroundColor: '#3b82f6'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#6b7280',
      fontSize: '14px'
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#1f2937'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#4b5563' : '#e0e7ff',
      borderRadius: '6px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: darkMode ? '#e5e7eb' : '#3730a3',
      fontSize: '13px',
      padding: '2px 6px'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#6366f1',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: darkMode ? '#ef4444' : '#dc2626',
        color: '#ffffff'
      }
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '10px',
      border: `2px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`,
      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
      boxShadow: darkMode 
        ? '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)'
        : '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      marginTop: '4px',
      zIndex: 9999
    }),
    menuList: (base) => ({
      ...base,
      borderRadius: '8px',
      padding: '4px',
      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
      maxHeight: '300px'
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#6b7280',
      transition: 'all 0.2s ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
      '&:hover': {
        color: '#3b82f6'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#6b7280',
      cursor: 'pointer',
      '&:hover': {
        color: '#ef4444'
      }
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#1f2937',
      fontSize: '14px'
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#4b5563' : '#e5e7eb'
    }),
    loadingIndicator: (base) => ({
      ...base,
      color: '#3b82f6'
    })
  };

  const formatOrderDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
  };

  const getStatusColor = (status) => {
    // Status colors work in both light and dark mode
    switch (status) {
      case 'Completed': return darkMode ? 'bg-green-900/50 text-green-300 border-green-700' : 'bg-green-100 text-green-800 border-green-200';
      case 'Pending': return darkMode ? 'bg-yellow-900/50 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Approved': return darkMode ? 'bg-blue-900/50 text-blue-300 border-blue-700' : 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Cancelled': return darkMode ? 'bg-red-900/50 text-red-300 border-red-700' : 'bg-red-100 text-red-800 border-red-200';
      default: return darkMode ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleDuplicateOrder = (order) => {
    // Create a new form based on the selected order
    const duplicatedForm = {
      ...initializeFormState(user?.user?._id),
      recipientType: order.recipientType,
      recipient: order.recipient ? {
        value: order.recipient._id || order.recipient.id,
        label: order.recipient.name || order.recipient.businessName,
        ...order.recipient
      } : null,
      location: order.location?._id || order.location?.id,
      vehicleNumber: order.vehicleNumber || '',
      transporter: order.transporter ? {
        value: order.transporter._id || order.transporter.id,
        label: order.transporter.name,
        ...order.transporter
      } : null,
      reference: order.reference ? {
        value: order.reference._id || order.reference.id,
        label: order.reference.name,
        ...order.reference
      } : null,
      items: order.items?.map(item => ({
        ...item,
        freshBoxes: 0,
        freshPieces: 0,
        damagedBoxes: 0,
        damagedPieces: 0,
        rate: item.rate || 0,
        discount: item.discount || 0
      })) || []
    };
    
    setForm(duplicatedForm);
    
    Swal.fire({
      title: 'Order Duplicated!',
      text: 'The order details have been copied to the form. Update quantities and submit.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      customClass: { popup: 'swal-dark-mode' }
    });
  };

  // Status Management Functions
  const openStatusModal = (order, newStatus = null) => {
    setSelectedOrderForStatus(order);
    setStatusUpdateData({
      status: newStatus || order.status || 'Pending', // Use provided newStatus or fall back to order's current status
      remarks: '',
      invoiceNumber: order.invoiceNumber || ''
    });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrderForStatus) return;

    // Validate required fields
    if ((statusUpdateData.status === 'Completed' || statusUpdateData.status === 'Cancelled') && !statusUpdateData.remarks.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Remarks Required',
        text: `Please provide remarks when changing status to ${statusUpdateData.status}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    // Validate invoice number for Completed status
    if (statusUpdateData.status === 'Completed' && !statusUpdateData.invoiceNumber.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Invoice Number Required',
        text: 'Please provide an invoice number when marking as Completed',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    try {
      const updatePayload = {
        status: statusUpdateData.status,
        remarks: statusUpdateData.remarks,
        updatedBy: user?.user?._id
      };

      if (statusUpdateData.status === 'Completed') {
        updatePayload.invoiceNumber = statusUpdateData.invoiceNumber;
      }

      if (statusUpdateData.status === 'Cancelled') {
        updatePayload.reason = statusUpdateData.remarks;
      }

      await api.put(`/tiles/delivery-orders/update-status/${selectedOrderForStatus._id}`, updatePayload);

      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Delivery order status updated to ${statusUpdateData.status}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        customClass: { popup: 'swal-dark-mode' }
      });

      // Refresh orders list
      fetchOrders();
      setShowStatusModal(false);
      setSelectedOrderForStatus(null);

    } catch (error) {
      console.error('Error updating status:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update delivery order status',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        customClass: { popup: 'swal-dark-mode' }
      });
    }
  };

  // Handle Cancel Order
  const handleCancelOrder = async (order) => {
    try {
      // Show confirmation dialog with dark mode support
      const result = await Swal.fire({
        title: 'Cancel Order?',
        html: `
          <div class="text-left ${darkMode ? 'text-gray-200' : 'text-gray-800'}">
            <p class="mb-4"><strong>Order:</strong> ${order.orderNumber || order.doNumber}</p>
            <p class="mb-4"><strong>Customer:</strong> ${order.recipient?.name || order.recipient?.businessName || 'N/A'}</p>
            <p class="mb-4 text-red-500"><strong>Warning:</strong> This action will:</p>
            <ul class="text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-4 pl-4 list-disc">
              <li>Cancel the delivery order</li>
              <li>Restore stock levels to inventory</li>
              <li>Delete associated outward entries</li>
              <li>Cannot be undone</li>
            </ul>
            <div class="mb-3">
              <label class="block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2">Cancellation Reason (Required):</label>
              <textarea id="cancel-reason" class="w-full p-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-900'} rounded-md" rows="3" placeholder="Enter reason for cancellation..."></textarea>
            </div>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Cancel Order',
        cancelButtonText: 'Keep Order',
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f3f4f6' : '#1f2937',
        preConfirm: () => {
          const reason = document.getElementById('cancel-reason').value.trim();
          if (!reason) {
            Swal.showValidationMessage('Please provide a reason for cancellation');
            return false;
          }
          return reason;
        }
      });

      if (result.isConfirmed && result.value) {
        const reason = result.value;
        
        // Show loading
        Swal.fire({
          title: 'Cancelling Order...',
          text: 'Please wait while we cancel the order and restore stock',
          allowOutsideClick: false,
          customClass: { popup: 'swal-dark-mode' },
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Call cancel API endpoint
        const response = await api.put(`/tiles/delivery-orders/cancel/${order._id}`, {
          reason: reason,
          cancelledBy: user?.user?._id
        });

        console.log('✅ Order cancelled successfully:', response.data);

        // Show success message with dark mode support
        await Swal.fire({
          icon: 'success',
          title: 'Order Cancelled!',
          html: `
            <div class="text-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}">
              <p class="mb-2">Order <strong>${order.orderNumber || order.doNumber}</strong> has been cancelled successfully.</p>
              <p class="text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}">Stock has been restored and outward entries have been removed.</p>
            </div>
          `,
          timer: 3000,
          showConfirmButton: false,
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#f3f4f6' : '#1f2937'
        });

        // Refresh orders list
        fetchOrders();

      }
    } catch (error) {
      console.error('❌ Error cancelling order:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'Cancellation Failed',
        text: error.response?.data?.message || 'Failed to cancel delivery order. Please try again.',
        timer: 5000,
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f3f4f6' : '#1f2937'
      });
    }
  };

  // Handle Edit Recipient - open modal
  const handleOpenEditRecipient = (order) => {
    setEditingRecipientOrder(order);
    setEditRecipientType(order.recipientType || 'Customer');
    setEditRecipientValue(null);
    setShowEditRecipientModal(true);
  };

  // Handle Update Recipient - submit
  const handleUpdateRecipient = async () => {
    if (!editRecipientValue?.value) {
      Swal.fire({ icon: 'warning', title: 'Select Recipient', text: 'Please select a new recipient.', background: darkMode ? '#1f2937' : '#ffffff', color: darkMode ? '#f3f4f6' : '#1f2937' });
      return;
    }
    try {
      setUpdatingRecipient(true);
      const response = await api.put(`/tiles/delivery-orders/${editingRecipientOrder._id}/recipient`, {
        recipientType: editRecipientType,
        recipient: editRecipientValue.value
      });
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Recipient Updated',
          text: `Recipient changed to ${editRecipientValue.label} for order ${editingRecipientOrder.doNumber}`,
          timer: 3000,
          showConfirmButton: false,
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#f3f4f6' : '#1f2937'
        });
        setShowEditRecipientModal(false);
        setEditingRecipientOrder(null);
        setEditRecipientValue(null);
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating recipient:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update recipient',
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f3f4f6' : '#1f2937'
      });
    } finally {
      setUpdatingRecipient(false);
    }
  };

  // Early return for safety
  if (!form || !form.items || !Array.isArray(form.items)) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className={`transition-colors duration-300 ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>Loading...</p>
        </div>
      </div>
    );
  }

  // Order Details Modal Component
  const OrderDetailsModal = () => {
    const [viewingPastData, setViewingPastData] = useState(null);
    
    if (!selectedOrder) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 overflow-y-auto">
        <div className={`rounded-lg p-4 sm:p-6 w-full max-w-6xl max-h-[75vh] sm:max-h-[80vh] overflow-y-auto shadow-2xl border m-4 transition-colors duration-300 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`flex justify-between items-center mb-4 sm:mb-6 border-b pb-3 sm:pb-4 transition-colors duration-300 ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div>
              <h3 className={`text-xl sm:text-2xl font-bold transition-colors duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>
                Tiles Delivery Order Details: <span className="text-blue-600">{selectedOrder.orderNumber}</span>
              </h3>
              <p className={`text-xs sm:text-sm mt-1 transition-colors duration-300 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Created on {((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(selectedOrder.createdAt))} by {selectedOrder.createdBy?.name || "Unknown"}
              </p>
            </div>
            <button
              onClick={() => setShowViewModal(false)}
              className={`p-2 rounded-full transition duration-150 ${
                darkMode 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>

          {viewingPastData ? (
            // Viewing a previous version of the order
            <div className="relative">
              <div className="absolute -top-12 right-0">
                <button
                  onClick={() => setViewingPastData(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                >
                  <FaArrowRight className="w-4 h-4 mr-2 rotate-180" />
                  Back to Current Version
                </button>
              </div>

              <div className={`border-l-4 p-4 mb-6 transition-colors duration-300 ${
                darkMode ? 'bg-yellow-900/20 border-yellow-600' : 'bg-yellow-50 border-yellow-400'
              }`}>
                <div className="flex items-center">
                  <FaInfoCircle className={`w-5 h-5 mr-3 transition-colors duration-300 ${
                    darkMode ? 'text-yellow-400' : 'text-yellow-600'
                  }`} />
                  <p className={`text-sm transition-colors duration-300 ${
                    darkMode ? 'text-yellow-200' : 'text-yellow-700'
                  }`}>
                    You are viewing a previous version of this order from <span className="font-medium">{((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(viewingPastData.date))}</span> updated by <span className="font-medium">{viewingPastData.employee?.name || "Unknown"}</span>
                  </p>
                </div>
              </div>

              {/* Display past version data */}
              <div className="space-y-6">
                <div className={`rounded-lg shadow-md overflow-hidden transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700' : 'bg-white'
                }`}>
                  <div className={`px-4 py-3 border-b transition-colors duration-300 ${
                    darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-800'
                    }`}>Previous Order Details</h3>
                  </div>
                  <div className="p-4">
                    <pre className={`whitespace-pre-wrap p-4 rounded text-sm overflow-x-auto transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-800'
                    }`}>
                      {JSON.stringify(viewingPastData.pastData, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Default view: Current order details
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Order Info */}
                <div className={`p-5 rounded-lg shadow-md transition-colors duration-300 ${
                  darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gradient-to-r from-blue-50 to-blue-100'
                }`}>
                  <h4 className={`font-semibold mb-4 flex items-center transition-colors duration-300 ${
                    darkMode ? 'text-blue-400' : 'text-blue-800'
                  }`}>
                    <FaClipboardList className="w-5 h-5 mr-2" />
                    Order Information
                  </h4>
                  <dl className="grid grid-cols-2 gap-3">
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Order Number:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.doNumber}</dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Status:</dt>
                    <dd className="text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${selectedOrder.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 
                          selectedOrder.status === 'Completed' ? 'bg-green-100 text-green-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {selectedOrder.status}
                      </span>
                    </dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Created On:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{new Date(selectedOrder.createdAt).toLocaleString()}</dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Vehicle Number:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.vehicleNumber || '-'}</dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Transporter:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.transporterName?.name || '-'}</dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Godown:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.godown?.Name || 'Unassigned'}</dd>
                  </dl>
                </div>
                
                {/* Recipient Info */}
                <div className={`p-5 rounded-lg shadow-md transition-colors duration-300 ${
                  darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gradient-to-r from-purple-50 to-purple-100'
                }`}>
                  <h4 className={`font-semibold mb-4 flex items-center transition-colors duration-300 ${
                    darkMode ? 'text-purple-400' : 'text-purple-800'
                  }`}>
                    <FaUser className="w-5 h-5 mr-2" />
                    Recipient Information
                  </h4>
                  <dl className="grid grid-cols-2 gap-3">
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Type:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.recipientType}</dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Name:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {selectedOrder.recipientType === 'Customer' 
                        ? selectedOrder.recipient?.name
                        : selectedOrder.recipient?.businessName || selectedOrder.recipient?.businessName}
                    </dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Address:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {selectedOrder.recipient?.address || '-'}
                    </dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Phone:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {selectedOrder.recipient?.phone || selectedOrder.recipient?.phoneNumber || '-'}
                    </dd>
                    
                    <dt className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Location:</dt>
                    <dd className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {selectedOrder.location?.name || '-'}
                    </dd>
                  </dl>
                </div>
              </div>
              
              {/* Tiles Items Table */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
              <div className="mt-6 mb-6">
                <h4 className={`font-semibold mb-4 flex items-center transition-colors duration-300 ${
                  darkMode ? 'text-gray-100' : 'text-gray-800'
                }`}>
                  <FaCubes className="w-5 h-5 mr-2" />
                  Tiles Items ({selectedOrder.items.length})
                  {selectedOrder.isMultiLocation && (
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                    }`}>
                      <FaMapMarkerAlt className="inline w-3 h-3 mr-1" />
                      Multi-Location ({selectedOrder.uniqueLocationsCount} locations)
                    </span>
                  )}
                </h4>
                <div className="overflow-x-auto rounded-lg shadow">
                  <table className={`min-w-full transition-colors duration-300 ${
                    darkMode ? 'bg-gray-700' : 'bg-white'
                  }`}>
                    <thead className={`transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Tile Name</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Location</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Size</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Company</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Quantity</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Unit</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Rate</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Discount</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>TT</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Amount</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y transition-colors duration-300 ${
                      darkMode ? 'divide-gray-600' : 'divide-gray-200'
                    }`}>
                      {selectedOrder.items?.map((item, index) => (
                        <tr key={index} className={index % 2 === 0 
                          ? (darkMode ? 'bg-gray-700' : 'bg-white')
                          : (darkMode ? 'bg-gray-800' : 'bg-gray-50')
                        }>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.Name || "Unknown Tile"}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                              darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}>
                              <FaMapMarkerAlt className="w-3 h-3 mr-1" />
                              {item.location?.name || selectedOrder.location?.name || '-'}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.Size || "-"}</td>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.Company || "-"}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.quantity || 0}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.units || "-"}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>₹{formatNumberWithCommas(item.rate || 0)}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.discount || 0}%</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {item.tt?.value > 0 ? (
                              <div className="space-y-1">
                                <span className="text-purple-600 font-medium">
                                  {item.tt.type === 'percentage' ? `${item.tt.value}%` : `₹${item.tt.value}`}
                                </span>
                                {item.tt.calculatedAmount > 0 && (
                                  <div className="text-xs text-purple-500">
                                    = ₹{item.tt.calculatedAmount.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>₹{formatNumberWithCommas(item.amountAfterDiscount || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className={`transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <tr>
                        <td colSpan="9" className={`px-4 py-3 text-right font-medium transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>Total:</td>
                        <td className={`px-4 py-3 whitespace-nowrap font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                          ₹{formatNumberWithCommas(selectedOrder.totalAmountWithGST || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              )}
              
              {/* Stock Items Table */}
              {selectedOrder.stockItems && selectedOrder.stockItems.length > 0 && (
              <div className="mt-6 mb-6">
                <h4 className={`font-semibold mb-4 flex items-center transition-colors duration-300 ${
                  darkMode ? 'text-gray-100' : 'text-gray-800'
                }`}>
                  <FaBoxes className="w-5 h-5 mr-2" />
                  Stock Items ({selectedOrder.stockItems.length})
                </h4>
                <div className="overflow-x-auto rounded-lg shadow">
                  <table className={`min-w-full transition-colors duration-300 ${
                    darkMode ? 'bg-gray-700' : 'bg-white'
                  }`}>
                    <thead className={`transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Item Name</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Brand</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Category</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Fresh Qty</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Damaged Qty</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Total Qty</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Rate</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Discount</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>Amount</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y transition-colors duration-300 ${
                      darkMode ? 'divide-gray-600' : 'divide-gray-200'
                    }`}>
                      {selectedOrder.stockItems?.map((item, index) => {
                        const totalQty = (item.freshQuantity || 0) + (item.damagedQuantity || 0);
                        return (
                        <tr key={index} className={index % 2 === 0 
                          ? (darkMode ? 'bg-gray-700' : 'bg-white')
                          : (darkMode ? 'bg-gray-800' : 'bg-gray-50')
                        }>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.stockItemId?.name || item.name || "Unknown Item"}</td>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.stockItemId?.brand?.name || item.brand || "-"}</td>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.stockItemId?.category?.name || item.category || "-"}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.freshQuantity || 0}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.damagedQuantity || 0}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{totalQty}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>₹{formatNumberWithCommas(item.rate || 0)}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.discount || 0}%</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>₹{formatNumberWithCommas(item.amountAfterDiscount || 0)}</td>
                        </tr>
                      )})}
                    </tbody>
                    <tfoot className={`transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <tr>
                        <td colSpan="8" className={`px-4 py-3 text-right font-medium transition-colors duration-300 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>Subtotal:</td>
                        <td className={`px-4 py-3 whitespace-nowrap font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                          ₹{formatNumberWithCommas(
                            selectedOrder.stockItems?.reduce((sum, item) => sum + (item.amountAfterDiscount || 0), 0) || 0
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              )}
              
              {/* Additional Charges, Other Details and Amounts */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded-lg shadow-md transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gradient-to-r from-green-50 to-green-100'
                }`}>
                  <h4 className={`font-semibold mb-3 flex items-center transition-colors duration-300 ${
                    darkMode ? 'text-green-400' : 'text-green-800'
                  }`}>
                    <FaMoneyBillWave className="w-5 h-5 mr-2" />
                    Additional Charges
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>P&F Charges:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>₹{formatNumberWithCommas(selectedOrder.packingAndForwarding || 0)}</dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Labour:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>₹{formatNumberWithCommas(selectedOrder.labour || 0)}</dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Cartage:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>₹{formatNumberWithCommas(selectedOrder.cartage || 0)}</dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Round Off:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>₹{formatNumberWithCommas(selectedOrder.roundoff || 0)}</dd>
                  </dl>
                </div>
                
                <div className={`p-4 rounded-lg shadow-md transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gradient-to-r from-amber-50 to-amber-100'
                }`}>
                  <h4 className={`font-semibold mb-3 flex items-center transition-colors duration-300 ${
                    darkMode ? 'text-amber-400' : 'text-amber-800'
                  }`}>
                    <FaInfoCircle className="w-5 h-5 mr-2" />
                    Other Details
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Reference:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.reference?.name || '-'}</dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Mode of Payment:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.modeOfPayment?.join(', ') || '-'}</dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Delivery Date:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {selectedOrder.deliveryDate ? ((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(selectedOrder.deliveryDate)) : '-'}
                    </dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Delivery Time:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>{selectedOrder.deliveryTime || '-'}</dd>
                  </dl>
                </div>

                <div className={`p-4 rounded-lg shadow-md transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gradient-to-r from-blue-50 to-blue-100'
                }`}>
                  <h4 className={`font-semibold mb-3 flex items-center transition-colors duration-300 ${
                    darkMode ? 'text-blue-400' : 'text-blue-800'
                  }`}>
                    <FaCalculator className="w-5 h-5 mr-2" />
                    Amount Summary
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Items Total:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>₹{formatNumberWithCommas(selectedOrder.itemsTotal || 0)}</dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>TT Amount:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>₹{formatNumberWithCommas(selectedOrder.totalTTAmount || 0)}</dd>
                    
                    <dt className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>GST Amount:</dt>
                    <dd className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      ₹{formatNumberWithCommas(((selectedOrder.packingAndForwardingGST || 0) + 
                         (selectedOrder.labourGST || 0) + 
                         (selectedOrder.cartageGST || 0)))}
                    </dd>
                    
                    <dt className={`font-semibold border-t pt-2 mt-2 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-600 border-blue-200'
                    }`}>Final Amount:</dt>
                    <dd className={`font-bold border-t pt-2 mt-2 transition-colors duration-300 ${
                      darkMode ? 'text-blue-400 border-gray-600' : 'text-blue-700 border-blue-200'
                    }`}>₹{formatNumberWithCommas(selectedOrder.totalAmountWithGST || 0)}</dd>
                  </dl>
                </div>
              </div>

              {/* TT Summary Section */}
              {selectedOrder.reference && selectedOrder.totalTTAmount > 0 && (
                <div className={`mt-6 p-4 rounded-lg shadow-md transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gradient-to-r from-purple-50 to-purple-100'
                }`}>
                  <h4 className={`font-semibold mb-3 flex items-center transition-colors duration-300 ${
                    darkMode ? 'text-purple-400' : 'text-purple-800'
                  }`}>
                    <FaPercentage className="w-5 h-5 mr-2" />
                    TT Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className={`font-medium transition-colors duration-300 ${
                        darkMode ? 'text-purple-300' : 'text-purple-700'
                      }`}>Total TT Amount:</span>
                      <span className={`font-bold transition-colors duration-300 ${
                        darkMode ? 'text-purple-400' : 'text-purple-800'
                      }`}>₹{formatNumberWithCommas(selectedOrder.totalTTAmount)}</span>
                    </div>
                    
                    {/* Item-wise TT breakdown */}
                    <div className={`space-y-1 text-xs p-2 rounded border transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800 border-purple-700' : 'bg-purple-25 border-purple-200'
                    }`}>
                      <div className={`font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-purple-300' : 'text-purple-700'
                      }`}>Item-wise breakdown:</div>
                      {selectedOrder.items?.filter(item => item.tt?.calculatedAmount > 0).map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className={`transition-colors duration-300 ${
                            darkMode ? 'text-purple-300' : 'text-purple-600'
                          }`}>
                            {item.Name || `Item #${index + 1}`}
                            <span className={`ml-1 transition-colors duration-300 ${
                              darkMode ? 'text-purple-400' : 'text-purple-500'
                            }`}>
                              ({item.tt.type === 'percentage' ? `${item.tt.value}%` : `₹${item.tt.value}`})
                            </span>
                          </span>
                          <span className={`font-medium transition-colors duration-300 ${
                            darkMode ? 'text-purple-300' : 'text-purple-700'
                          }`}>
                            ₹{formatNumberWithCommas(item.tt.calculatedAmount)}
                          </span>
                        </div>
                      ))}
                      
                      {selectedOrder.items?.filter(item => item.tt?.calculatedAmount > 0).length === 0 && (
                        <div className={`italic transition-colors duration-300 ${
                          darkMode ? 'text-purple-400' : 'text-purple-500'
                        }`}>No TT calculations found</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Update History */}
              <div className={`mt-6 rounded-lg shadow overflow-hidden transition-colors duration-300 ${
                darkMode ? 'bg-gray-700' : 'bg-white'
              }`}>
                <div className={`px-4 py-3 border-b transition-colors duration-300 ${
                  darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <h4 className={`text-lg font-semibold transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-800'
                    }`}>Update History</h4>
                    {selectedOrder.updatedBy && selectedOrder.updatedBy.length > 0 && (
                      <span className={`text-sm px-2 py-1 rounded ${darkMode ? 'text-blue-300 bg-blue-900/50' : 'text-gray-600 bg-blue-100'}`}>
                        {selectedOrder.updatedBy.length} {selectedOrder.updatedBy.length === 1 ? 'Entry' : 'Entries'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  {selectedOrder.updatedBy && selectedOrder.updatedBy.length > 0 ? (
                    <div className="space-y-4">
                      {selectedOrder.updatedBy.map((update, index) => (
                        <div key={index} className={`border-l-4 pl-4 pb-3 border-b last:border-b-0 ${darkMode ? 'border-blue-600 border-b-gray-700' : 'border-blue-200 border-b-gray-100'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center">
                                <FaUser className={`w-4 h-4 mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                                <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                  {update.employeeName || update.employee?.name || update.employee?.email || 'Unknown User'}
                                </span>
                              </div>
                              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>•</span>
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {new Date(update.date).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {update.pastData && (
                                <button
                                  onClick={() => setViewingPastData({
                                    date: update.date,
                                    employee: { name: update.employeeName || update.employee?.name || 'Unknown' },
                                    pastData: update.pastData
                                  })}
                                  className={`text-xs px-2 py-1 rounded transition duration-150 ${darkMode ? 'bg-blue-900/50 hover:bg-blue-800/50 text-blue-300' : 'bg-blue-100 hover:bg-blue-200 text-blue-700'}`}
                                >
                                  View Data
                                </button>
                              )}
                            </div>
                          </div>
                          {update.pastData && (
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Previous order data available - click "View Data" to see details
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaFileAlt className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className="text-sm">No update history available</p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>This delivery order hasn't been modified since creation</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
            
          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                setViewingPastData(null);
                setShowViewModal(false);
              }}
              className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition duration-150 flex items-center"
            >
              <FaTimes className="w-4 h-4 mr-2" />
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${
      darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'
    }`}>
      <div className="w-full mx-auto space-y-6">
        
        {/* Form Section */}
        <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-8">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <FaCube className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {isEditing ? 'Edit Tiles Delivery Order' : 'Create Tiles Delivery Order'}
                </h1>
                <p className="text-white/90">Manage tiles delivery orders with stock tracking</p>
                {/* Keyboard shortcuts hint */}
                <div className="flex flex-wrap gap-2 mt-2 text-xs text-white/70">
                  <span className="bg-white/10 px-2 py-1 rounded">Ctrl+Enter: Submit</span>
                  <span className="bg-white/10 px-2 py-1 rounded">Insert: Add Tile</span>
                  <span className="bg-white/10 px-2 py-1 rounded">Shift+Insert: Add Stock Item</span>
                  {isEditing && <span className="bg-white/10 px-2 py-1 rounded">Esc: Cancel Edit</span>}
                </div>
              </div>
              {isEditing && (
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-all backdrop-blur-sm flex items-center gap-2"
                >
                  <FaTimes className="w-4 h-4" />
                  Cancel Edit (Esc)
                </button>
              )}
            </div>
          </div>

          <form ref={formRef} className="p-8 space-y-6">
            {/* Order Information Section */}
            <div className={`rounded-xl border-2 p-6 transition-colors duration-300 ${
              darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 transition-colors duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                <FaClipboardList className="text-indigo-600" />
                Order Information
              </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Enhanced Recipient Type */}
                  <div className="space-y-3 group">
                    <label className={`block text-sm font-bold mb-3 flex items-center transition-colors duration-300 ${
                      darkMode ? 'text-gray-200 group-hover:text-blue-400' : 'text-gray-800 group-hover:text-blue-700'
                    }`}>
                      <div className="w-6 h-6 mr-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <FaUser className="w-3 h-3 text-white" />
                      </div>
                      Recipient Type <span className="text-red-500 ml-2 animate-pulse">*</span>
                      {isEditing && <FaLock className="w-3 h-3 text-gray-400 ml-2" />}
                    </label>
                    <Select
                      value={form.recipientType ? { value: form.recipientType, label: form.recipientType } : null}
                      onChange={(selectedOption) => {
                        setForm(prev => ({ 
                          ...prev, 
                          recipientType: selectedOption?.value || '',
                          recipient: null 
                        }));
                      }}
                      options={[
                        { value: 'Customer', label: '👤 Customer' },
                        { value: 'Dealer', label: '🏢 Dealer' }
                      ]}
                      placeholder={isEditing ? "Cannot modify recipient type" : "Choose recipient type..."}
                      isRequired
                      isDisabled={isEditing}
                      styles={selectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>

                  {/* Enhanced Recipient Selection */}
                  <div className="space-y-3 group">
                    <label className={`block text-sm font-bold mb-3 flex items-center transition-colors duration-300 ${
                      darkMode ? 'text-gray-200 group-hover:text-green-400' : 'text-gray-800 group-hover:text-green-700'
                    }`}>
                      <div className="w-6 h-6 mr-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                        <FaUser className="w-3 h-3 text-white" />
                      </div>
                      Recipient <span className="text-red-500 ml-2 animate-pulse">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => !isEditing && setShowRecipientModal(true)}
                      disabled={isEditing}
                      className={`w-full p-5 border-2 rounded-2xl text-left transition-all duration-300 relative overflow-hidden ${
                        isEditing 
                          ? (darkMode 
                              ? 'border-gray-600 bg-gray-700 cursor-not-allowed opacity-60' 
                              : 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60')
                          : (darkMode
                              ? 'border-gray-600 bg-gradient-to-r from-gray-700 via-gray-750 to-gray-700 hover:from-green-900/30 hover:via-gray-700 hover:to-blue-900/30 hover:border-green-500 focus:ring-4 focus:ring-green-500/20 focus:border-green-500 group-hover:shadow-lg'
                              : 'border-gray-200 bg-gradient-to-r from-white via-gray-50 to-white hover:from-green-50 hover:via-white hover:to-blue-50 hover:border-green-300 focus:ring-4 focus:ring-green-500/20 focus:border-green-500 group-hover:shadow-lg')
                      }`}
                    >
                        {/* Button Background Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      <div className="flex items-center justify-between relative z-10">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${form.recipient ? 'bg-green-100' : 'bg-gray-100'}`}>
                            {isEditing ? (
                              <FaLock className="w-4 h-4 text-gray-400" />
                            ) : form.recipient ? (
                              <FaCheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <FaUser className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <span className={`font-semibold transition-colors duration-300 ${
                            form.recipient 
                              ? (darkMode ? 'text-gray-100' : 'text-gray-900')
                              : (darkMode ? 'text-gray-400' : 'text-gray-500')
                          }`}>
                            {isEditing 
                              ? (form.recipient ? `${form.recipient.label} (Locked)` : "Cannot modify recipient")
                              : (form.recipient 
                                  ? form.recipient.label 
                                  : `Select ${form.recipientType || 'Recipient'}`
                                )}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {form.recipient && (
                            <div className={`px-2 py-1 rounded-lg text-xs font-bold ${
                              darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                            }`}>
                              Selected
                            </div>
                          )}
                          <svg className={`w-6 h-6 transition-colors ${
                            darkMode ? 'text-gray-400 group-hover:text-blue-400' : 'text-gray-400 group-hover:text-blue-500'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  </div>

                </div>
              </div>

              {/* Enhanced Items Section */}
              <div className={`rounded-xl shadow-md border p-6 hover:shadow-lg transition-all duration-300 ${
                darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'
              }`}>
                <div className={`flex items-center justify-between mb-6 pb-4 border-b transition-colors duration-300 ${
                  darkMode ? 'border-gray-600' : 'border-gray-100'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg">
                      <FaBox className="w-4 h-4 text-white" />
                    </div>
                    <h2 className={`text-xl font-bold transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Tiles Items</h2>
                    <div className={`px-3 py-1 rounded-full ${
                      darkMode ? 'bg-green-900/30' : 'bg-green-50'
                    }`}>
                      <span className={`text-xs font-medium ${
                        darkMode ? 'text-green-400' : 'text-green-600'
                      }`}>Step 2 of 4</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isEditing && form.recipient?.value && (
                      <button
                        type="button"
                        onClick={fetchPendingSalesOrders}
                        className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg hover:from-amber-600 hover:to-orange-700 flex items-center space-x-2 shadow-md hover:shadow-lg transition-all duration-200 group"
                      >
                        <FaShoppingCart className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        <span className="font-medium">Import from SO</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={addItem}
                      className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center space-x-2 shadow-md hover:shadow-lg transition-all duration-200 group"
                    >
                      <FaPlus className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="font-medium">Add Item</span>
                    </button>
                    <button
                      type="button"
                      onClick={pasteItem}
                      disabled={!copiedItem}
                      className={`px-4 py-2.5 rounded-lg flex items-center space-x-2 transition-all duration-200 border disabled:opacity-40 ${
                        darkMode ? 'border-teal-700 text-teal-300 hover:bg-teal-900/30' : 'border-teal-300 text-teal-700 hover:bg-teal-50'
                      }`}
                    >
                      <FaPaste className="w-4 h-4" />
                      <span className="font-medium">Paste Item</span>
                    </button>
                  </div>
                </div>

                {/* Enhanced Items List */}
                <div className="space-y-4">
                  {form.items?.map((item, index) => (
                    <div key={index} className={`border-2 rounded-xl p-6 transition-all duration-300 shadow-sm hover:shadow-md ${
                      darkMode 
                        ? 'border-gray-600 bg-gradient-to-r from-gray-700/50 to-gray-700 hover:from-blue-900/20 hover:to-gray-700 hover:border-blue-500' 
                        : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white hover:border-blue-200'
                    }`}>
                      <div className={`flex items-center justify-between mb-4 pb-3 border-b transition-colors duration-300 ${
                        darkMode ? 'border-gray-600' : 'border-gray-200'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white font-bold text-sm">
                            {index + 1}
                          </div>
                          <h3 className={`text-lg font-bold transition-colors duration-300 ${
                            darkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>Item {index + 1}</h3>
                          {item.Name && (
                            <div className={`px-3 py-1 rounded-full ${
                              darkMode ? 'bg-green-900/30' : 'bg-green-100'
                            }`}>
                              <span className={`text-xs font-medium ${
                                darkMode ? 'text-green-400' : 'text-green-700'
                              }`}>✓ Selected</span>
                            </div>
                          )}
                        </div>
                        {form.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className={`p-2 text-red-500 hover:text-red-700 rounded-lg transition-all duration-200 ${
                              darkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-100'
                            }`}
                            title="Remove this item"
                          >
                            <FaTimes className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => copyItem(index)}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            darkMode ? 'text-violet-400 hover:bg-violet-900/30' : 'text-violet-600 hover:bg-violet-100'
                          }`}
                          title="Copy this item"
                        >
                          <FaCopy className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1">
                        {/* Item Location Selection */}
                        <div className="md:col-span-1">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaMapMarkerAlt className="w-4 h-4 text-orange-600" />
                            <span>Location {!item.location && <span className="text-red-500">*</span>}</span>
                          </label>
                          <Select
                            ref={(el) => { itemLocationSelectRefs.current[index] = el; }}
                            value={locations?.find(l => l._id === (item.location || form.location)) 
                              ? { value: item.location || form.location, label: locations.find(l => l._id === (item.location || form.location))?.name }
                              : null
                            }
                            onChange={(selected) => {
                              updateItem(index, 'location', selected?.value || null);
                            }}
                            options={locations?.map(location => ({
                              value: location._id,
                              label: location.name
                            })) || []}
                            placeholder="Select location..."
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: '44px',
                                backgroundColor: darkMode ? '#374151' : '#fff',
                                borderColor: !item.location ? (darkMode ? '#EF4444' : '#EF4444') : (darkMode ? '#4B5563' : '#D1D5DB'),
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color: darkMode ? '#F9FAFB' : '#111827',
                              }),
                              input: (base) => ({
                                ...base,
                                color: darkMode ? '#F9FAFB' : '#111827',
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color: darkMode ? '#9CA3AF' : '#6B7280',
                              }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: darkMode ? '#374151' : '#fff',
                                zIndex: 9999,
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused 
                                  ? (darkMode ? '#4B5563' : '#E5E7EB')
                                  : (darkMode ? '#374151' : '#fff'),
                                color: darkMode ? '#F9FAFB' : '#111827',
                              }),
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>

                        {/* Tile Selection */}
                        <div className="md:col-span-1 lg:col-span-2 xl:col-span-3">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaCube className="w-4 h-4 text-purple-600" />
                            <span>Tile Selection</span>
                            <span className="text-red-500">*</span>
                            {item.Name && (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                              }`}>
                                ✓ Selected
                              </span>
                            )}
                          </label>
                          <button
                            type="button"
                            ref={index === 0 ? firstTileSelectButtonRef : null}
                            onClick={() => {
                              const itemLocation = item.location || form.location;
                              if (!itemLocation) {
                                Swal.fire({
                                  icon: 'info',
                                  title: 'Info',
                                  text: 'Please select a location first',
                                  customClass: { popup: 'swal-dark-mode' }
                                });
                                return;
                              }
                              setCurrentItemIndex(index);
                              setShowTilesModal(true);
                            }}
                            className={`w-full px-4 py-3 border-2 border-dashed rounded-lg transition-all duration-200 ${
                              item.Name 
                                ? (darkMode 
                                    ? 'border-green-500 bg-green-900/20 text-green-300 hover:bg-green-900/30' 
                                    : 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100')
                                : (darkMode
                                    ? 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-blue-500 hover:bg-blue-900/20'
                                    : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-blue-400 hover:bg-blue-50')
                            }`}
                          >
                            {item.Name ? (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <FaCheckCircle className="w-4 h-4 text-green-600" />
                                  <span className="font-medium">{item.Name}</span>
                                </div>
                                <span className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Click to change</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center space-x-2">
                                <FaSearch className="w-4 h-4" />
                                <span>Select Tile</span>
                              </div>
                            )}
                          </button>
                        </div>

                        {/* Stock Information Display */}
                        {item.availableStock && item.tileId && item.Name && (
                          <div className={`md:col-span-2 lg:col-span-3 xl:col-span-4 border rounded-lg p-3 mb-4 transition-colors duration-300 ${
                            darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
                          }`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <FaWarehouse className="w-4 h-4 text-blue-600" />
                                <h4 className={`text-sm font-bold ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                                  {isEditing ? 'Available Stock (order reversed)' : 'Current Stock'}
                                </h4>
                              </div>
                              {((item.tileDetails?.inTransitBoxes || 0) > 0 || (item.tileDetails?.boxesOrdered || 0) > 0) && (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                  Total Expected: {(item.availableStock?.freshStockBox || 0) + (item.tileDetails?.inTransitBoxes || 0) + (item.tileDetails?.boxesOrdered || 0)} boxes
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                              <div className={`rounded p-2 transition-colors duration-300 ${
                                darkMode ? 'bg-gray-700' : 'bg-white'
                              }`}>
                                <div className="flex items-center space-x-1 mb-1">
                                  <FaBox className="w-3 h-3 text-green-600" />
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Fresh Boxes:</span>
                                </div>
                                <span className="font-bold text-green-600">{item.availableStock?.freshStockBox || 0}</span>
                              </div>
                              <div className={`rounded p-2 transition-colors duration-300 ${
                                darkMode ? 'bg-gray-700' : 'bg-white'
                              }`}>
                                <div className="flex items-center space-x-1 mb-1">
                                  <FaCube className="w-3 h-3 text-blue-600" />
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Fresh Pieces:</span>
                                </div>
                                <span className="font-bold text-blue-600">{item.availableStock?.freshStockPieces || 0}</span>
                              </div>
                              <div className={`rounded p-2 transition-colors duration-300 ${
                                darkMode ? 'bg-gray-700' : 'bg-white'
                              }`}>
                                <div className="flex items-center space-x-1 mb-1">
                                  <FaExclamationTriangle className="w-3 h-3 text-yellow-600" />
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Corner Dmg Boxes:</span>
                                </div>
                                <span className="font-bold text-yellow-600">{item.availableStock?.cornerDamagedBox || 0}</span>
                              </div>
                              <div className={`rounded p-2 transition-colors duration-300 ${
                                darkMode ? 'bg-gray-700' : 'bg-white'
                              }`}>
                                <div className="flex items-center space-x-1 mb-1">
                                  <FaBan className="w-3 h-3 text-yellow-600" />
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Corner Dmg Pieces:</span>
                                </div>
                                <span className="font-bold text-yellow-600">{item.availableStock?.cornerDamagedPieces || 0}</span>
                              </div>
                            </div>
                            {/* In Transit & On Order */}
                            {((item.tileDetails?.inTransitBoxes || 0) > 0 || (item.tileDetails?.boxesOrdered || 0) > 0) && (
                              <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-blue-700' : 'border-blue-200'}`}>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                  {(item.tileDetails?.inTransitBoxes || 0) > 0 && (
                                    <div className={`rounded p-2 ${darkMode ? 'bg-yellow-900/30 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                                      <div className="flex items-center space-x-1 mb-1">
                                        <FaTruck className="w-3 h-3 text-yellow-600" />
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>In Transit:</span>
                                      </div>
                                      <span className={`font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{item.tileDetails.inTransitBoxes} boxes</span>
                                    </div>
                                  )}
                                  {(item.tileDetails?.boxesOrdered || 0) > 0 && (
                                    <div className={`rounded p-2 ${darkMode ? 'bg-purple-900/30 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
                                      <div className="flex items-center space-x-1 mb-1">
                                        <FaClipboardList className="w-3 h-3 text-purple-600" />
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>On Order:</span>
                                      </div>
                                      <span className={`font-bold ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>{item.tileDetails.boxesOrdered} boxes</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Quantity Inputs */}
                        <div className="group">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaBox className="w-4 h-4 text-green-600" />
                            <span>Fresh Boxes</span>
                          </label>
                          <input
                            ref={(el) => {
                              if (!quantityInputRefs.current[index]) quantityInputRefs.current[index] = {};
                              quantityInputRefs.current[index].freshBoxes = el;
                            }}
                            type="number"
                            min="0"
                            disabled={!item.tileId || (((item.availableStock?.freshStockBox || 0) + (item.tileDetails?.inTransitBoxes || 0)) === 0)}
                            max={(item.availableStock?.freshStockBox || 0) + (item.tileDetails?.inTransitBoxes || 0)}
                            value={item.freshBoxes || 0}
                            onChange={(e) => updateItem(index, 'freshBoxes', e.target.value)}
                            className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="0"
                          />
                          {(((item.availableStock?.freshStockBox || 0) + (item.tileDetails?.inTransitBoxes || 0)) !== 0) && (
                            <div className={`text-xs mt-1 transition-colors duration-300 ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Max: {(item.availableStock?.freshStockBox || 0) + (item.tileDetails?.inTransitBoxes || 0)}
                              {(item.tileDetails?.inTransitBoxes || 0) > 0 && (
                                <span className={darkMode ? 'text-yellow-400' : 'text-yellow-600'}> (incl. {item.tileDetails.inTransitBoxes} in-transit)</span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="group">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaCube className="w-4 h-4 text-blue-600" />
                            <span>Fresh Pieces</span>
                          </label>
                          <input
                            ref={(el) => {
                              if (!quantityInputRefs.current[index]) quantityInputRefs.current[index] = {};
                              quantityInputRefs.current[index].freshPieces = el;
                            }}
                            type="number"
                            min="0"
                            max={item.availableStock?.freshStockPieces || 0}
                            disabled={!item.tileId || (item.availableStock?.freshStockPieces === 0)}
                            value={item.freshPieces || 0}
                            onChange={(e) => updateItem(index, 'freshPieces', e.target.value)}
                            className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="0"
                          />
                          {(item.availableStock?.freshStockPieces !== 0) && (
                            <div className={`text-xs mt-1 transition-colors duration-300 ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Max: {item.availableStock.freshStockPieces}
                            </div>
                          )}
                        </div>

                        <div className="group">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaExclamationTriangle className="w-4 h-4 text-yellow-600" />
                            <span>Corner Dmg Boxes</span>
                          </label>
                          <input
                            ref={(el) => {
                              if (!quantityInputRefs.current[index]) quantityInputRefs.current[index] = {};
                              quantityInputRefs.current[index].damagedBoxes = el;
                            }}
                            type="number"
                            min="0"
                            max={item.availableStock?.cornerDamagedBox || 0}
                            disabled={!item.tileId || (item.availableStock?.cornerDamagedBox === 0)}
                            value={item.damagedBoxes || 0}
                            onChange={(e) => updateItem(index, 'damagedBoxes', e.target.value)}
                            className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="0"
                          />
                          {(item.availableStock?.cornerDamagedBox !== 0) && (
                            <div className={`text-xs mt-1 transition-colors duration-300 ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Max: {item.availableStock.cornerDamagedBox}
                            </div>
                          )}
                        </div>

                        <div className="group">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaBan className="w-4 h-4 text-yellow-600" />
                            <span>Corner Dmg Pieces</span>
                          </label>
                          <input
                            ref={(el) => {
                              if (!quantityInputRefs.current[index]) quantityInputRefs.current[index] = {};
                              quantityInputRefs.current[index].damagedPieces = el;
                            }}
                            type="number"
                            min="0"
                            max={item.availableStock?.cornerDamagedPieces || 0}
                            disabled={!item.tileId || (item.availableStock?.cornerDamagedPieces === 0)}
                            value={item.damagedPieces || 0}
                            onChange={(e) => updateItem(index, 'damagedPieces', e.target.value)}
                            className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="0"
                          />
                          {(item.availableStock?.cornerDamagedPieces !== 0) && (
                            <div className={`text-xs mt-1 transition-colors duration-300 ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Max: {item.availableStock.cornerDamagedPieces}
                            </div>
                          )}
                        </div>

                        {/* Rate and Discount */}
                        <div className="group">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 mt-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaCalculator className="w-4 h-4 text-yellow-600" />
                            <span>Rate per Box</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.rate || 0}
                            onChange={(e) => updateItem(index, 'rate', e.target.value)}
                            className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="0.00"
                          />
                        </div>

                        <div className="group">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 mt-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaInfoCircle className="w-4 h-4 text-purple-600" />
                            <span>Discount (%)</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={item.discount || 0}
                            onChange={(e) => updateItem(index, 'discount', e.target.value)}
                            className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                            }`}
                            placeholder="0.00"
                          />
                          <div className={`text-xs mt-1 transition-colors duration-300 ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Max: 100%
                          </div>
                        </div>

                        {/* TT Configuration - Only show when reference is selected */}
                        {form.reference && (
                          <div className="md:col-span-2 mt-4">
                            <div className={`border rounded p-3 transition-colors duration-300 ${
                              darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
                            }`}>
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2">
                                    <svg className={`w-4 h-4 transition-colors duration-300 ${
                                      darkMode ? 'text-indigo-400' : 'text-indigo-600'
                                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <label className={`text-sm font-medium transition-colors duration-300 ${
                                      darkMode ? 'text-indigo-300' : 'text-indigo-800'
                                    }`}>TT:</label>
                                  </div>
                                  
                                  <Select
                                    value={item.tt?.type ? { value: item.tt.type, label: item.tt.type === 'percentage' ? '%' : '₹' } : { value: 'percentage', label: '%' }}
                                    onChange={(selectedOption) => {
                                      updateItem(index, 'tt', { 
                                        ...item.tt, 
                                        type: selectedOption?.value || 'percentage',
                                        value: item.tt?.value || 0,
                                        calculatedAmount: item.tt?.calculatedAmount || 0
                                      });
                                    }}
                                    options={[
                                      { value: 'percentage', label: '%' },
                                      { value: 'fixed', label: '₹' }
                                    ]}
                                    isSearchable={false}
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: '28px',
                                        fontSize: '12px',
                                        backgroundColor: darkMode ? '#374151' : '#fff',
                                        borderColor: darkMode ? '#4B5563' : '#e5e7eb',
                                      }),
                                      valueContainer: (base) => ({
                                        ...base,
                                        padding: '0 6px'
                                      }),
                                      singleValue: (base) => ({
                                        ...base,
                                        color: darkMode ? '#F9FAFB' : '#111827',
                                      }),
                                      menu: (base) => ({
                                        ...base,
                                        backgroundColor: darkMode ? '#374151' : '#fff',
                                        zIndex: 9999,
                                      }),
                                      option: (base, state) => ({
                                        ...base,
                                        fontSize: '12px',
                                        backgroundColor: state.isFocused 
                                          ? (darkMode ? '#4B5563' : '#E5E7EB')
                                          : (darkMode ? '#374151' : '#fff'),
                                        color: darkMode ? '#F9FAFB' : '#111827',
                                      }),
                                      dropdownIndicator: (base) => ({
                                        ...base,
                                        padding: '2px',
                                        color: darkMode ? '#9CA3AF' : '#6B7280',
                                      }),
                                      indicatorSeparator: () => ({ display: 'none' }),
                                    }}
                                    className="w-16"
                                  />
                                  
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={item.tt?.type === 'percentage' ? 100 : undefined}
                                    value={item.tt?.value || 0}
                                    onChange={(e) => {
                                      updateItem(index, 'tt', { 
                                        ...item.tt, 
                                        type: item.tt?.type || 'percentage',
                                        value: parseFloat(e.target.value) || 0,
                                        calculatedAmount: item.tt?.calculatedAmount || 0
                                      });
                                    }}
                                    placeholder={item.tt?.type === 'percentage' ? '0.00' : '0.00'}
                                    className={`w-16 px-2 py-1 border rounded text-xs text-center focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
                                      darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white' 
                                        : 'bg-white border-gray-200 text-gray-900'
                                    }`}
                                  />
                                </div>
                                
                                <div className="text-right">
                                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>TT Amount</div>
                                  <div className={`text-sm font-semibold ${darkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>
                                    ₹{formatNumberWithCommas(item.tt?.calculatedAmount || 0)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Amount Display */}
                        <div className="md:col-span-2 mt-4">
                          <div className={`rounded-lg p-4 border transition-colors duration-300 ${
                            darkMode 
                              ? 'bg-gradient-to-r from-gray-800 to-gray-800 border-gray-700' 
                              : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <FaCalculator className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                                <span className={`text-sm font-bold transition-colors duration-300 ${
                                  darkMode ? 'text-gray-200' : 'text-gray-700'
                                }`}>Total Item Amount</span>
                              </div>
                              <div className="text-right">
                                <div className={`text-lg font-bold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                                  ₹{formatNumberWithCommas(item.amountAfterDiscount || 0)}
                                </div>
                                {item.discount > 0 && (
                                  <div className={`text-xs transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className={`line-through ${darkMode ? 'text-gray-500' : ''}`}>₹{formatNumberWithCommas(item.amountBeforeDiscount || 0)}</span>
                                    <span className="ml-2 text-green-500">({item.discount}% off)</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stock Items Section */}
              <div className={`rounded-xl shadow-md border p-6 hover:shadow-lg transition-all duration-300 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className={`flex items-center justify-between mb-6 pb-4 border-b transition-colors duration-300 ${
                  darkMode ? 'border-gray-700' : 'border-gray-100'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg">
                      <FaBoxes className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-xl font-bold transition-colors duration-300 ${
                        darkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>Additional Stock Items</h2>
                      <p className={`text-xs transition-colors duration-300 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>Add other stock items like chemicals, adhesives, etc.</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full transition-colors duration-300 ${
                      darkMode ? 'bg-purple-900/30' : 'bg-purple-50'
                    }`}>
                      <span className={`text-xs font-medium transition-colors duration-300 ${
                        darkMode ? 'text-purple-300' : 'text-purple-600'
                      }`}>Optional</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addStockItem}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 ${
                      darkMode 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                        : 'bg-purple-500 hover:bg-purple-600 text-white'
                    }`}
                  >
                    <FaPlus className="w-4 h-4" />
                    <span>Add Stock Item</span>
                  </button>
                  <button
                    type="button"
                    onClick={pasteStockItem}
                    disabled={!copiedStockItem}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 border disabled:opacity-40 ${
                      darkMode 
                        ? 'border-teal-700 text-teal-300 hover:bg-teal-900/30' 
                        : 'border-teal-300 text-teal-700 hover:bg-teal-50'
                    }`}
                  >
                    <FaPaste className="w-4 h-4" />
                    <span>Paste Stock Item</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {form.stockItems && form.stockItems.length > 0 ? form.stockItems.map((stockItem, index) => (
                    <div 
                      key={index} 
                      className={`p-4 border-2 rounded-xl transition-all duration-300 ${
                        darkMode 
                          ? 'bg-gray-700/50 border-purple-700 hover:border-purple-500' 
                          : 'bg-purple-50/30 border-purple-200 hover:border-purple-400'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                            <FaBoxes className="w-5 h-5 text-white" />
                          </div>
                          <h3 className={`font-semibold transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>Stock Item #{index + 1}</h3>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeStockItem(index)}
                          disabled={form.stockItems.length === 1}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            form.stockItems.length === 1
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-red-100 text-red-600 hover:scale-110'
                          }`}
                        >
                          <FaMinus className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => copyStockItem(index)}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            darkMode ? 'text-violet-400 hover:bg-violet-900/30' : 'text-violet-600 hover:bg-violet-100'
                          }`}
                          title="Copy this stock item"
                        >
                          <FaCopy className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Stock Item Location Selection */}
                        <div className="md:col-span-1">
                          <label className={`flex items-center space-x-2 text-sm font-bold mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaMapMarkerAlt className="w-4 h-4 text-purple-600" />
                            <span>Location {!stockItem.location && <span className="text-red-500">*</span>}</span>
                          </label>
                          <Select
                            ref={(el) => { stockItemLocationSelectRefs.current[index] = el; }}
                            value={locations?.find(l => l._id === (stockItem.location || form.location)) 
                              ? { value: stockItem.location || form.location, label: locations.find(l => l._id === (stockItem.location || form.location))?.name }
                              : null
                            }
                            onChange={(selected) => {
                              updateStockItem(index, 'location', selected?.value || null);
                            }}
                            options={locations?.map(location => ({
                              value: location._id,
                              label: location.name
                            })) || []}
                            placeholder="Select location..."
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: '44px',
                                backgroundColor: darkMode ? '#374151' : '#fff',
                                borderColor: !stockItem.location ? (darkMode ? '#EF4444' : '#EF4444') : (darkMode ? '#4B5563' : '#D1D5DB'),
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color: darkMode ? '#F9FAFB' : '#111827',
                              }),
                              input: (base) => ({
                                ...base,
                                color: darkMode ? '#F9FAFB' : '#111827',
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color: darkMode ? '#9CA3AF' : '#6B7280',
                              }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: darkMode ? '#374151' : '#fff',
                                zIndex: 9999,
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused 
                                  ? (darkMode ? '#4B5563' : '#E5E7EB')
                                  : (darkMode ? '#374151' : '#fff'),
                                color: darkMode ? '#F9FAFB' : '#111827',
                              }),
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>

                        {/* Stock Item Selection */}
                        <div className="md:col-span-1">
                          <label className={`flex items-center space-x-2 mb-2 text-sm font-bold transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>
                            <FaBoxes className="text-purple-600" />
                            <span>Stock Item {!stockItem.stockItemId && <span className="text-red-500">*</span>}</span>
                            {stockItem.stockItemId && (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                              }`}>
                                ✓ Selected
                              </span>
                            )}
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const itemLocation = stockItem.location || form.location;
                              if (!itemLocation) {
                                Swal.fire({
                                  icon: 'info',
                                  title: 'Info',
                                  text: 'Please select a location first',
                                  customClass: { popup: 'swal-dark-mode' }
                                });
                                return;
                              }
                              setCurrentStockItemIndex(index);
                              setShowStockItemsModal(true);
                            }}
                            className={`w-full px-4 py-3 border-2 border-dashed rounded-lg transition-all duration-200 ${
                              stockItem.name 
                                ? (darkMode 
                                    ? 'border-purple-500 bg-purple-900/20 text-purple-300 hover:bg-purple-900/30' 
                                    : 'border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100')
                                : (darkMode
                                    ? 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-purple-500 hover:bg-purple-900/20'
                                    : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-purple-400 hover:bg-purple-50')
                            }`}
                          >
                            {stockItem.name ? (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <FaCheckCircle className="w-4 h-4 text-purple-600" />
                                  <span className="font-medium">{stockItem.name}</span>
                                </div>
                                <span className={`text-xs ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>Click to change</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center space-x-2">
                                <FaSearch className="w-4 h-4" />
                                <span>Select Stock Item</span>
                              </div>
                            )}
                          </button>
                        </div>

                        {/* Stock Information Display */}
                        {stockItem.stockItemId && stockItem.name && (
                          <div className={`md:col-span-2 border rounded-lg p-3 mb-4 transition-colors duration-300 ${
                            darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'
                          }`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <FaWarehouse className="w-4 h-4 text-purple-600" />
                                <h4 className={`text-sm font-bold ${darkMode ? 'text-purple-300' : 'text-purple-900'}`}>
                                  {isEditing ? 'Available Stock (order reversed)' : 'Available Stock'}
                                </h4>
                              </div>
                              {((stockItem.stockItemDetails?.inTransitQuantity || 0) > 0 || (stockItem.stockItemDetails?.quantityOrdered || 0) > 0) && (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-800 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                  Total Expected: {(stockItem.availableStock?.freshQuantity || 0) + (stockItem.stockItemDetails?.inTransitQuantity || 0) + (stockItem.stockItemDetails?.quantityOrdered || 0)} units
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className={`rounded p-2 transition-colors duration-300 ${
                                darkMode ? 'bg-gray-700' : 'bg-white'
                              }`}>
                                <div className="flex items-center space-x-1 mb-1">
                                  <FaBox className="w-3 h-3 text-green-600" />
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Fresh Qty:</span>
                                </div>
                                <div className={`font-bold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                                  {stockItem.availableStock?.freshQuantity ?? 0}
                                </div>
                              </div>
                              <div className={`rounded p-2 transition-colors duration-300 ${
                                darkMode ? 'bg-gray-700' : 'bg-white'
                              }`}>
                                <div className="flex items-center space-x-1 mb-1">
                                  <FaExclamationTriangle className="w-3 h-3 text-orange-600" />
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Damaged Qty:</span>
                                </div>
                                <div className={`font-bold ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                                  {stockItem.availableStock?.damagedQuantity ?? 0}
                                </div>
                              </div>
                            </div>
                            {/* In Transit & On Order */}
                            {((stockItem.stockItemDetails?.inTransitQuantity || 0) > 0 || (stockItem.stockItemDetails?.quantityOrdered || 0) > 0) && (
                              <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-purple-700' : 'border-purple-200'}`}>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  {(stockItem.stockItemDetails?.inTransitQuantity || 0) > 0 && (
                                    <div className={`rounded p-2 ${darkMode ? 'bg-yellow-900/30 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                                      <div className="flex items-center space-x-1 mb-1">
                                        <FaTruck className="w-3 h-3 text-yellow-600" />
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>In Transit:</span>
                                      </div>
                                      <span className={`font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{stockItem.stockItemDetails.inTransitQuantity} units</span>
                                    </div>
                                  )}
                                  {(stockItem.stockItemDetails?.quantityOrdered || 0) > 0 && (
                                    <div className={`rounded p-2 ${darkMode ? 'bg-purple-900/30 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
                                      <div className="flex items-center space-x-1 mb-1">
                                        <FaClipboardList className="w-3 h-3 text-purple-600" />
                                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>On Order:</span>
                                      </div>
                                      <span className={`font-bold ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>{stockItem.stockItemDetails.quantityOrdered} units</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Fresh Quantity */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>Fresh Quantity</label>
                          <input
                            ref={(el) => {
                              if (!stockItemQuantityInputRefs.current[index]) stockItemQuantityInputRefs.current[index] = {};
                              stockItemQuantityInputRefs.current[index].freshQuantity = el;
                            }}
                            type="number"
                            step="any"
                            min="0"
                            max={(stockItem.availableStock?.freshQuantity || 0) + (stockItem.stockItemDetails?.inTransitQuantity || 0)}
                            disabled={!stockItem.stockItemId || (((stockItem.availableStock?.freshQuantity || 0) + (stockItem.stockItemDetails?.inTransitQuantity || 0)) === 0)}
                            value={stockItem.freshQuantity || 0}
                            onChange={(e) => updateStockItem(index, 'freshQuantity', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                          {(((stockItem.availableStock?.freshQuantity || 0) + (stockItem.stockItemDetails?.inTransitQuantity || 0)) !== 0) && (
                            <div className={`text-xs mt-1 transition-colors duration-300 ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Max: {(stockItem.availableStock?.freshQuantity || 0) + (stockItem.stockItemDetails?.inTransitQuantity || 0)}
                              {(stockItem.stockItemDetails?.inTransitQuantity || 0) > 0 && (
                                <span className={darkMode ? 'text-yellow-400' : 'text-yellow-600'}> (incl. {stockItem.stockItemDetails.inTransitQuantity} in-transit)</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Damaged Quantity */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>Damaged Quantity</label>
                          <input
                            ref={(el) => {
                              if (!stockItemQuantityInputRefs.current[index]) stockItemQuantityInputRefs.current[index] = {};
                              stockItemQuantityInputRefs.current[index].damagedQuantity = el;
                            }}
                            type="number"
                            step="any"
                            min="0"
                            max={stockItem.availableStock?.damagedQuantity || 0}
                            disabled={!stockItem.stockItemId || ((stockItem.availableStock?.damagedQuantity || 0) === 0)}
                            value={stockItem.damagedQuantity || 0}
                            onChange={(e) => updateStockItem(index, 'damagedQuantity', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                          {((stockItem.availableStock?.damagedQuantity || 0) !== 0) && (
                            <div className={`text-xs mt-1 transition-colors duration-300 ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Max: {stockItem.availableStock?.damagedQuantity || 0}
                            </div>
                          )}
                        </div>

                        {/* Rate */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>Rate (₹)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={stockItem.rate || 0}
                            onChange={(e) => updateStockItem(index, 'rate', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        {/* Discount */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>Discount (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={stockItem.discount || 0}
                            onChange={(e) => updateStockItem(index, 'discount', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        {/* Amount Display */}
                        <div className="md:col-span-2 mt-4">
                          <div className={`rounded-lg p-4 border transition-colors duration-300 ${
                            darkMode 
                              ? 'bg-gradient-to-r from-gray-800 to-gray-800 border-gray-700' 
                              : 'bg-gradient-to-r from-purple-50 to-purple-50 border-purple-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <FaCalculator className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                                <span className={`text-sm font-bold transition-colors duration-300 ${
                                  darkMode ? 'text-gray-200' : 'text-gray-700'
                                }`}>Total Stock Item Amount</span>
                              </div>
                              <div className="text-right">
                                <div className={`text-lg font-bold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                                  ₹{formatNumberWithCommas(stockItem.amountAfterDiscount || 0)}
                                </div>
                                {stockItem.discount > 0 && (
                                  <div className={`text-xs transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className={`line-through ${darkMode ? 'text-gray-500' : ''}`}>₹{formatNumberWithCommas(stockItem.amountBeforeDiscount || 0)}</span>
                                    <span className="ml-2 text-purple-500">({stockItem.discount}% off)</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaBoxes className="mx-auto h-12 w-12 mb-2" />
                      <p>No stock items added yet. Click "Add Stock Item" to get started.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Additional Details Section */}
              <div className={`rounded-xl shadow-md border p-6 hover:shadow-lg transition-all duration-300 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className={`flex items-center justify-between mb-6 pb-4 border-b transition-colors duration-300 ${
                  darkMode ? 'border-gray-700' : 'border-gray-100'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg">
                      <FaTruck className="w-4 h-4 text-white" />
                    </div>
                    <h2 className={`text-xl font-bold transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Additional Details</h2>
                    <div className={`px-3 py-1 rounded-full transition-colors duration-300 ${
                      darkMode ? 'bg-purple-900/30' : 'bg-purple-50'
                    }`}>
                      <span className={`text-xs font-medium transition-colors duration-300 ${
                        darkMode ? 'text-purple-300' : 'text-purple-600'
                      }`}>Step 3 of 4</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="group space-y-3">
                    <label className={`flex items-center space-x-2 text-sm font-bold transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                        <FaTruck className="w-3 h-3 text-white" />
                      </div>
                      <span>Vehicle Number</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={form.vehicleNumber}
                        onChange={(e) => setForm(prev => ({ ...prev, vehicleNumber: e.target.value }))}
                        placeholder="Enter vehicle number..."
                        className={`w-full px-4 py-3 pl-12 border-2 rounded-xl backdrop-blur-sm focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 group-hover:border-blue-300 ${
                          darkMode 
                            ? 'bg-gray-700/80 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-500">
                        <FaTruck className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Transporter</label>
                    <button
                      type="button"
                      onClick={() => setShowTransporterModal(true)}
                      className={`w-full px-3 py-2 border rounded-md text-left transition-colors ${
                        darkMode 
                          ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-100 focus:ring-2 focus:ring-blue-500' 
                          : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`truncate pr-2 ${form.transporterName ? '' : (darkMode ? 'text-gray-400' : 'text-gray-400')}`}>
                          {form.transporterName
                            ? (form.display?.transporterDisplayName || transporters?.find(t => t._id === form.transporterName)?.name || 'Selected')
                            : 'Select transporter...'
                          }
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {form.transporterName && (
                            <span
                              onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, transporterName: '', display: { ...prev.display, transporterDisplayName: '' } })); }}
                              className="text-gray-400 hover:text-red-500 cursor-pointer p-0.5"
                            >
                              ×
                            </span>
                          )}
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Reference</label>
                    <button
                      type="button"
                      onClick={() => setShowReferenceModal(true)}
                      className={`w-full px-3 py-2 border rounded-md text-left transition-colors ${
                        darkMode 
                          ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-100 focus:ring-2 focus:ring-blue-500' 
                          : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`truncate pr-2 ${form.reference ? '' : (darkMode ? 'text-gray-400' : 'text-gray-400')}`}>
                          {form.reference
                            ? (form.display?.referenceName || references?.find(r => r._id === form.reference)?.name || 'Selected')
                            : 'Select reference...'
                          }
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {form.reference && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setForm(prev => {
                                  // Reset TT fields when reference is cleared
                                  if (prev.items?.length > 0) {
                                    const updatedItems = prev.items.map(item => ({
                                      ...item,
                                      tt: { type: 'percentage', value: 0, calculatedAmount: 0 }
                                    }));
                                    return { ...prev, reference: '', display: { ...prev.display, referenceName: '' }, items: updatedItems, totalTTAmount: 0 };
                                  }
                                  return { ...prev, reference: '', display: { ...prev.display, referenceName: '' }, totalTTAmount: 0 };
                                });
                                setTimeout(() => calculateTotals(), 100);
                              }}
                              className="text-gray-400 hover:text-red-500 cursor-pointer p-0.5"
                            >
                              ×
                            </span>
                          )}
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Mode of Sales, Manual DO Number & PO Number */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Mode of Sales
                    </label>
                    <Select
                      value={{ value: form.modeOfSales, label: form.modeOfSales }}
                      onChange={(selectedOption) => setForm(prev => ({ ...prev, modeOfSales: selectedOption.value }))}
                      options={[
                        { value: 'Paid Instantly', label: 'Paid Instantly' },
                        { value: 'Credit', label: 'Credit' },
                        { value: 'Full Advance', label: 'Full Advance' },
                        { value: 'Partial Advance', label: 'Partial Advance' }
                      ]}
                      styles={selectStyles}
                      placeholder="Select Mode of Sales"
                      isSearchable={false}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Manual DO Number
                    </label>
                    <input
                      type="text"
                      value={form.Manual_dO_Number}
                      onChange={(e) => setForm(prev => ({ ...prev, Manual_dO_Number: e.target.value }))}
                      className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Enter manual DO number"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      PO Number
                    </label>
                    <input
                      type="text"
                      value={form.poNumber}
                      onChange={(e) => setForm(prev => ({ ...prev, poNumber: e.target.value }))}
                      className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Enter PO number"
                    />
                  </div>
                </div>

                {/* Credit Reference and Credit Period - Only show when mode of sales is Credit */}
                {form.modeOfSales === 'Credit' && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Credit Reference
                      </label>
                      <input
                        type="text"
                        value={form.creditReference}
                        onChange={(e) => setForm(prev => ({ ...prev, creditReference: e.target.value }))}
                        className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Enter credit reference"
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Credit Period (Days)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={form.creditPeriod}
                        onChange={(e) => setForm(prev => ({ ...prev, creditPeriod: parseInt(e.target.value) || 0 }))}
                        className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Enter credit period"
                      />
                    </div>
                  </div>
                )}

                {/* Claim Payment From (Optional) */}
                <div className="mb-4">
                  <div className={`border rounded-lg p-3 mb-3 transition-colors duration-300 ${
                    darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'
                  }`}>
                    <h4 className={`text-sm font-medium mb-1 flex items-center transition-colors duration-300 ${
                      darkMode ? 'text-purple-300' : 'text-purple-800'
                    }`}>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Claim Payment From (Optional)
                    </h4>
                    <div className={`text-xs transition-colors duration-300 ${
                      darkMode ? 'text-purple-400' : 'text-purple-600'
                    }`}>
                      <strong>💡 Optional:</strong> Add contact person name and 10-digit phone number for payment claims or follow-ups. Leave blank if not applicable.
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Contact Person Name
                      </label>
                      <input
                        type="text"
                        value={form.claimPaymentFrom.name}
                        onChange={(e) => setForm(prev => ({ 
                          ...prev, 
                          claimPaymentFrom: { ...prev.claimPaymentFrom, name: e.target.value }
                        }))}
                        className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Enter contact person name"
                        maxLength={100}
                      />
                      <div className={`text-xs mt-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {form.claimPaymentFrom.name.length}/100 characters
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Contact Number
                      </label>
                      <input
                        type="tel"
                        value={form.claimPaymentFrom.number}
                        onChange={(e) => setForm(prev => ({ 
                          ...prev, 
                          claimPaymentFrom: { ...prev.claimPaymentFrom, number: e.target.value }
                        }))}
                        className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="10 digit phone number"
                        maxLength={10}
                        pattern="[0-9]{10}"
                      />
                      <div className={`text-xs mt-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {form.claimPaymentFrom.number.length}/10 digits
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mode of Payment & Salesperson */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Mode of Payment
                    </label>
                    <Select
                      isMulti
                      value={
                        Array.isArray(form.modeOfPayment)
                          ? form.modeOfPayment.map(mode => {
                              const existingOption = paymentModeOptions.find(option => option.value === mode);
                              return existingOption || { value: mode, label: mode };
                            })
                          : []
                      }
                      onChange={handlePaymentModesChange}
                      options={paymentModeOptions}
                      styles={selectStyles}
                      placeholder="Select payment modes"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Or enter comma-separated payment modes
                    </label>
                    <input
                      type="text"
                      className={`w-full p-2 border rounded text-sm transition-colors duration-300 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Or enter comma-separated payment modes"
                      value={paymentModesInput}
                      onChange={handlePaymentModeInput}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Salesperson
                    </label>
                    <Select
                      isMulti
                      value={
                        Array.isArray(form.salesman)
                          ? form.salesman.map(emp => ({
                              value: emp,
                              label: employees.find(e => e._id === emp)?.name || emp
                            }))
                          : []
                      }
                      onChange={(selected) => {
                        const selectedIds = selected ? selected.map(option => option.value) : [];
                        setForm(prev => ({ ...prev, salesman: selectedIds }));
                      }}
                      options={employees.map(emp => ({ value: emp._id, label: emp.name }))}
                      styles={selectStyles}
                      placeholder={`Select salesperson ${employees.length > 0 ? `(${employees.length} available)` : '(No employees found)'}`}
                    />
                  </div>
                </div>

                {/* Site Address & Narration */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Site Address</label>
                    <textarea
                      value={form.siteAddress}
                      onChange={(e) => setForm(prev => ({ ...prev, siteAddress: e.target.value }))}
                      rows="3"
                      className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Enter site address"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Narration</label>
                    <textarea
                      value={form.Narration}
                      onChange={(e) => setForm(prev => ({ ...prev, Narration: e.target.value }))}
                      rows="3"
                      className={`w-full p-2 border rounded focus:ring focus:ring-blue-200 transition-colors duration-300 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Enter narration"
                    />
                  </div>
                </div>

                {/* Additional Charges Section */}
                <div className={`mb-6 rounded-lg p-4 transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  <h4 className={`text-lg font-semibold mb-4 flex items-center transition-colors duration-300 ${
                    darkMode ? 'text-gray-100' : 'text-gray-800'
                  }`}>
                    <FaCalculator className="w-5 h-5 text-blue-600 mr-2" />
                    Additional Charges
                  </h4>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        P&F Charges
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.packingAndForwarding || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, packingAndForwarding: parseFloat(e.target.value) || 0 }))}
                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Labour
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.labour || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, labour: parseFloat(e.target.value) || 0 }))}
                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Cartage
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.cartage || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, cartage: parseFloat(e.target.value) || 0 }))}
                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Round Off
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={form.roundoff || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, roundoff: parseFloat(e.target.value) || 0 }))}
                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Print Options */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Print Options</label>
                  <div className="flex flex-wrap gap-4">
                    <label className={`flex items-center cursor-pointer transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <input
                        type="checkbox"
                        checked={form.isReference || false}
                        onChange={(e) => setForm(prev => ({ ...prev, isReference: e.target.checked }))}
                        className="mr-2"
                      />
                      Include Reference
                    </label>
                    <label className={`flex items-center cursor-pointer transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <input
                        type="checkbox"
                        checked={form.isNarration || false}
                        onChange={(e) => setForm(prev => ({ ...prev, isNarration: e.target.checked }))}
                        className="mr-2"
                      />
                      Include Narration
                    </label>
                  </div>
                </div>
              </div>

              {/* Premium Action Buttons Section */}
              <div className={`relative rounded-2xl shadow-xl border p-8 group hover:shadow-2xl transition-all duration-500 ${
                darkMode 
                  ? 'bg-gradient-to-br from-gray-800 via-gray-800/90 to-gray-900/90 border-gray-700/50' 
                  : 'bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 backdrop-blur-sm border-gray-200/50'
              }`}>
                {/* Animated Background Elements */}
                <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  darkMode 
                    ? 'bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-green-600/10' 
                    : 'bg-gradient-to-r from-blue-600/5 via-purple-600/5 to-green-600/5'
                }`}></div>
                <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 rounded-2xl shadow-lg">
                        <FaRocket className="w-6 h-6 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
                    </div>
                    <div>
                      <h3 className={`text-2xl font-black bg-gradient-to-r bg-clip-text text-transparent ${
                        darkMode 
                          ? 'from-gray-100 via-blue-300 to-purple-300' 
                          : 'from-gray-900 via-blue-900 to-purple-900'
                      }`}>
                        Ready to Launch! 🚀
                      </h3>
                      <p className={`text-sm font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Step 4 of 4 - Review and create your order</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="flex space-x-1">
                          {[...Array(4)].map((_, i) => (
                            <div key={i} className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }}></div>
                          ))}
                        </div>
                        <span className="text-xs text-green-600 font-bold">All Steps Complete</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    {/* Reset Button */}
                    <button
                      type="button"
                      onClick={isEditing ? handleCancelEdit : () => setForm(initializeFormState(user?.user?._id))}
                      className={`group/reset relative px-6 py-4 border-2 rounded-2xl transition-all duration-300 flex items-center space-x-3 font-bold shadow-md hover:shadow-lg overflow-hidden ${
                        darkMode 
                          ? 'border-gray-600 text-gray-300 hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-600 hover:border-gray-500' 
                          : 'border-gray-300 text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:border-gray-400'
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 opacity-0 group-hover/reset:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative z-10 flex items-center space-x-2">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-r from-red-500 to-orange-600 flex items-center justify-center">
                          <FaTimes className="w-3 h-3 text-white" />
                        </div>
                        <span>{isEditing ? 'Cancel Edit' : 'Reset Form'}</span>
                      </div>
                    </button>
                    
                    {/* Submit Button */}
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={submitting || !form.recipient?.value}
                      className="group/submit relative px-10 py-4 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 text-white rounded-2xl hover:from-green-600 hover:via-emerald-600 hover:to-teal-700 disabled:from-gray-400 disabled:via-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed flex items-center space-x-3 shadow-xl hover:shadow-2xl transition-all duration-300 font-black text-lg overflow-hidden transform hover:scale-105"
                    >
                      {/* Animated Background */}
                      <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 -translate-x-full group-hover/submit:translate-x-full transition-transform duration-1000"></div>
                      
                      <div className="relative z-10 flex items-center space-x-3">
                        {submitting ? (
                          <div className="relative">
                            <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent"></div>
                            <div className="absolute inset-0 animate-ping rounded-full h-6 w-6 border border-white opacity-20"></div>
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center group-hover/submit:rotate-12 transition-transform duration-300">
                            <FaRocket className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <span>{submitting ? (isEditing ? 'Updating Order...' : 'Creating Order...') : (isEditing ? 'Update Order' : 'Create Order')}</span>
                        {!submitting && (
                          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center group-hover/submit:translate-x-1 transition-transform duration-300">
                            <FaArrowRight className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      
                      {/* Success particles effect */}
                      {!submitting && (
                        <div className="absolute inset-0 overflow-hidden rounded-2xl">
                          <div className="absolute top-2 left-4 w-1 h-1 bg-white rounded-full animate-bounce opacity-60" style={{ animationDelay: '0s' }}></div>
                          <div className="absolute top-4 right-6 w-1 h-1 bg-white rounded-full animate-bounce opacity-40" style={{ animationDelay: '0.5s' }}></div>
                          <div className="absolute bottom-3 left-8 w-1 h-1 bg-white rounded-full animate-bounce opacity-50" style={{ animationDelay: '1s' }}></div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Validation Reasons Display */}
                {(() => {
                  const validationReasons = getValidationReasons();
                  if (validationReasons.length > 0) {
                    return (
                      <div className={`relative z-10 mt-4 p-4 rounded-lg border transition-colors duration-300 ${
                        darkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-start space-x-2">
                          <FaExclamationTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                            darkMode ? 'text-red-400' : 'text-red-500'
                          }`} />
                          <div className="flex-1">
                            <h4 className={`font-semibold text-sm mb-2 ${
                              darkMode ? 'text-red-300' : 'text-red-700'
                            }`}>Cannot {isEditing ? 'Update' : 'Create'} Order - Please fix the following:</h4>
                            <ul className={`text-sm space-y-1 ${
                              darkMode ? 'text-red-200' : 'text-red-600'
                            }`}>
                              {validationReasons.map((reason, idx) => (
                                <li key={idx} className="flex items-center space-x-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0"></span>
                                  <span>{reason}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* Progress Indicator */}
                {(() => {
                  const validationReasons = getValidationReasons();
                  const totalChecks = 4; // recipient, location, items, valid calculations
                  const passedChecks = totalChecks - Math.min(validationReasons.length, totalChecks);
                  const completionPercent = Math.round((passedChecks / totalChecks) * 100);
                  
                  return (
                    <div className={`relative z-10 mt-6 pt-4 border-t transition-colors duration-300 ${
                      darkMode ? 'border-gray-700/50' : 'border-gray-200/50'
                    }`}>
                      <div className={`flex items-center justify-between text-xs mb-2 transition-colors duration-300 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span>Form Completion</span>
                        <span className={`font-bold ${
                          completionPercent === 100 ? 'text-green-600' : completionPercent >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{completionPercent}%</span>
                      </div>
                      <div className={`w-full rounded-full h-2 overflow-hidden transition-colors duration-300 ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                        <div 
                          className={`h-full rounded-full transition-all duration-500 shadow-sm ${
                            completionPercent === 100 
                              ? 'bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600' 
                              : completionPercent >= 50 
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                                : 'bg-gradient-to-r from-red-500 to-pink-500'
                          }`} 
                          style={{ width: `${completionPercent}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })()}
              </div>

            {/* Amount Summary - Matching DeliveryOrderNew.js format */}
            <div className={`mb-6 rounded-lg shadow-lg overflow-hidden transition-colors duration-300 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
            <div className={`px-6 py-4 border-b transition-colors duration-300 ${
              darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>Amount Summary</h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg transition-colors duration-300 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <h4 className={`font-semibold mb-3 text-base transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-800'
                    }`}>Tiles Summary</h4>
                    {form.items.filter(item => item.Name || item.tileId).map((item, index) => {
                      const locationName = locations.find(l => l._id === item.location)?.name || 'No Location';
                      return (
                        <div key={index} className="flex justify-between text-sm mb-2">
                          <div className="flex flex-col">
                            <span className={`transition-colors duration-300 ${
                              darkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>{item.Name || `Tile #${index + 1}`}</span>
                            <span className={`text-xs transition-colors duration-300 ${
                              darkMode ? 'text-gray-500' : 'text-gray-500'
                            }`}>{locationName}</span>
                          </div>
                          <span className={`font-medium transition-colors duration-300 ${
                            darkMode ? 'text-gray-200' : 'text-gray-800'
                          }`}>₹{formatNumberWithCommas(item.amountAfterDiscount || 0)}</span>
                        </div>
                      );
                    })}
                    <div className={`border-t mt-2 pt-2 flex justify-between font-semibold transition-colors duration-300 ${
                      darkMode ? 'border-gray-600 text-gray-100' : 'border-gray-300 text-gray-800'
                    }`}>
                      <span>Tiles Total:</span>
                      <span>₹{formatNumberWithCommas(
                        form.items.reduce((sum, item) => sum + (parseFloat(item.amountAfterDiscount) || 0), 0)
                      )}</span>
                    </div>
                  </div>

                  {/* Stock Items Summary */}
                  {form.stockItems && form.stockItems.length > 0 && form.stockItems.some(item => item.name || item.stockItemId) && (
                    <div className={`p-4 rounded-lg transition-colors duration-300 ${
                      darkMode ? 'bg-purple-900/30' : 'bg-purple-50'
                    }`}>
                      <h4 className={`font-semibold mb-3 text-base transition-colors duration-300 ${
                        darkMode ? 'text-purple-300' : 'text-purple-800'
                      }`}>Stock Items Summary</h4>
                      {form.stockItems.filter(item => item.name || item.stockItemId).map((stockItem, index) => {
                        const locationName = locations.find(l => l._id === stockItem.location)?.name || 'No Location';
                        return (
                          <div key={index} className="flex justify-between text-sm mb-2">
                            <div className="flex flex-col">
                              <span className={`transition-colors duration-300 ${
                                darkMode ? 'text-gray-300' : 'text-gray-700'
                              }`}>{stockItem.name || `Stock Item #${index + 1}`}</span>
                              <span className={`text-xs transition-colors duration-300 ${
                                darkMode ? 'text-gray-500' : 'text-gray-500'
                              }`}>{locationName}</span>
                            </div>
                            <span className={`font-medium transition-colors duration-300 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>₹{formatNumberWithCommas(stockItem.amountAfterDiscount || 0)}</span>
                          </div>
                        );
                      })}
                      <div className={`border-t mt-2 pt-2 flex justify-between font-semibold transition-colors duration-300 ${
                        darkMode ? 'border-purple-700 text-purple-300' : 'border-purple-300 text-purple-800'
                      }`}>
                        <span>Stock Items Total:</span>
                        <span>₹{formatNumberWithCommas(
                          form.stockItems.reduce((sum, item) => sum + (parseFloat(item.amountAfterDiscount) || 0), 0)
                        )}</span>
                      </div>
                    </div>
                  )}

                  <div className={`p-4 rounded-lg transition-colors duration-300 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <h4 className={`font-semibold mb-3 text-base transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-800'
                    }`}>Additional Charges</h4>
                    {(parseFloat(form.packingAndForwarding) || 0) > 0 && (
                      <div className="flex justify-between text-sm mb-2">
                        <span className={`transition-colors duration-300 ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>P&F Charges:</span>
                        <span className={`font-medium transition-colors duration-300 ${
                          darkMode ? 'text-gray-200' : 'text-gray-800'
                        }`}>₹{formatNumberWithCommas(form.packingAndForwarding)}</span>
                      </div>
                    )}
                    {(parseFloat(form.labour) || 0) > 0 && (
                      <div className="flex justify-between text-sm mb-2">
                        <span className={`transition-colors duration-300 ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>Labour:</span>
                        <span className={`font-medium transition-colors duration-300 ${
                          darkMode ? 'text-gray-200' : 'text-gray-800'
                        }`}>₹{formatNumberWithCommas(form.labour)}</span>
                      </div>
                    )}
                    {(parseFloat(form.cartage) || 0) > 0 && (
                      <div className="flex justify-between text-sm mb-2">
                        <span className={`transition-colors duration-300 ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>Cartage:</span>
                        <span className={`font-medium transition-colors duration-300 ${
                          darkMode ? 'text-gray-200' : 'text-gray-800'
                        }`}>₹{formatNumberWithCommas(form.cartage)}</span>
                      </div>
                    )}
                    {((parseFloat(form.packingAndForwarding) || 0) + (parseFloat(form.labour) || 0) + (parseFloat(form.cartage) || 0)) === 0 && (
                      <div className={`text-sm italic transition-colors duration-300 ${
                        darkMode ? 'text-gray-500' : 'text-gray-500'
                      }`}>No additional charges</div>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col justify-end">
                  <div className={`p-4 rounded-lg transition-colors duration-300 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className="flex justify-between items-center mb-4">
                      <span className={`font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>Total Amount (Items + AC):</span>
                      <span className={`text-2xl font-bold transition-colors duration-300 ${
                        darkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>₹{formatNumberWithCommas(form.totalAmountWithoutGST)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className={`font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>GST on AC:</span>
                      <span className={`text-2xl font-bold transition-colors duration-300 ${
                        darkMode ? 'text-green-400' : 'text-green-600'
                      }`}>
                        ₹{formatNumberWithCommas((form.packingAndForwardingGST || 0) + (form.labourGST || 0) + (form.cartageGST || 0))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className={`font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>Round Off:</span>
                      <span className={`text-2xl font-bold transition-colors duration-300 ${
                        darkMode ? 'text-green-400' : 'text-green-600'
                      }`}>
                        ₹{formatNumberWithCommas(form.roundoff)}
                      </span>
                    </div>
                    
                    {/* TT Amount Summary - Show only if reference is selected and TT amounts exist */}
                    {(() => {
                      // Only show TT summary if reference is selected
                      if (!form.reference) return null;
                      
                      const totalTT = form.items?.reduce((sum, item) => sum + (parseFloat(item.tt?.calculatedAmount) || 0), 0) || 0;
                      const hasTTItems = form.items?.some(item => item.tt?.value > 0);
                      
                      return (totalTT > 0 || hasTTItems) ? (
                        <div className={`border rounded p-3 mb-4 transition-colors duration-300 ${
                          darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'
                        }`}>
                          <div className="flex justify-between items-center mb-2">
                            <div>
                              <span className={`font-medium flex items-center gap-2 transition-colors duration-300 ${
                                darkMode ? 'text-purple-300' : 'text-purple-800'
                              }`}>
                                <svg className={`w-4 h-4 transition-colors duration-300 ${
                                  darkMode ? 'text-purple-400' : 'text-purple-600'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                TT Amount Summary
                              </span>
                            </div>
                            <div className="text-right">
                              <span className={`text-2xl font-bold transition-colors duration-300 ${
                                darkMode ? 'text-purple-400' : 'text-purple-600'
                              }`}>
                                ₹{formatNumberWithCommas(totalTT)}
                              </span>
                              <div className={`text-xs mt-1 transition-colors duration-300 ${
                                darkMode ? 'text-purple-400' : 'text-purple-500'
                              }`}>
                                {form.items?.filter(item => item.tt?.value > 0).length || 0} item(s) with TT
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                    
                    <div className="flex justify-between items-center">
                      <span className={`font-semibold transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>Final Amount (with GST):</span>
                      <span className={`text-2xl font-bold transition-colors duration-300 ${
                        darkMode ? 'text-blue-400' : 'text-blue-600'
                      }`}>₹{formatNumberWithCommas(form.totalAmountWithGST)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </form>
        </div>

        {/* Orders List Section */}
        <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Gradient Header */}
          <div className={`p-6 ${darkMode ? 'bg-gradient-to-r from-gray-800 via-gray-750 to-gray-800' : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700'} rounded-t-2xl`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                  <FaClipboardList className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Delivery Orders</h2>
                  <p className="text-indigo-100 text-sm">{filteredOrders?.length || 0} orders found</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {filteredOrders?.length > 0 && (
                  <button
                    onClick={() => {
                      const csvContent = [
                        ['Order Number', 'Customer', 'Location', 'Status', 'Total Amount', 'Created Date', 'Vehicle Number'],
                        ...filteredOrders.map(order => [
                          order.doNumber || order.orderNumber || '',
                          order.recipient?.name || order.recipient?.companyName || order.recipient?.businessName || '',
                          order.location?.name || '',
                          order.status || '',
                          (order.totalAmountWithGST || order.totalAmount || 0).toString(),
                          ((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(order.createdAt)),
                          order.vehicleNumber || ''
                        ])
                      ].map(row => row.join(',')).join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `delivery-orders-${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl text-white hover:bg-white/30 text-sm flex items-center gap-2 transition-all duration-200"
                    title="Export filtered orders to CSV"
                  >
                    <FaDownload className="w-3 h-3" /> Export
                  </button>
                )}
                <button onClick={fetchOrders} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl text-white hover:bg-white/30 text-sm flex items-center gap-2 transition-all duration-200">
                  <FaSearch className="w-3 h-3" /> Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Controls Row: Show/Hide Filters + Items Per Page */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Recent Orders</h3>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowFilters(prev => !prev)}
                  className={`flex items-center px-3 py-2 rounded-lg w-full sm:w-auto justify-center text-sm font-medium transition-colors ${
                    darkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                >
                  <FaFilter className="mr-2" />
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
                <div className="w-full sm:w-40">
                  <Select
                    value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                    onChange={(opt) => setItemsPerPage(opt.value)}
                    options={[
                      { value: 10, label: '10 per page' },
                      { value: 25, label: '25 per page' },
                      { value: 50, label: '50 per page' }
                    ]}
                    styles={selectStyles}
                    isSearchable={false}
                  />
                </div>
              </div>
            </div>

            {/* Always-Visible Quick Search Bar */}
            <div className="mb-4 relative">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={quickSearch}
                    onChange={(e) => handleQuickSearchChange(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => { if (quickSearch.length > 1) { setShowSearchSuggestions(true); setSelectedSuggestionIndex(-1); } }}
                    onBlur={() => setTimeout(() => { setShowSearchSuggestions(false); setSelectedSuggestionIndex(-1); }, 200)}
                    placeholder="Quick search by DO number, customer name, manual DO, PO number, vehicle..."
                    className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                    }`}
                    autoComplete="off"
                  />
                  <FaSearch className={`absolute left-3 top-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  {quickSearch && (
                    <button onClick={() => { setQuickSearch(''); setShowSearchSuggestions(false); }} className={`absolute right-3 top-3 p-1 rounded-full ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                      <FaTimes className="w-3 h-3" />
                    </button>
                  )}
                  {/* Search Suggestions Dropdown */}
                  {showSearchSuggestions && searchSuggestionsList.length > 0 && (
                    <div className={`absolute z-20 w-full mt-1 border rounded-xl shadow-lg max-h-48 overflow-y-auto ${
                      darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                    }`}>
                      <div className={`px-3 py-2 text-xs font-medium border-b ${darkMode ? 'text-gray-400 bg-gray-750 border-gray-600' : 'text-gray-500 bg-gray-50 border-gray-200'}`}>
                        Quick matches from existing orders <span className="float-right">Use ↑↓ to navigate</span>
                      </div>
                      {searchSuggestionsList.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => { setQuickSearch(suggestion.text); setShowSearchSuggestions(false); setSelectedSuggestionIndex(-1); }}
                          onMouseEnter={() => setSelectedSuggestionIndex(index)}
                          className={`w-full text-left px-3 py-2 transition-colors text-sm ${
                            selectedSuggestionIndex === index
                              ? (darkMode ? 'bg-blue-900/40 ring-2 ring-blue-500 ring-inset' : 'bg-blue-100 ring-2 ring-blue-500 ring-inset')
                              : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-50')
                          }`}
                        >
                          <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{suggestion.text}</div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{suggestion.type}: {suggestion.fullText}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Quick Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => handleFilterChange('status', filters.status === 'Pending' ? '' : 'Pending')}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${filters.status === 'Pending' ? 'bg-yellow-500 text-white' : (darkMode ? 'bg-yellow-900/30 text-yellow-300 hover:bg-yellow-900/50' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200')}`}>
                    Pending
                  </button>
                  <button onClick={() => handleFilterChange('status', filters.status === 'Completed' ? '' : 'Completed')}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${filters.status === 'Completed' ? 'bg-green-500 text-white' : (darkMode ? 'bg-green-900/30 text-green-300 hover:bg-green-900/50' : 'bg-green-100 text-green-700 hover:bg-green-200')}`}>
                    Completed
                  </button>
                  <button onClick={resetAllFilters}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    Clear All
                  </button>
                  {/* Keyboard Shortcuts Tooltip */}
                  <div className="relative group">
                    <button className={`px-3 py-2 text-sm rounded-lg transition-colors flex items-center ${darkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`} title="Keyboard shortcuts">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" /></svg>
                      <span className="ml-1">Shortcuts</span>
                    </button>
                    <div className={`absolute bottom-full right-0 mb-2 w-64 p-3 text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-900 text-white'}`}>
                      <div className="font-medium mb-2">Keyboard Shortcuts</div>
                      <div className="space-y-1">
                        <div className="flex justify-between"><span>Quick Search:</span><span className="font-mono bg-gray-700 px-1 rounded">/ or Ctrl+K</span></div>
                        <div className="flex justify-between"><span>Toggle Filters:</span><span className="font-mono bg-gray-700 px-1 rounded">Ctrl+F</span></div>
                        <div className="flex justify-between"><span>Clear/Close:</span><span className="font-mono bg-gray-700 px-1 rounded">Esc</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading Indicator */}
            {loading && (
              <div className={`mb-4 p-2 rounded-lg text-center text-sm ${darkMode ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent mr-2"></div>
                Searching...
              </div>
            )}

            {/* Enhanced Filter Panel */}
            {showFilters && (
              <div className={`mb-6 p-5 rounded-xl border-2 transition-all duration-300 ${
                darkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                {/* Filter Presets */}
                {filterPresets.length > 0 && (
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Saved Presets</label>
                    <div className="flex flex-wrap gap-2">
                      {filterPresets.map((preset, index) => (
                        <div key={index} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white border border-gray-200 text-gray-700'}`}>
                          <button onClick={() => loadFilterPreset(preset)} className="hover:underline">{preset.name}</button>
                          <button onClick={() => deleteFilterPreset(index)} className={`ml-1 ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}><FaTimes className="w-2.5 h-2.5" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Filter History */}
                {filterHistory.length > 0 && (
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recent Filters</label>
                    <div className="flex flex-wrap gap-2">
                      {filterHistory.slice(0, 5).map((entry, index) => (
                        <button key={index} onClick={() => { setFilters(entry.filters); setQuickSearch(entry.quickSearch || ''); }}
                          className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                          {new Date(entry.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {entry.quickSearch && ` - "${entry.quickSearch.substring(0, 15)}..."`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Basic Filters Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Search Type</label>
                    <Select
                      value={filters.searchType ? { value: filters.searchType, label: { all: 'All Fields', doNumber: 'DO Number', manualDO: 'Manual DO', poNumber: 'PO Number', recipient: 'Recipient', vehicle: 'Vehicle Number' }[filters.searchType] } : null}
                      onChange={(opt) => handleFilterChange('searchType', opt ? opt.value : 'all')}
                      options={[
                        { value: 'all', label: 'All Fields' },
                        { value: 'doNumber', label: 'DO Number' },
                        { value: 'manualDO', label: 'Manual DO' },
                        { value: 'poNumber', label: 'PO Number' },
                        { value: 'recipient', label: 'Recipient' },
                        { value: 'vehicle', label: 'Vehicle Number' }
                      ]}
                      styles={selectStyles} placeholder="Search In..." isSearchable={false}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                    <Select
                      value={filters.status ? { value: filters.status, label: filters.status } : null}
                      onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                      options={[
                        { value: 'Pending', label: 'Pending' },
                        { value: 'Completed', label: 'Completed' },
                        { value: 'Cancelled', label: 'Cancelled' }
                      ]}
                      styles={selectStyles} placeholder="All Statuses" isClearable isSearchable={false}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recipient Type</label>
                    <Select
                      value={filters.recipientType ? { value: filters.recipientType, label: filters.recipientType } : null}
                      onChange={(opt) => handleFilterChange('recipientType', opt ? opt.value : '')}
                      options={[
                        { value: 'Customer', label: 'Customer' },
                        { value: 'Dealer', label: 'Dealer' }
                      ]}
                      styles={selectStyles} placeholder="All Types" isClearable isSearchable={false}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mode of Sales</label>
                    <Select
                      value={filters.modeOfSales ? { value: filters.modeOfSales, label: filters.modeOfSales } : null}
                      onChange={(opt) => handleFilterChange('modeOfSales', opt ? opt.value : '')}
                      options={[
                        { value: 'Paid Instantly', label: 'Paid Instantly' },
                        { value: 'Credit', label: 'Credit' },
                        { value: 'Full Advance', label: 'Full Advance' }
                      ]}
                      styles={selectStyles} placeholder="All Modes" isClearable isSearchable={false}
                    />
                  </div>
                </div>

                {/* Date Range Filters */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date Range</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Select
                      value={filters.dateRange ? { value: filters.dateRange, label: { today: 'Today', yesterday: 'Yesterday', thisWeek: 'This Week', lastWeek: 'Last Week', thisMonth: 'This Month', lastMonth: 'Last Month', last7Days: 'Last 7 Days', last30Days: 'Last 30 Days', thisYear: 'This Year' }[filters.dateRange] } : null}
                      onChange={(opt) => handleDateRangeChange(opt ? opt.value : '')}
                      options={[
                        { value: 'today', label: 'Today' },
                        { value: 'yesterday', label: 'Yesterday' },
                        { value: 'thisWeek', label: 'This Week' },
                        { value: 'lastWeek', label: 'Last Week' },
                        { value: 'thisMonth', label: 'This Month' },
                        { value: 'lastMonth', label: 'Last Month' },
                        { value: 'last7Days', label: 'Last 7 Days' },
                        { value: 'last30Days', label: 'Last 30 Days' },
                        { value: 'thisYear', label: 'This Year' }
                      ]}
                      styles={selectStyles} placeholder="Select Date Range" isClearable isSearchable={false}
                    />
                    <DatePicker
                      selected={filters.startDate}
                      onChange={(date) => handleFilterChange('startDate', date)}
                      className={`w-full p-2.5 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholderText="Custom Start Date"
                      dateFormat="dd/MM/yyyy"
                    />
                    <DatePicker
                      selected={filters.endDate}
                      onChange={(date) => handleFilterChange('endDate', date)}
                      className={`w-full p-2.5 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholderText="Custom End Date"
                      dateFormat="dd/MM/yyyy"
                    />
                    <button onClick={() => setFilters(prev => ({ ...prev, dateRange: '', startDate: null, endDate: null }))}
                      className={`px-3 py-2 rounded-xl text-sm transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                      Clear Dates
                    </button>
                  </div>
                </div>

                {/* Amount Range */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Amount Range</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <input type="number" value={filters.minAmount} onChange={(e) => handleFilterChange('minAmount', e.target.value)} placeholder="Min Amount"
                      className={`w-full p-2.5 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900'}`} />
                    <input type="number" value={filters.maxAmount} onChange={(e) => handleFilterChange('maxAmount', e.target.value)} placeholder="Max Amount"
                      className={`w-full p-2.5 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900'}`} />
                    <button onClick={() => handleFilterChange('minAmount', '50000')}
                      className={`px-3 py-2 rounded-xl text-sm transition-colors ${darkMode ? 'bg-blue-900/30 text-blue-300 border border-blue-700 hover:bg-blue-900/50' : 'text-blue-600 border border-blue-300 hover:bg-blue-50'}`}>
                      High Value (&gt;50k)
                    </button>
                    <button onClick={() => setFilters(prev => ({ ...prev, minAmount: '', maxAmount: '' }))}
                      className={`px-3 py-2 rounded-xl text-sm transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                      Clear Amount
                    </button>
                  </div>
                </div>

                {/* Advanced Filters Toggle */}
                <div className="mb-4">
                  <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className={`flex items-center font-medium text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                    <FaChartBar className="mr-2" />
                    {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
                  </button>
                </div>

                {/* Advanced Filters */}
                {showAdvancedFilters && (
                  <div className={`border-t pt-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Payment Modes</label>
                        <Select isMulti
                          value={paymentModeOptions.filter(o => filters.paymentModes.includes(o.value))}
                          onChange={(opts) => handleMultiSelectFilter('paymentModes', opts)}
                          options={paymentModeOptions} styles={selectStyles} placeholder="Select Payment Modes" isSearchable={false}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Salesperson</label>
                        <Select isMulti
                          value={employees.filter(e => filters.salesmen.includes(e._id)).map(e => ({ value: e._id, label: e.name }))}
                          onChange={(opts) => handleMultiSelectFilter('salesmen', opts)}
                          options={employees.map(e => ({ value: e._id, label: e.name }))} styles={selectStyles} placeholder="Select Salesperson" isSearchable
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Transporters</label>
                        <Select isMulti
                          value={transporters.filter(t => filters.transporters.includes(t._id)).map(t => ({ value: t._id, label: t.name }))}
                          onChange={(opts) => handleMultiSelectFilter('transporters', opts)}
                          options={transporters.map(t => ({ value: t._id, label: t.name }))} styles={selectStyles} placeholder="Select Transporters" isSearchable
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>References</label>
                        <Select isMulti
                          value={references.filter(r => filters.references.includes(r._id)).map(r => ({ value: r._id, label: r.name }))}
                          onChange={(opts) => handleMultiSelectFilter('references', opts)}
                          options={references.map(r => ({ value: r._id, label: r.name }))} styles={selectStyles} placeholder="Select References" isSearchable
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Credit Period (Days)</label>
                        <Select
                          value={filters.creditPeriod ? { value: filters.creditPeriod, label: `${filters.creditPeriod} days` } : null}
                          onChange={(opt) => handleFilterChange('creditPeriod', opt ? opt.value : '')}
                          options={[
                            { value: '0', label: '0 days (Immediate)' },
                            { value: '30', label: '30 days' },
                            { value: '60', label: '60 days' },
                            { value: '90', label: '90 days' }
                          ]}
                          styles={selectStyles} placeholder="All Credit Periods" isClearable isSearchable={false}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Item Count Range</label>
                        <div className="flex space-x-2">
                          <input type="number" value={filters.minItemCount} onChange={(e) => handleFilterChange('minItemCount', e.target.value)} placeholder="Min"
                            className={`w-full p-2 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900'}`} />
                          <input type="number" value={filters.maxItemCount} onChange={(e) => handleFilterChange('maxItemCount', e.target.value)} placeholder="Max"
                            className={`w-full p-2 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900'}`} />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Credit Period Range (Days)</label>
                        <div className="flex space-x-2">
                          <input type="number" value={filters.minCreditPeriod} onChange={(e) => handleFilterChange('minCreditPeriod', e.target.value)} placeholder="Min Days"
                            className={`w-full p-2 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900'}`} />
                          <input type="number" value={filters.maxCreditPeriod} onChange={(e) => handleFilterChange('maxCreditPeriod', e.target.value)} placeholder="Max Days"
                            className={`w-full p-2 border-2 rounded-xl text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900'}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-between mt-6 gap-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={saveFilterPreset}
                      className={`px-4 py-2 rounded-xl flex items-center text-sm font-medium transition-colors ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                      <FaSave className="mr-2" /> Save as Preset
                    </button>
                    <button onClick={() => {
                      const filterData = { filters, quickSearch, timestamp: new Date().toISOString() };
                      navigator.clipboard.writeText(JSON.stringify(filterData, null, 2));
                      Swal.fire('Copied!', 'Filter configuration copied to clipboard', 'success');
                    }}
                      className={`px-4 py-2 rounded-xl flex items-center text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-600 text-white hover:bg-gray-700'}`}>
                      <FaDownload className="mr-2" /> Export Filters
                    </button>
                    <button onClick={async () => {
                      try {
                        const clipboardText = await navigator.clipboard.readText();
                        const importedData = JSON.parse(clipboardText);
                        if (importedData.filters) { setFilters(importedData.filters); setQuickSearch(importedData.quickSearch || ''); Swal.fire('Imported!', 'Filter configuration imported successfully', 'success'); }
                      } catch (error) { Swal.fire('Error!', 'Invalid filter data in clipboard', 'error'); }
                    }}
                      className={`px-4 py-2 rounded-xl flex items-center text-sm font-medium transition-colors ${darkMode ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                      <FaDownload className="mr-2 transform rotate-180" /> Import Filters
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={resetAllFilters}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                      Reset All Filters
                    </button>
                    <button onClick={addToFilterHistory}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${darkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Last 7 Days Statistics */}
            {lastSevenDaysStats && (
              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  <FaCalendarAlt className={`mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  Last 7 Days Overview
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-xl shadow ${darkMode ? 'bg-gradient-to-r from-teal-900/40 to-teal-800/30 border border-teal-700/50' : 'bg-gradient-to-r from-teal-50 to-teal-100'}`}>
                    <h4 className={`font-medium mb-2 ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>Recent Orders</h4>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-teal-100' : 'text-teal-900'}`}>{lastSevenDaysStats.totalOrders}</p>
                    <div className={`text-sm mt-2 space-y-1 ${darkMode ? 'text-teal-300' : 'text-teal-600'}`}>
                      <div className="flex justify-between"><span>Pending:</span><span className="font-medium">{lastSevenDaysStats.pendingOrders}</span></div>
                      <div className="flex justify-between"><span>Completed:</span><span className="font-medium">{lastSevenDaysStats.completedOrders}</span></div>
                      <div className="flex justify-between"><span>Cancelled:</span><span className="font-medium">{lastSevenDaysStats.cancelledOrders}</span></div>
                    </div>
                  </div>
                  <div className={`p-4 rounded-xl shadow ${darkMode ? 'bg-gradient-to-r from-indigo-900/40 to-indigo-800/30 border border-indigo-700/50' : 'bg-gradient-to-r from-indigo-50 to-indigo-100'}`}>
                    <h4 className={`font-medium mb-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>Recent Revenue</h4>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-indigo-100' : 'text-indigo-900'}`}>₹{lastSevenDaysStats.totalAmount}</p>
                    <div className={`text-sm mt-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                      <div className="flex justify-between"><span>Daily Average:</span><span className="font-medium">₹{formatNumberWithCommas(parseFloat(lastSevenDaysStats.totalAmount.replace(/,/g, '')) / 7)}</span></div>
                    </div>
                  </div>
                  <div className={`p-4 rounded-xl shadow ${darkMode ? 'bg-gradient-to-r from-rose-900/40 to-rose-800/30 border border-rose-700/50' : 'bg-gradient-to-r from-rose-50 to-rose-100'}`}>
                    <h4 className={`font-medium mb-2 ${darkMode ? 'text-rose-300' : 'text-rose-700'}`}>Status Distribution</h4>
                    <div className={`text-sm ${darkMode ? 'text-rose-200' : 'text-rose-900'}`}>
                      <div className="mb-2">
                        <div className="flex justify-between mb-1"><span>Pending:</span><span className="font-medium">{lastSevenDaysStats.totalOrders ? ((lastSevenDaysStats.pendingOrders / lastSevenDaysStats.totalOrders) * 100).toFixed(1) : 0}%</span></div>
                        <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-rose-900/50' : 'bg-rose-200'}`}>
                          <div className="bg-yellow-400 h-2 rounded-full" style={{ width: `${lastSevenDaysStats.totalOrders ? (lastSevenDaysStats.pendingOrders / lastSevenDaysStats.totalOrders) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="flex justify-between mb-1"><span>Completed:</span><span className="font-medium">{lastSevenDaysStats.totalOrders ? ((lastSevenDaysStats.completedOrders / lastSevenDaysStats.totalOrders) * 100).toFixed(1) : 0}%</span></div>
                        <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-rose-900/50' : 'bg-rose-200'}`}>
                          <div className="bg-green-400 h-2 rounded-full" style={{ width: `${lastSevenDaysStats.totalOrders ? (lastSevenDaysStats.completedOrders / lastSevenDaysStats.totalOrders) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="flex justify-between mb-1"><span>Cancelled:</span><span className="font-medium">{lastSevenDaysStats.totalOrders ? ((lastSevenDaysStats.cancelledOrders / lastSevenDaysStats.totalOrders) * 100).toFixed(1) : 0}%</span></div>
                        <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-rose-900/50' : 'bg-rose-200'}`}>
                          <div className="bg-red-400 h-2 rounded-full" style={{ width: `${lastSevenDaysStats.totalOrders ? (lastSevenDaysStats.cancelledOrders / lastSevenDaysStats.totalOrders) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`p-4 rounded-xl shadow ${darkMode ? 'bg-gradient-to-r from-amber-900/40 to-amber-800/30 border border-amber-700/50' : 'bg-gradient-to-r from-amber-50 to-amber-100'}`}>
                    <h4 className={`font-medium mb-2 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Amount by Status</h4>
                    <div className={`text-sm space-y-1 ${darkMode ? 'text-amber-200' : 'text-amber-900'}`}>
                      <div className="flex justify-between"><span>Pending:</span><span className="font-medium">₹{lastSevenDaysStats.pendingAmount}</span></div>
                      <div className="flex justify-between"><span>Completed:</span><span className="font-medium">₹{lastSevenDaysStats.completedAmount}</span></div>
                      <div className="flex justify-between"><span>Cancelled:</span><span className="font-medium">₹{lastSevenDaysStats.cancelledAmount}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Search Results Summary */}
            {!loading && (
              <div className={`mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2`}>
                <div className="flex items-center space-x-4">
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{filteredOrders?.length || 0}</span> orders found
                    {(quickSearch || Object.entries(filters).some(([k, v]) => Array.isArray(v) ? v.length > 0 : (v !== '' && v !== null && v !== 'all'))) && (
                      <span className={`ml-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        (filtered)
                        <button onClick={resetAllFilters} className={`ml-2 text-xs underline ${darkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-500 hover:text-blue-700'}`}>
                          Clear all filters
                        </button>
                      </span>
                    )}
                  </div>
                  {quickSearch && (
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Searching for: <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>"{quickSearch}"</span>
                    </div>
                  )}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Page {currentPage} of {Math.max(1, Math.ceil((filteredOrders?.length || 0) / itemsPerPage))}
                </div>
              </div>
            )}



              {/* Orders Table */}
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent"></div>
                    <span className="ml-3 text-gray-600 font-medium">Loading orders...</span>
                  </div>
                ) : filteredOrders?.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-600 to-purple-600">
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Order #
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y transition-colors duration-300 ${
                      darkMode ? 'bg-gray-700 divide-gray-600' : 'bg-white divide-gray-200'
                    }`}>
                      {filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((order, index) => (
                        <tr 
                          key={order._id} 
                          className={`transition-colors ${
                            darkMode 
                              ? 'hover:bg-indigo-900/30' + (index % 2 === 0 ? ' bg-gray-700' : ' bg-gray-750')
                              : 'hover:bg-indigo-50' + (index % 2 === 0 ? ' bg-white' : ' bg-gray-50')
                          }`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className={`text-sm font-bold transition-colors duration-300 ${
                              darkMode ? 'text-gray-100' : 'text-gray-900'
                            }`}>
                              #{order.orderNumber || order.doNumber}
                            </div>
                            {order.vehicleNumber && (
                              <div className={`text-xs transition-colors duration-300 ${
                                darkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                🚚 {order.vehicleNumber}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className={`text-sm transition-colors duration-300 ${
                              darkMode ? 'text-gray-300' : 'text-gray-900'
                            }`}>
                              {formatOrderDate(order.createdAt)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className={`text-sm font-medium transition-colors duration-300 ${
                              darkMode ? 'text-gray-200' : 'text-gray-900'
                            }`}>
                              {order.recipient?.name || order.recipient?.businessName  || 'N/A'}
                            </div>
                            {order.recipient?.phoneNumber && (
                              <div className={`text-xs transition-colors duration-300 ${
                                darkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                📞 {order.recipient.phoneNumber}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`text-sm transition-colors duration-300 ${
                              darkMode ? 'text-gray-300' : 'text-gray-900'
                            }`}>
                              {order.location?.name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className={`text-sm transition-colors duration-300 ${
                              darkMode ? 'text-gray-300' : 'text-gray-900'
                            }`}>
                              {order.itemsCount || ((order.items?.length || 0) + (order.stockItems?.length || 0))} item(s)
                            </div>
                            {order.items && order.items.length > 0 && (
                              <div className={`text-xs max-w-xs truncate transition-colors duration-300 ${
                                darkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {order.items[0]?.Name || order.items[0]?.tileName}
                                {order.items.length > 1 && ` +${order.items.length - 1} more`}
                              </div>
                            )}
                            {order.stockItems && order.stockItems.length > 0 && !order.items?.length && (
                              <div className={`text-xs max-w-xs truncate transition-colors duration-300 ${
                                darkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {order.stockItems[0]?.name || 'Stock Item'}
                                {order.stockItems.length > 1 && ` +${order.stockItems.length - 1} more`}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-bold text-green-600">
                              ₹{(order.totalAmountWithGST || order.totalAmount || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              {order.status === 'Pending' && (
                              <button
                                onClick={() => handleEdit(order)}
                                className={`p-2 text-green-600 rounded-lg transition-colors ${
                                  darkMode ? 'hover:bg-green-900/30' : 'hover:bg-green-100'
                                }`}
                                title="Edit Order"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              )}
                              {order.status === 'Pending' && (
                              <button
                                onClick={() => handleOpenEditRecipient(order)}
                                className={`p-2 text-amber-600 rounded-lg transition-colors ${
                                  darkMode ? 'hover:bg-amber-900/30' : 'hover:bg-amber-100'
                                }`}
                                title="Edit Recipient"
                              >
                                <FaUserTie className="w-4 h-4" />
                              </button>
                              )}
                              <button
                                onClick={(e) => handlePrintMenuOpen(e, order._id)}
                                className={`p-2 text-purple-600 rounded-lg transition-colors ${
                                  darkMode ? 'hover:bg-purple-900/30' : 'hover:bg-purple-100'
                                }`}
                                title="Print Order"
                              >
                                <FaPrint className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDuplicateOrder(order)}
                                className={`p-2 text-blue-600 rounded-lg transition-colors ${
                                  darkMode ? 'hover:bg-blue-900/30' : 'hover:bg-blue-100'
                                }`}
                                title="Duplicate Order"
                              >
                                <FaCopy className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    // Fetch full order details with populated fields
                                    const response = await api.get(`/tiles/delivery-orders/details/${order._id}`);
                                    if (response.data.success) {
                                      setSelectedOrder(response.data.deliveryOrder);
                                      setShowViewModal(true);
                                    }
                                  } catch (error) {
                                    console.error('Error fetching order details:', error);
                                    Swal.fire({
                                      icon: 'error',
                                      title: 'Error',
                                      text: 'Failed to load order details',
                                      customClass: { popup: 'swal-dark-mode' }
                                    });
                                  }
                                }}
                                className={`p-2 rounded-lg transition-colors ${
                                  darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                title="View Details"
                              >
                                <FaEye className="w-4 h-4" />
                              </button>
                              
                              {/* Status Management Buttons */}
                              {order.status === 'Pending' && (
                                <button
                                  onClick={() => openStatusModal(order, 'Completed')}
                                  className={`p-2 text-green-600 rounded-lg transition-colors ${
                                    darkMode ? 'hover:bg-green-900/30' : 'hover:bg-green-100'
                                  }`}
                                  title="Mark as Completed"
                                >
                                  <FaCheck className="w-4 h-4" />
                                </button>
                              )}
                              
                              {order.status !== 'Cancelled' && order.status !== 'Completed' && (
                                <button
                                  onClick={() => handleCancelOrder(order)}
                                  className={`p-2 text-red-600 rounded-lg transition-colors ${
                                    darkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-100'
                                  }`}
                                  title="Cancel Order"
                                >
                                  <FaBan className="w-4 h-4" />
                                </button>
                              )}
                              

                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={`text-center py-12 transition-colors duration-300 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <div className={`rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <FaClipboardList className={`w-10 h-10 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <h4 className={`text-lg font-medium mb-2 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>No Orders Found</h4>
                    <p className="text-sm mb-4">
                      {(quickSearch || Object.entries(filters).some(([k, v]) => Array.isArray(v) ? v.length > 0 : (v !== '' && v !== null && v !== 'all')))
                        ? 'No orders match your current filters. Try adjusting your search criteria.'
                        : 'You haven\'t created any tiles delivery orders yet.'
                      }
                    </p>
                    {(quickSearch || filters.status || filters.startDate || filters.endDate) && (
                      <div className="flex justify-center flex-wrap gap-2">
                        <button onClick={resetAllFilters}
                          className={`px-3 py-1 text-xs rounded-full ${darkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}>
                          Clear All Filters
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {filteredOrders?.length > itemsPerPage && (
                <div className={`mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      First
                    </button>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      <FaChevronLeft className="w-3 h-3" />
                    </button>
                    {(() => {
                      const totalPg = Math.ceil(filteredOrders.length / itemsPerPage);
                      const pages = [];
                      let start = Math.max(1, currentPage - 2);
                      let end = Math.min(totalPg, start + 4);
                      if (end - start < 4) start = Math.max(1, end - 4);
                      for (let i = start; i <= end; i++) pages.push(i);
                      return pages.map(p => (
                        <button key={p} onClick={() => setCurrentPage(p)}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${p === currentPage
                            ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white')
                            : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                          }`}>
                          {p}
                        </button>
                      ));
                    })()}
                    <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredOrders.length / itemsPerPage), p + 1))} disabled={currentPage >= Math.ceil(filteredOrders.length / itemsPerPage)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${currentPage >= Math.ceil(filteredOrders.length / itemsPerPage) ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      <FaChevronRight className="w-3 h-3" />
                    </button>
                    <button onClick={() => setCurrentPage(Math.ceil(filteredOrders.length / itemsPerPage))} disabled={currentPage >= Math.ceil(filteredOrders.length / itemsPerPage)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${currentPage >= Math.ceil(filteredOrders.length / itemsPerPage) ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      Last
                    </button>
                  </div>
                </div>
              )}

              {/* Orders Summary */}
              {filteredOrders?.length > 0 && (
                <div className={`mt-4 pt-3 border-t transition-colors duration-300 ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Showing</p>
                      <p className="font-semibold text-blue-600">{Math.min(itemsPerPage, filteredOrders.length)}</p>
                    </div>
                    <div className="text-center">
                      <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Orders</p>
                      <p className="font-semibold text-green-600">{count || orders.length}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Import Sales Order Modal */}
      {showImportSOModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className={`rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden m-4 border-2 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-5 border-b-2 ${
              darkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-750' : 'border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <FaShoppingCart className="text-white text-lg" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Import from Sales Order</h3>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Select a pending sales order to import into this delivery order
                  </p>
                </div>
              </div>
              <button onClick={() => setShowImportSOModal(false)} className={`p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {loadingPendingSO ? (
                <div className="flex justify-center items-center py-16">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mx-auto mb-4"></div>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Loading pending sales orders...</p>
                  </div>
                </div>
              ) : pendingSalesOrders.length === 0 ? (
                <div className={`text-center py-16 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FaClipboardList className="mx-auto h-16 w-16 mb-4 opacity-30" />
                  <p className="text-lg font-medium mb-2">No Pending Sales Orders</p>
                  <p className="text-sm">No pending or partially fulfilled sales orders found for this recipient.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingSalesOrders.map((so) => {
                    const totalTileItems = so.items?.length || 0;
                    const totalStockItems = so.stockItems?.length || 0;
                    const hasRemainingItems = so.items?.some(item => {
                      const remFB = (item.freshBoxes || 0) - (item.deliveredFreshBoxes || 0);
                      const remFP = (item.freshPieces || 0) - (item.deliveredFreshPieces || 0);
                      const remDB = (item.damagedBoxes || 0) - (item.deliveredDamagedBoxes || 0);
                      const remDP = (item.damagedPieces || 0) - (item.deliveredDamagedPieces || 0);
                      return remFB > 0 || remFP > 0 || remDB > 0 || remDP > 0;
                    }) || so.stockItems?.some(si => {
                      const remF = (si.freshQuantity || 0) - (si.deliveredFreshQuantity || 0);
                      const remD = (si.damagedQuantity || 0) - (si.deliveredDamagedQuantity || 0);
                      return remF > 0 || remD > 0;
                    });

                    return (
                      <div
                        key={so._id}
                        onClick={() => hasRemainingItems && handleImportSO(so)}
                        className={`border-2 rounded-xl p-5 transition-all duration-200 ${
                          hasRemainingItems
                            ? `cursor-pointer hover:shadow-lg ${darkMode ? 'border-gray-600 hover:border-amber-500 bg-gray-700/50' : 'border-gray-200 hover:border-amber-400 bg-white'}`
                            : `opacity-50 cursor-not-allowed ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`text-lg font-bold ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>{so.orderNumber}</span>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              so.status === 'Pending'
                                ? (darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                                : (darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700')
                            }`}>{so.status}</span>
                          </div>
                          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {so.date ? new Date(so.date).toLocaleDateString('en-IN') : ''}
                          </span>
                        </div>

                        {/* Items summary */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-600/50' : 'bg-gray-50'}`}>
                            <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tile Items</span>
                            <div className={`text-lg font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{totalTileItems}</div>
                          </div>
                          <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-600/50' : 'bg-gray-50'}`}>
                            <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Stock Items</span>
                            <div className={`text-lg font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{totalStockItems}</div>
                          </div>
                        </div>

                        {/* Remaining items preview */}
                        {so.items?.slice(0, 3).map((item, idx) => {
                          const remFB = Math.max(0, (item.freshBoxes || 0) - (item.deliveredFreshBoxes || 0));
                          const remFP = Math.max(0, (item.freshPieces || 0) - (item.deliveredFreshPieces || 0));
                          return (remFB > 0 || remFP > 0) ? (
                            <div key={idx} className={`text-xs mb-1 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              <FaBox className="w-3 h-3 text-green-500" />
                              <span className="font-medium">{item.tileName || item.tileId?.tilesName || 'Tile'}</span>
                              <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>—</span>
                              <span>Remaining: {remFB > 0 ? `${remFB} box` : ''}{remFB > 0 && remFP > 0 ? ', ' : ''}{remFP > 0 ? `${remFP} pcs` : ''}</span>
                            </div>
                          ) : null;
                        })}
                        {so.items?.length > 3 && (
                          <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            +{so.items.length - 3} more items...
                          </div>
                        )}

                        {/* Total amount */}
                        <div className={`mt-3 pt-3 border-t flex items-center justify-between ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          <span className={`text-sm font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                            Total: ₹{formatNumberWithCommas(so.totalAmount || 0)}
                          </span>
                          {hasRemainingItems && (
                            <span className={`text-xs font-medium px-3 py-1 rounded-full ${darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                              Click to Import
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tiles Selection Modal */}
      <TileSelectionModal
        isOpen={showTilesModal}
        onClose={() => setShowTilesModal(false)}
        onSelect={handleTileSelect}
        darkMode={darkMode}
        userId={user?.user?._id}
        locationId={form.items?.[currentItemIndex]?.location || form.location}
        showNetStock={false}
      />

      {/* Stock Items Selection Modal */}
      <StockItemSelectionModal
        isOpen={showStockItemsModal}
        onClose={() => setShowStockItemsModal(false)}
        onSelect={handleStockItemSelect}
        darkMode={darkMode}
        userId={user?.user?._id}
        locationId={form.stockItems?.[currentStockItemIndex]?.location || form.location}
        showNetStock={false}
      />

      {/* Confirmation Modal */}
      {showConfirmationModal && stockImpact && Array.isArray(stockImpact) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onKeyDown={handleConfirmationModalKeyDown}
          tabIndex={-1}
        >
          <div className={`rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-hidden transition-colors duration-300 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="bg-green-500 text-white p-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{isEditing ? 'Confirm Order Update' : 'Confirm Order Creation'}</h2>
                  <span className="text-xs text-green-100">(Press Enter to confirm, Esc to cancel)</span>
                </div>
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="text-white hover:text-gray-200 p-1"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-3">
              <div className="mb-3">
                <h3 className={`text-lg font-bold mb-2 transition-colors duration-300 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{isEditing ? 'Order Changes Summary' : 'Stock Impact Analysis'}</h3>
                <p className={`text-sm mb-2 transition-colors duration-300 ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {isEditing 
                    ? 'Review the changes between original and updated order:'
                    : 'Review the changes that will occur to your inventory after creating this order:'}
                </p>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {stockImpact?.map((impact, index) => (
                  <div key={index} className={`border rounded-lg p-4 transition-colors duration-300 ${
                    impact.isStockItem 
                      ? (darkMode ? 'border-blue-700 bg-blue-900/20' : 'border-blue-200 bg-blue-50')
                      : (darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-white')
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className={`font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>{impact.tileName}</h4>
                      {impact.locationId && impact.locationId !== 'default' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          📍 {locations.find(l => l._id === impact.locationId)?.name || impact.locationId}
                        </span>
                      )}
                    </div>
                    
                    {isEditing ? (
                      /* ===== EDIT MODE: Show Original vs Updated comparison ===== */
                      <div>
                        {/* Quantities Comparison Table */}
                        <table className={`w-full text-sm border-collapse ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <thead>
                            <tr className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-300'}>
                              <th className="text-left py-1 pr-2 font-medium">Field</th>
                              <th className={`text-center py-1 px-2 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Original</th>
                              <th className="text-center py-1 px-2 font-medium text-blue-500">Updated</th>
                              <th className="text-center py-1 pl-2 font-medium">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              { label: impact.isStockItem ? 'Fresh Qty' : 'Fresh Boxes', origKey: 'freshBoxes', newKey: 'freshBoxes' },
                              ...(!impact.isStockItem ? [{ label: 'Fresh Pieces', origKey: 'freshPieces', newKey: 'freshPieces' }] : []),
                              { label: impact.isStockItem ? 'Damaged Qty' : 'Corner Dmg Boxes', origKey: 'damagedBoxes', newKey: 'damagedBoxes' },
                              ...(!impact.isStockItem ? [{ label: 'Corner Dmg Pieces', origKey: 'damagedPieces', newKey: 'damagedPieces' }] : [])
                            ].map((row, ri) => {
                              const origVal = parseFloat((impact.originalQuantities?.[row.origKey] || 0).toFixed(4));
                              const newVal = parseFloat((impact.newQuantities?.[row.newKey] || 0).toFixed(4));
                              const diff = parseFloat((newVal - origVal).toFixed(4));
                              const changed = diff !== 0;
                              return (
                                <tr key={ri} className={`${changed ? (darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50') : ''} ${darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}`}>
                                  <td className="py-1.5 pr-2 font-medium">{row.label}</td>
                                  <td className={`text-center py-1.5 px-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{origVal}</td>
                                  <td className={`text-center py-1.5 px-2 font-semibold ${changed ? 'text-blue-500' : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>{newVal}</td>
                                  <td className={`text-center py-1.5 pl-2 font-bold ${
                                    diff > 0 ? 'text-red-500' : diff < 0 ? 'text-green-500' : (darkMode ? 'text-gray-500' : 'text-gray-400')
                                  }`}>
                                    {diff === 0 ? '—' : (diff > 0 ? `+${diff}` : diff)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        
                        {/* Current Stock → After Impact for edit mode */}
                        <div className={`mt-3 grid grid-cols-2 gap-3 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <div className={`p-2 rounded ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                            <h5 className={`font-semibold mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Current Stock</h5>
                            <div className="space-y-0.5">
                              <div>{impact.isStockItem ? 'Fresh Qty' : 'Fresh Box'}: <span className="font-medium">{impact.current.freshStockBox}</span></div>
                              {!impact.isStockItem && <div>Fresh Pcs: <span className="font-medium">{impact.current.freshStockPieces}</span></div>}
                              <div>{impact.isStockItem ? 'Dmg Qty' : 'Corner Dmg Box'}: <span className="font-medium">{impact.current.cornerDamagedBox}</span></div>
                              {!impact.isStockItem && <div>Corner Dmg Pcs: <span className="font-medium">{impact.current.cornerDamagedPieces}</span></div>}
                            </div>
                          </div>
                          <div className={`p-2 rounded ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                            <h5 className={`font-semibold mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>After Impact</h5>
                            <div className="space-y-0.5">
                              <div className={impact.remaining.freshStockBox < 0 ? 'text-red-500 font-bold' : ''}>
                                {impact.isStockItem ? 'Fresh Qty' : 'Fresh Box'}: <span className="font-medium">{impact.remaining.freshStockBox}</span>
                              </div>
                              {!impact.isStockItem && (
                                <div className={impact.remaining.freshStockPieces < 0 ? 'text-red-500 font-bold' : ''}>
                                  Fresh Pcs: <span className="font-medium">{impact.remaining.freshStockPieces}</span>
                                </div>
                              )}
                              <div className={impact.remaining.cornerDamagedBox < 0 ? 'text-red-500 font-bold' : ''}>
                                {impact.isStockItem ? 'Dmg Qty' : 'Corner Dmg Box'}: <span className="font-medium">{impact.remaining.cornerDamagedBox}</span>
                              </div>
                              {!impact.isStockItem && (
                                <div className={impact.remaining.cornerDamagedPieces < 0 ? 'text-red-500 font-bold' : ''}>
                                  Corner Dmg Pcs: <span className="font-medium">{impact.remaining.cornerDamagedPieces}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* No stock change indicator */}
                        {impact.requested.freshBoxes === 0 && (impact.requested.freshPieces || 0) === 0 && impact.requested.damagedBoxes === 0 && (impact.requested.damagedPieces || 0) === 0 && (
                          <div className={`mt-2 pt-2 border-t text-xs ${darkMode ? 'border-gray-600 text-green-400' : 'border-gray-200 text-green-600'}`}>
                            ✓ No stock change — quantities unchanged
                          </div>
                        )}
                      </div>
                    ) : (
                      /* ===== CREATE MODE: Show Current Stock / Requested / After Order ===== */
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <h5 className={`font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Stock</h5>
                          <div className={`space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <div>{impact.isStockItem ? 'Fresh Qty' : 'Fresh Box'}: {impact.current.freshStockBox}</div>
                            {!impact.isStockItem && <div>Fresh Pcs: {impact.current.freshStockPieces}</div>}
                            <div>{impact.isStockItem ? 'Dmg Qty' : 'Corner Dmg Box'}: {impact.current.cornerDamagedBox}</div>
                            {!impact.isStockItem && <div>Corner Dmg Pcs: {impact.current.cornerDamagedPieces}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className={`font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Requested</h5>
                          <div className={`space-y-1 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            <div>{impact.isStockItem ? 'Fresh Qty' : 'Fresh Box'}: {impact.requested.freshBoxes}</div>
                            {!impact.isStockItem && <div>Fresh Pcs: {impact.requested.freshPieces}</div>}
                            <div>{impact.isStockItem ? 'Dmg Qty' : 'Corner Dmg Box'}: {impact.requested.damagedBoxes}</div>
                            {!impact.isStockItem && <div>Corner Dmg Pcs: {impact.requested.damagedPieces}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className={`font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>After Order</h5>
                          <div className={`space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <div className={impact.remaining.freshStockBox < 0 ? 'text-red-600' : ''}>
                              {impact.isStockItem ? 'Fresh Qty' : 'Fresh Box'}: {impact.remaining.freshStockBox}
                            </div>
                            {!impact.isStockItem && (
                              <div className={impact.remaining.freshStockPieces < 0 ? 'text-red-600' : ''}>
                                Fresh Pcs: {impact.remaining.freshStockPieces}
                              </div>
                            )}
                            <div className={impact.remaining.cornerDamagedBox < 0 ? 'text-red-600' : ''}>
                              {impact.isStockItem ? 'Dmg Qty' : 'Corner Dmg Box'}: {impact.remaining.cornerDamagedBox}
                            </div>
                            {!impact.isStockItem && (
                              <div className={impact.remaining.cornerDamagedPieces < 0 ? 'text-red-600' : ''}>
                                Corner Dmg Pcs: {impact.remaining.cornerDamagedPieces}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className={`mt-3 border rounded p-3 transition-colors duration-300 ${
                darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start space-x-2">
                  <FaExclamationTriangle className={`w-5 h-5 mt-0.5 transition-colors duration-300 ${
                    darkMode ? 'text-yellow-400' : 'text-yellow-600'
                  }`} />
                  <div>
                    <h4 className={`font-medium transition-colors duration-300 ${
                      darkMode ? 'text-yellow-200' : 'text-yellow-900'
                    }`}>Important Notes:</h4>
                    <ul className={`text-sm mt-1 space-y-1 transition-colors duration-300 ${
                      darkMode ? 'text-yellow-300' : 'text-yellow-800'
                    }`}>
                      {isEditing ? (
                        <>
                          <li>• Stock levels will be adjusted based on the quantity differences</li>
                          <li>• Outward entries will be updated automatically</li>
                          <li>• Sales order tracking will be recalculated if linked</li>
                        </>
                      ) : (
                        <>
                          <li>• This action will automatically create a tiles outward entry</li>
                          <li>• Stock levels will be updated immediately</li>
                          <li>• This action cannot be undone</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-3">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className={`px-4 py-2 border rounded transition-colors duration-300 ${
                    darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel (Esc)
                </button>
                <button
                  ref={confirmButtonRef}
                  onClick={confirmSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2 focus:ring-2 focus:ring-green-300 focus:ring-offset-2"
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <FaCheckCircle className="w-4 h-4" />
                  )}
                  <span>{submitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Confirm & Update (Enter)' : 'Confirm & Create (Enter)')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      {(form.recipient?.value && (form.items?.some(item => item.Name) || form.stockItems?.some(item => item.stockItemId || item.name))) && (
        <div className="fixed bottom-8 right-8 z-50">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="group relative w-16 h-16 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 hover:from-green-600 hover:via-emerald-600 hover:to-teal-700 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
          >
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-full"></div>
            
            {/* Icon */}
            <div className="relative z-10">
              {submitting ? (
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
              ) : (
                <FaRocket className="w-8 h-8 group-hover:rotate-12 transition-transform duration-300" />
              )}
            </div>
            
            {/* Pulse Ring */}
            <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20"></div>
            
            {/* Hover Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              <div className="font-semibold mb-1">Quick Submit Order</div>
              <div className="text-gray-400 text-[10px]">Shortcut: Ctrl+Enter</div>
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </button>
        </div>
      )}

      {/* Order Details Modal */}
      {showViewModal && selectedOrder && (
        <OrderDetailsModal />
      )}

      {/* Recipient Selection Modal */}
<MasterSelection
    isOpen={showRecipientModal}
    onClose={() => setShowRecipientModal(false)}
    onSelect={handleRecipientSelect}
    allowedTypes={['Customer', 'Dealer']}
    defaultType={form.recipientType}
        title="Select Recipient"
      />

      {/* Reference Selection Modal */}
      <MasterSelection
        isOpen={showReferenceModal}
        onClose={() => setShowReferenceModal(false)}
        onSelect={(item) => {
          const newReference = item._id;
          setForm(prev => {
            // Reset TT fields when reference changes
            if (prev.items?.length > 0) {
              const updatedItems = prev.items.map(i => ({
                ...i,
                tt: { type: 'percentage', value: 0, calculatedAmount: 0 }
              }));
              return { ...prev, reference: newReference, display: { ...prev.display, referenceName: item.name }, items: updatedItems, totalTTAmount: 0 };
            }
            return { ...prev, reference: newReference, display: { ...prev.display, referenceName: item.name }, totalTTAmount: 0 };
          });
          setTimeout(() => calculateTotals(), 100);
          setShowReferenceModal(false);
        }}
        allowedTypes={['Reference']}
        defaultType="Reference"
        title="Select Reference"
      />

      {/* Transporter Selection Modal */}
      <MasterSelection
        isOpen={showTransporterModal}
        onClose={() => setShowTransporterModal(false)}
        onSelect={(item) => {
          setForm(prev => ({ ...prev, transporterName: item._id, display: { ...prev.display, transporterDisplayName: item.name } }));
          setShowTransporterModal(false);
        }}
        allowedTypes={['Transporter']}
        defaultType="Transporter"
        title="Select Transporter"
      />

      {/* Edit Recipient - Master Selection Picker */}
      <MasterSelection
        isOpen={showEditRecipientPicker}
        onClose={() => setShowEditRecipientPicker(false)}
        onSelect={(recipient) => {
          const name = editRecipientType === 'Customer' ? recipient.name : recipient.businessName;
          setEditRecipientValue({ value: recipient._id, label: name });
          setShowEditRecipientPicker(false);
        }}
        allowedTypes={[editRecipientType]}
        defaultType={editRecipientType}
        title={`Select ${editRecipientType}`}
      />

      {/* Status Update Modal */}
      {showStatusModal && selectedOrderForStatus && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border shadow-lg rounded-md transition-colors duration-300 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'
          } w-96`}>
            <div className="mt-3">
              <h3 className={`text-lg font-medium mb-4 flex items-center gap-2 transition-colors duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                <FaCog className="text-blue-600" />
                Update Delivery Order Status
              </h3>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Delivery Order
                  </label>
                  <div className={`text-sm p-2 rounded transition-colors duration-300 ${
                    darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-50'
                  }`}>
                    {selectedOrderForStatus.doNumber} - {selectedOrderForStatus.recipient?.name}
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    New Status
                  </label>
                  <div className={`w-full px-3 py-2 border rounded-lg transition-colors duration-300 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                      statusUpdateData.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      statusUpdateData.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {statusUpdateData.status}
                    </span>
                  </div>
                </div>

                {statusUpdateData.status === 'Completed' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Invoice Number *
                      <span className="text-red-500 ml-1">(Required for Completed status)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter invoice number..."
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      value={statusUpdateData.invoiceNumber}
                      onChange={(e) => setStatusUpdateData(prev => ({...prev, invoiceNumber: e.target.value}))}
                      required
                    />
                  </div>
                )}

                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Remarks *
                    {(statusUpdateData.status === 'Completed' || statusUpdateData.status === 'Cancelled') && (
                      <span className="text-red-500 ml-1">(Required for status change)</span>
                    )}
                  </label>
                  <textarea
                    placeholder="Enter remarks for this status update..."
                    rows="4"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    value={statusUpdateData.remarks}
                    onChange={(e) => setStatusUpdateData(prev => ({...prev, remarks: e.target.value}))}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedOrderForStatus(null);
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors duration-300 ${
                    darkMode 
                      ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <FaCog />
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Recipient Modal - hidden when picker is open to avoid overlap */}
      {showEditRecipientModal && editingRecipientOrder && !showEditRecipientPicker && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-10 mx-auto p-6 border shadow-xl rounded-xl w-full max-w-lg transition-colors duration-300 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-amber-900/40' : 'bg-amber-100'}`}>
                  <FaUserTie className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    Edit Recipient
                  </h3>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Order: {editingRecipientOrder.doNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setShowEditRecipientModal(false); setEditingRecipientOrder(null); setEditRecipientValue(null); }}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Current vs New Recipient Comparison */}
            <div className={`rounded-xl border mb-5 overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {/* Current Recipient */}
              <div className={`px-4 py-3 ${darkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current Recipient</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    {editingRecipientOrder.recipientType}
                  </span>
                </div>
                <p className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {editingRecipientOrder.recipient?.name || editingRecipientOrder.recipient?.businessName || 'N/A'}
                </p>
              </div>

              {/* Arrow divider */}
              <div className={`flex items-center justify-center py-2 ${darkMode ? 'bg-gray-800 border-t border-b border-gray-700' : 'bg-white border-t border-b border-gray-200'}`}>
                <div className={`flex items-center gap-2 text-xs font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  <FaArrowRight className="w-3 h-3" />
                  <span>Change to</span>
                  <FaArrowRight className="w-3 h-3" />
                </div>
              </div>

              {/* New Recipient */}
              <div className={`px-4 py-3 ${editRecipientValue ? (darkMode ? 'bg-green-900/20' : 'bg-green-50') : (darkMode ? 'bg-gray-750' : 'bg-gray-50')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>New Recipient</span>
                  <span className={`text-xs font-medium ${darkMode ? 'text-red-400' : 'text-red-500'}`}>* Required</span>
                </div>
                {editRecipientValue ? (
                  <p className={`font-semibold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                    {editRecipientValue.label}
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                      {editRecipientType}
                    </span>
                  </p>
                ) : (
                  <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No recipient selected yet
                  </p>
                )}
              </div>
            </div>

            {/* Step 1: Recipient Type */}
            <div className="mb-4">
              <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Step 1: Select Recipient Type <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-2">
                {['Customer', 'Dealer'].map(type => (
                  <button
                    key={type}
                    onClick={() => { setEditRecipientType(type); setEditRecipientValue(null); }}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      editRecipientType === type
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                        : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Select Recipient */}
            <div className="mb-5">
              <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Step 2: Search & Select {editRecipientType} <span className="text-red-500">*</span>
              </label>
              <button
                onClick={() => setShowEditRecipientPicker(true)}
                className={`w-full px-4 py-3 border-2 rounded-xl text-left flex items-center justify-between transition-all duration-200 ${
                  editRecipientValue
                    ? darkMode
                      ? 'border-green-600 bg-green-900/20 text-green-300 hover:border-green-500'
                      : 'border-green-400 bg-green-50 text-green-800 hover:border-green-500'
                    : darkMode
                      ? 'border-blue-600 bg-gray-700 text-gray-100 hover:border-blue-400 hover:shadow-md hover:shadow-blue-500/10'
                      : 'border-blue-400 bg-white text-gray-900 hover:border-blue-500 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${editRecipientValue ? (darkMode ? 'bg-green-900/40' : 'bg-green-100') : (darkMode ? 'bg-gray-600' : 'bg-blue-100')}`}>
                    <FaSearch className={`w-4 h-4 ${editRecipientValue ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-blue-500')}`} />
                  </div>
                  <div>
                    {editRecipientValue ? (
                      <span className="font-semibold">{editRecipientValue.label}</span>
                    ) : (
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Search and select a {editRecipientType.toLowerCase()}...</span>
                    )}
                    <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {editRecipientValue ? 'Click to change selection' : 'Click to open search dialog'}
                    </p>
                  </div>
                </div>
                {editRecipientValue ? (
                  <FaCheckCircle className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                ) : (
                  <FaChevronRight className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                )}
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => { setShowEditRecipientModal(false); setEditingRecipientOrder(null); setEditRecipientValue(null); }}
                className={`px-5 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
                  darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <div className="flex items-center gap-3">
                {!editRecipientValue && (
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Select a {editRecipientType.toLowerCase()} to continue
                  </span>
                )}
                <button
                  onClick={handleUpdateRecipient}
                  disabled={!editRecipientValue || updatingRecipient}
                  title={!editRecipientValue ? `Please select a ${editRecipientType.toLowerCase()} first` : ''}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-200 ${
                    editRecipientValue && !updatingRecipient
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-600/20 hover:shadow-lg'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                  }`}
                >
                  {updatingRecipient ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <FaUserTie className="w-4 h-4" />
                      <span>Update Recipient</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Menu Dropdown */}
      {printMenuAnchor && (
        <div className="fixed inset-0 z-50" onClick={handlePrintMenuClose}>
          <div 
            className={`absolute border rounded-lg shadow-lg py-2 min-w-48 transition-colors duration-300 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
            style={{
              top: printMenuAnchor.getBoundingClientRect().bottom + 8,
              left: printMenuAnchor.getBoundingClientRect().left,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-4 py-2 text-sm font-semibold border-b transition-colors duration-300 ${
              darkMode ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-200'
            }`}>
              Print Options
            </div>
            <button
              onClick={() => handlePrintOption('basic')}
              className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors duration-300 ${
                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaPrint className="w-4 h-4 text-blue-600" />
              <span>Standard Print</span>
            </button>
            <button
              onClick={() => handlePrintOption('discount')}
              className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors duration-300 ${
                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaPrint className="w-4 h-4 text-green-600" />
              <span>Print with Discount Details</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TilesDeliveryOrder;